/**
 * Translations for the embeddable widgets.
 *
 * The widgets render on the customer's OWN website, so they must speak the
 * studio's language — not Danish by default. Every string below used to be
 * hardcoded Danish, which meant an English studio embedding the booking widget
 * showed "Bekræft booking" to its English customers.
 *
 * Locale comes from `initData.locale`, which /api/widget/init fills from
 * `tenant.locale`. Anything unknown falls back to Danish, matching the API's
 * own `tenant.locale || 'da'`.
 *
 * NOT translated on purpose: the setup errors in widget-base (missing api-key,
 * unresolvable host). They fire BEFORE init, so no locale is known yet, and
 * they address whoever is embedding the widget — not the end user. They stay
 * English like the rest of the widget API's integrator-facing errors.
 */

export type WidgetLocale = 'da' | 'en' | 'de' | 'sv' | 'nb' | 'es' | 'fr'

const SUPPORTED: WidgetLocale[] = ['da', 'en', 'de', 'sv', 'nb', 'es', 'fr']

/** Date/number formatting locale per UI language. */
const INTL_LOCALE: Record<WidgetLocale, string> = {
  da: 'da-DK',
  en: 'en-GB',
  de: 'de-DE',
  sv: 'sv-SE',
  nb: 'nb-NO',
  es: 'es-ES',
  fr: 'fr-FR',
}

/**
 * Retreat booking-modal strings. Ported verbatim from the tenant-side
 * next-intl namespace `cms.widgets.retreat.booking` / `.packageTravel`, so the
 * embedded modal reads identically to the studio's own booking modal.
 * Placeholders in `{braces}` are interpolated manually at the call site.
 */
export interface RetreatBookingStrings {
  modalAriaLabel: string
  nav: {
    cancel: string
    back: string
    next: string
    toPayment: string
    confirmFree: string
    submitting: string
    /** {current}, {total} */
    stepProgress: string
  }
  steps: {
    indicatorAriaLabel: string
    who: {
      title: string
      description: string
      shortLabel: string
      countLegend: string
      soloOption: string
      soloHelp: string
      pairOption: string
      pairHelp: string
      privateDoubleSoloTitle: string
      privateDoubleSoloBody: string
      roommateLegend: string
      sharedDoubleSoloHelp: string
      roommate: {
        matchMe: string
        matchMeHelp: string
        alone: string
        aloneHelp: string
        knownPartner: string
        knownPartnerHelp: string
        knownPartnerNameLabel: string
        knownPartnerNamePlaceholder: string
      }
    }
    info: {
      title: string
      description: string
      shortLabel: string
      /** {index} */
      participantHeading: string
      participantHeadingSingle: string
      firstNameLabel: string
      lastNameLabel: string
      emailLabel: string
      phoneLabel: string
      dietaryLabel: string
      dietaryPlaceholder: string
      dietaryHelp: string
      errors: {
        firstNameRequired: string
        lastNameRequired: string
        invalidEmail: string
        invalidPhone: string
        dietaryTooLong: string
      }
    }
    questions: {
      title: string
      description: string
      /** Et event (kind=event) har intet ophold at planlægge. */
      descriptionEvent: string
      shortLabel: string
      noQuestions: string
      selectPlaceholder: string
      errors: { required: string }
    }
    payment: {
      title: string
      description: string
      shortLabel: string
      /** {count}, {tierName}, {perPerson} */
      breakdownLine: string
      /** {rate} */
      vatLine: string
      totalLabel: string
      chooseModeLegend: string
      fullPayment: string
      fullPaymentHelp: string
      depositPayment: string
      /** {percent}, {remaining} */
      depositPaymentHelp: string
      /** {percent}, {remaining}, {dueDate} */
      depositPaymentHelpWithDate: string
      /** {amount} */
      payNowShort: string
      installmentsPayment: string
      /** {count}, {perInstallment} */
      installmentsPaymentHelp: string
      /** {amount} */
      perMonthShort: string
      discountSectionTitle: string
      giftCardLabel: string
      giftCardPlaceholder: string
      giftCardHelp: string
      couponLabel: string
      couponPlaceholder: string
      couponHelp: string
      submitting: string
    }
  }
  priceSummary: {
    /** {count}, {tierName} */
    lineLabel: string
    /** {rate} */
    vatLabel: string
    discountLabel: string
    totalLabel: string
    payNowLabel: string
    dueLaterLabel: string
    /** {percent} */
    depositNote: string
    /** {count} */
    installmentsNote: string
  }
  errors: {
    capacityTaken: string
    paymentNotConfigured: string
    paymentProviderError: string
    paymentModeUnavailable: string
    notAvailable: string
    validation: string
    generic: string
    network: string
    coupon: {
      notAllowed: string
      unknown: string
      notStarted: string
      expired: string
      usedUp: string
      alreadyUsed: string
      newCustomersOnly: string
      requiresMembership: string
      requiresSpecificMembership: string
      wrongProduct: string
      otherRetreat: string
      notAllowedEvent: string
      wrongProductEvent: string
      otherRetreatEvent: string
      /** {minimum} */
      belowMinimum: string
    }
  }
  packageTravel: {
    heading: string
    /** {organizer} */
    organizerNote: string
    /** {organizer}, {memberNumber} */
    registeredLine: string
    formToggle: string
    formIntro: string
    /** {organizer}, {memberNumber} */
    formResponsibility: string
    rightsTitle: string
    right1: string
    right2: string
    right3: string
    right4: string
    right5: string
    right6: string
    contact: string
    consentLabel: string
    logoAlt: string
  }
}

export interface CheckoutLegalStrings {
  /** jura-hs p44: ordreoversigt + samtykke før køb. Spejl af public.checkoutLegal i HS' messages
   *  (widget-bundlen kan ikke importere dem); en jest-test holder teksterne ens. */
  summaryTitle: string
  seller: string
  companyId: string
  platformNote: string
  total: string
  /** Totalen før en rabatkode, som serveren først regner ved betalingen (booking-vinduet). */
  totalBeforeDiscount: string
  recurringNote: string
  perFourWeeks: string
  perMonth: string
  perQuarter: string
  perHalfYear: string
  perYear: string
  oneTimeNote: string
  acceptTerms: string
  acceptTermsEarlyStart: string
  termsLink: string
  withdrawalLink: string
  privacyLink: string
  withdrawContractLink: string
  noWithdrawal: string
  paidOrderNote: string
  payGeneric: string
  payAmount: string
  payCard: string
  payMobilePay: string
}

export interface WidgetStrings {
  checkoutLegal: CheckoutLegalStrings
  // Generic
  loadError: string
  close: string
  cancel: string
  login: string
  readMore: string
  readLess: string
  book: string
  buy: string
  // Booking modal
  alreadyBookedTitle: string
  alreadyBookedBody: string
  waitlistTitle: string
  waitlistBody: string
  confirmedTitle: string
  confirmedBody: string
  loginToBook: string
  fullTitle: string
  fullBody: string
  membershipRequiredBody: string
  paymentRequiredBody: string
  /** OPG-0185: «Eller køb et klippekort» i booking-vinduet, når holdet ellers skal betales. */
  classPlansHeading: string
  /** Hold der kun kan bookes med klippekort: overskrift uden «Eller». */
  classPlansHeadingOnly: string
  /** ICU-plural: antal klip på kortet. */
  classPlansClips: string
  /** ICU-plural: kortets gyldighed i dage. */
  classPlansValidDays: string
  classPlansBuy: string
  classPlansViewSubscription: string
  classPlansSubscription: string
  /** Efter købet: åbn holdet igen og tryk Book (ingen automatisk booking). */
  classPlansAfterNote: string
  classPlansPaySingle: string
  /** Body når et hold kræver en accepteret ansvarsfraskrivelse — accepten kan kun registreres på den native flade. */
  waiverRequiredBody: string
  /** Body første gang et hold stiller intake-spørgsmål — svarene bæres med over derefter. */
  intakeRequiredBody: string
  /** Body når et hold kræver deltagernavne — kun det native flow indsamler dem. */
  participantNamesBody: string
  /** Deltagerprofiler på studiet: profilvalget sker på den native flade. */
  participantProfileBody: string
  /** Body når holdet er fuldt og kundens klippekort dækker det: ventelisten (med klippet reserveret) sker på den native flade. */
  waitlistClipCardBody: string
  /** Sekundært link i booking-modalen når holdet tillader gæster (handoff til native). */
  bookWithGuests: string
  fullVenueButton: string
  continueToBooking: string
  dropInPrice: string
  /** Compact clip-cost badge as an ICU plural (`{count, plural, one {…} other {…}}`); format it with `plural()`. */
  clipCostShort: string
  /** «Fra» foran prisen på en plan, hvor alle deltagere er momsfri (laveste pris). */
  priceFromPrefix: string
  /** Mærket «Momsfri pris» under prisen. */
  vatFreePrice: string
  /** Særlig afmeldingsfrist på et hold (afviger fra studiets generelle). */
  ownCancellationHeading: string
  ownCancellationDeadline: string
  ownCancellationDeadlineOne: string
  ownCancellationNoRefund: string
  /** Schedule price cell when a class has no drop-in price and standard clip cost — it is booked via membership/clipcard, not a single drop-in. */
  membershipOnly: string
  /** Fri holdtype på medlemmets abonnement (OPG-0079): vises i stedet for drop-in-prisen. */
  includedInSubscription: string
  /** Schedule price cell when a class costs 0 and does NOT require a membership: anyone can book it without paying. */
  freeClass: string
  booking: string
  joinWaitlist: string
  confirmBooking: string
  // Inline gæste-stepper i BookingModal (fladt flow): medlem tager betalte gæster med.
  /** Sektions-label over gæste-stepperen. */
  guests: string
  /** Pris-suffiks pr. gæst, fx "pr. gæst" → vises som "200 kr pr. gæst". */
  perGuest: string
  /** Note på medlemskabs-hold: medlemmets egen plads er gratis, betalingen er for gæsten. */
  memberSpotCovered: string
  /** Kundens egen plads betales med et klip (som booking-vinduet på hjemmesiden). */
  coveredByClipCard: string
  /** Flueben (A4): fravælg klippet og betal drop-in i stedet (samme tekst som booking-vinduet på hjemmesiden). */
  useClipCard: string
  /** Kundens egen plads på et drop-in-hold er dækket af abonnementet. */
  coveredBySubscription: string
  /** Bekræft-knap når der skal betales (gæster/drop-in) — telegraferer betalingen. */
  confirmAndPay: string
  /** Overskrift på metode-vælgeren (MobilePay eller kort), vist når studiet har begge. */
  paymentMethod: string
  /** Kort-valget i metode-vælgeren (Stripe). MobilePay/Vipps vises med sit brandnavn. */
  paymentMethodCard: string
  /** Sekundært tilvalg der folder gæste-stepperen ud — så "book dig selv" forbliver
   *  den primære handling og gæster er et opt-in (som rabatkode-toggle). */
  addGuests: string
  // Embed guest checkout (gæste-gren i BookingModal, når tenant.guestCheckoutEnabled)
  /** Knap der folder gæste-formen ud. */
  guestOption: string
  /** Samtykke-checkbox-tekst (vilkår + privatliv). */
  guestConsent: string
  /** Klient-side fejl når samtykke ikke er afkrydset (intet kald sendes). */
  guestConsentRequired: string
  /** Submit-knap i gæste-formen. */
  guestSubmit: string
  /** Submit-knap mens kaldet kører. */
  guestSubmitting: string
  /** Generisk gæste-fejl (ukendt kode fra serveren). */
  guestError: string
  /** Fejl når e-mailen allerede ejer en konto (ACCOUNT_EXISTS) — log ind i stedet. */
  guestAccountExists: string
  /** Tekst over login når holdet kan købes som gæst (native: guestOrLoginPrompt). */
  guestOrLoginPrompt: string
  /** Øverst i gæste-formen: hvorfor vi spørger (native: guestIntro). */
  guestIntro: string
  /** Link til studiets handelsbetingelser i samtykket (native: guestTermsLink). */
  guestTermsLink: string
  /** Link til studiets privatlivspolitik i samtykket (native: guestPrivacyLink). */
  guestPrivacyLink: string
  /** Under samtykket: klippekort/abonnement kræver login (native: guestCoverageHint). */
  guestCoverageHint: string
  /** Tidligere gæst (H12): der er mailet et link til at vælge adgangskode (GUEST_LINK_SENT). */
  guestLinkSent: string
  // Empty states
  noBlogPosts: string
  noClassTypes: string
  noContactInfo: string
  noInstructors: string
  noPricingPlans: string
  /** OPG-0130: periodevælgeren på et kort med flere planer i samme gruppe. */
  planPeriodPicker: string
  planPeriodFourWeeks: string
  planPeriodOneMonth: string
  planPeriodThreeMonths: string
  planPeriodSixMonths: string
  planPeriodTwelveMonths: string
  planPerMonth: string
  noRooms: string
  noSeasons: string
  noTreatments: string
  noWaitlists: string
  noClassesScheduled: string
  /** Empty state for ONE navigated week (differs from "no classes at all"). */
  noClassesThisWeek: string
  noClassesDay: string
  weekOverview: string
  weekAbbrev: string
  previousWeek: string
  nextWeek: string
  thisWeek: string
  /** View-toggle labels: flat class list vs. week calendar. */
  viewList: string
  viewCalendar: string
  /** "All" chip on the pricing category filter. */
  filterAll: string
  loadTreatmentsError: string
  // Labels
  openingHours: string
  confirmed: string
  paused: string
  popular: string
  comingSoon: string
  yearly: string
  monthly: string
  classesThisMonth: string
  previousPurchases: string
  showOnMap: string
  buyGiftCard: string
  /** Alt-tekst til studiets eget gavekortbillede, når gavekortet ikke har en titel (OPG-0064). */
  giftCardImageAlt: string
  // Inline gavekort-køb (GiftCardModal). Genbruger buyGiftCard (knap), cancel,
  // continueToPurchase + purchaseHandoffBody (fallback).
  gcBuyTitle: string
  gcBuyAmount: string
  gcBuyYourName: string
  gcBuyYourEmail: string
  gcBuyRecipientEmail: string
  gcBuyMessage: string
  gcBuySubmitting: string
  registrationOpen: string
  contactFormIntro: string
  contactFormSuccess: string
  noClassesPlanned: string
  /**
   * Vises når et `weeks`-filter skjuler ALLE hold, men studiet har hold
   * længere ude. «Ingen hold planlagt» var direkte misvisende: playgroundlx
   * havde `weeks="1"` på sin side 29-09-2026, 93 hold fra 12. oktober, og
   * kunderne fik at vide at der ikke var noget. {date} = første holddato.
   */
  nextClassesStart: string
  booked: string
  full: string
  price: string
  /** Pricing-table column header for the feature list, e.g. "Inkluderer". */
  includes: string
  waitlist: string
  /** Opryknet plads, der venter på betaling (qa-venteliste-betaling). */
  awaitingPayment: string
  /** {time} = betalingsfristen. */
  payBy: string
  payNow: string
  fullyBooked: string
  loggedInAs: string
  /** Singular/plural for remaining spots, e.g. "3 pladser". */
  spot: string
  spots: string
  /** Singular/plural after a count with "available": "1 plads ledig" / "3 pladser ledige". */
  spotAvailable: string
  spotsAvailable: string
  /** «Vis antal ledige pladser» = Ca. (OPG-0078): uden tal. */
  spotsOpenLabel: string
  spotsFewLabel: string
  /** From-price label before an amount: "fra 450 kr". */
  fromPrice: string
  /** Singular/plural word for classes after a count: "8 hold". */
  classSingular: string
  classPlural: string
  signUp: string
  registrationClosed: string
  // Membership widget (logged-in body)
  /** Card-title fallback for a clip card without a plan name. */
  klippekort: string
  /** Standalone category label for single-class plans (membershipCategory "single"). */
  dropIn: string
  /** Discount code entry in the booking modal + the four errors worth naming. */
  discountCodeToggle: string
  discountCodePlaceholder: string
  discountInvalid: string
  discountExpired: string
  discountUsedUp: string
  discountAlreadyUsed: string
  discountNotForClasses: string
  discountTooManyAttempts: string
  /** Discount code in the purchase modal (clip cards/subscriptions): apply, remove, the
   *  price lines and when a subscription discount applies. {months} = a number. */
  discountApply: string
  discountRemove: string
  discountLine: string
  priceAfterDiscount: string
  discountFirstPaymentOnly: string
  /** «Træk den 1.»: første træk og det faste beløb derefter ({price}). */
  firstChargeLabel: string
  /** Samme tekst som prissidens public.pricing.signupFeeLineLabel. */
  signupFeeLineLabel: string
  /** Totalens etiket, når første betaling har gebyr eller et særskilt første træk (som prissidens). */
  firstPaymentTotalLabel: string
  firstChargeNote: string
  /** PT-ydelser (V538): adgang nu, første betaling ({price}) på {date}. */
  deferredFirstChargeNote: string
  firstChargeNoteFolded: string
  discountEveryPayment: string
  discountFreeMonths: string
  /** Card-title fallback for a subscription without a plan name. */
  subscriptionTitle: string
  /** Prefix before a date: "Startet 01.02.2026". */
  startedOn: string
  /** After a count: "8 klip tilbage". */
  clipsRemaining: string
  /** Joins remaining and total clips: "8 klip tilbage af 10". */
  ofTotal: string
  /** Prefix before a date: "Udløber den 01.02.2026". */
  expiresOn: string
  active: string
  /** Prefix before a date: "Aktiv til 01.02.2026". */
  activeUntil: string
  autoRenews: string
  noAutoRenew: string
  pauseOrCancel: string
  /** Confirm dialog before cancelling a booking; {name} is replaced with the class name. */
  confirmUnsubscribe: string
  // Newsletter widget (defaults when the embedding page sets no override)
  newsletterTitle: string
  newsletterSubtitle: string
  newsletterSubmit: string
  newsletterSubmitting: string
  newsletterThanksTitle: string
  newsletterThanksBody: string
  emailPlaceholder: string
  // Login widget
  /** Greeting; {name} is replaced with the user's display name. */
  welcomeName: string
  logout: string
  forgotPassword: string
  forgotPasswordQ: string
  /** {tenant} is replaced with the studio name. */
  forgotPasswordHint: string
  /** Instruktion over glemt-password-formularen. */
  forgotPasswordInstruction: string
  /** Knap: send nulstillings-link. */
  forgotPasswordSubmit: string
  /** Bekræftelse efter afsendt (enumeration-sikker: afslører ikke om kontoen findes). */
  forgotPasswordSent: string
  backToLogin: string
  /** Info-boks efter førstegangs-login på en konto uden adgangskode. */
  passwordSetupTitle: string
  /** {email} erstattes med mailadressen. */
  passwordSetupBody: string
  passwordTooShort: string
  createAccount: string
  /** {tenant} is replaced with the studio name. */
  createAccountAt: string
  /** {tenant} is replaced with the studio name. */
  loginAt: string
  fieldFirstName: string
  fieldLastName: string
  fieldPhone: string
  /** Parenthesized marker after an optional field label: "(valgfri)". */
  optionalSuffix: string
  fieldPassword: string
  /** Password label including the minimum-length hint. */
  fieldPasswordMin: string
  creatingAccount: string
  loggingIn: string
  alreadyHaveAccount: string
  noAccountQ: string
  createOne: string
  // Contact form
  contactTitle: string
  fillMarkedFields: string
  sending: string
  sendMessage: string
  // Contact info + testimonials
  address: string
  noTestimonials: string
  /** Star-rating aria-label; {n} is replaced with the rating. */
  starsOutOf: string
  /** Map iframe title; {tenant} is replaced with the studio name. */
  mapOf: string
  /** Joins a date and a clock time: "5. maj kl. 09:00". Empty where unidiomatic. */
  atTime: string
  unsubscribe: string
  noUpcomingClasses: string
  temperatureWater: string
  temperatureAir: string
  temperatureUpdated: string
  temperatureUnavailable: string
  noUpcomingBookings: string
  loginToSeeBookings: string
  bookClass: string
  seeClasses: string
  seePrices: string
  topUp: string
  manageMembership: string
  loginToSeeAgreement: string
  noActiveAgreement: string
  subscription: string
  soldOut: string
  closed: string
  comingSoonShort: string
  /** Knappen på en «Kontakt mig»-plan (membershipCategory 'contact'), når studiet ikke har sin egen tekst. */
  contactMe: string
  /** Overskrift/chip for kontakt-kategorien — som fanen «Andet» på den native prisside. */
  categoryOther: string
  plan: string
  fieldName: string
  fieldEmail: string
  /** F-T14-3: foran serverens forslag til en rettet adresse, fx «Mente du» navn@outlook.dk? */
  emailDidYouMean: string
  fieldMessage: string
  contactFormThanks: string
  tba: string
  /** Shown above the inline login form inside the booking modal. */
  loginOrSignUpToBook: string
  /** Follows a number: "12 on the waitlist". */
  onWaitlist: string
  joinWaitlistCta: string
  waitlistJoinedTitle: string
  yourPosition: string
  alreadyOnWaitlist: string
  loginOrSignUpToJoin: string
  /** Heading above the list of upcoming sessions for one class type. */
  upcomingSessions: string
  // Klinik-schedule widget (the house's combined treatment times)
  /** Service-picker chip that shows times for every service in the house. */
  klinikPickerAllServices: string
  klinikNoSlots: string
  klinikShowMore: string
  /** Slot-button hint; {name} is replaced with the member clinic's name. */
  klinikBookAt: string
  /** Pris for en behandling med pris pr. person (OPG-0041). {price} = formateret beløb. */
  pricePerPerson: string
  // Retreat widgets (overview + detail)
  /** Tom liste i retreats-/events-widgetten: neutral, for widgetten kender ikke typen, når listen er tom. */
  noUpcoming: string
  /** Card + tier CTA: "Se retreat". */
  viewRetreat: string
  /** Knaptekst for typen event. */
  viewEvent: string
  /** Knap under en side-delt liste (fx kompakt retreat-oversigt). */
  showMore: string
  /** Per-tier low-capacity marker. */
  fewSpotsLeft: string
  retreatAbout: string
  retreatInstructors: string
  retreatLocation: string
  retreatFaq: string
  retreatChooseTier: string
  /** Shown when a retreat exists but isn't published yet. */
  retreatNotPublished: string
  /** Event-varianter (kind='event') af detaljesidens retreat-ord. */
  eventAbout: string
  eventChooseTier: string
  eventNotPublished: string
  /** Package-travel organizer label. */
  retreatOrganizer: string
  /** Legal package-travel line; {organizer} + {regNo} are replaced. */
  retreatPackageInfo: string
  // Retreat booking modal (ported from cms.widgets.retreat.booking)
  retreatBooking: RetreatBookingStrings
  // Inline klippekort-køb (PurchaseModal)
  /** Login-prompt i købs-modalen (i modsætning til booking: "...for at købe"). */
  buyLoginPrompt: string
  /** Knap-tekst mens købet oprettes. */
  purchasing: string
  /** Fallback-knap når inline-køb ikke kan gennemføres → handoff til studiets side. */
  continueToPurchase: string
  /** Body-tekst på fallback-skærmen. */
  purchaseHandoffBody: string
}

const da: WidgetStrings = {
  checkoutLegal: {
    summaryTitle: 'Ordreoversigt',
    seller: 'Sælger: {name}',
    companyId: 'CVR {cvr}',
    platformNote: 'Holdstyring leverer kun den tekniske løsning og er ikke part i købet.',
    total: 'I alt inkl. moms',
    totalBeforeDiscount: 'I alt inkl. moms før rabatkode',
    recurringNote: 'Abonnement, der fornyes automatisk, indtil du opsiger det.',
    perFourWeeks: 'pr. 4 uger',
    perMonth: 'pr. måned',
    perQuarter: 'pr. kvartal',
    perHalfYear: 'pr. halvår',
    perYear: 'pr. år',
    oneTimeNote: 'Engangskøb.',
    acceptTerms: 'Jeg accepterer handelsbetingelserne og har læst oplysningerne om fortrydelsesret.',
    acceptTermsEarlyStart: 'Jeg accepterer handelsbetingelserne og fortrydelsesretten, og at ydelsen starter med det samme.',
    termsLink: 'Handelsbetingelser',
    withdrawalLink: 'Fortrydelsesret',
    privacyLink: 'Privatlivspolitik',
    withdrawContractLink: 'Fortryd aftale',
    noWithdrawal: 'Der er ingen fortrydelsesret ved booking af et hold på en bestemt dato.',
    paidOrderNote: 'Når du trykker på knappen, afgiver du en bestilling med betalingspligt.',
    payGeneric: 'Køb og betal',
    payAmount: 'Køb og betal ({amount})',
    payCard: 'Køb og betal med kort',
    payMobilePay: 'Køb og betal med MobilePay',
  },
  spotsOpenLabel: 'Ledige pladser',
  spotsFewLabel: 'Få pladser tilbage',
  loadError: 'Kunne ikke indlæse',
  close: 'Luk',
  cancel: 'Annullér',
  login: 'Log ind',
  readMore: 'Læs mere',
  readLess: 'Vis mindre',
  book: 'Book',
  buy: 'Køb',
  buyLoginPrompt: 'Log ind eller opret en konto for at købe',
  purchasing: 'Køber...',
  continueToPurchase: 'Fortsæt til betaling',
  purchaseHandoffBody: 'Fuldfør købet på vores hjemmeside.',
  alreadyBookedTitle: 'Du er tilmeldt',
  alreadyBookedBody: 'Du har allerede booket dette hold.',
  waitlistTitle: 'På venteliste',
  waitlistBody: 'Vi giver besked, hvis der bliver en plads ledig.',
  confirmedTitle: 'Booking bekræftet',
  confirmedBody: 'Du modtager en bekræftelse på email.',
  loginToBook: 'Log ind for at booke dette hold.',
  fullTitle: 'Holdet er fuldt',
  fullBody: 'Der er ingen venteliste på dette hold.',
  membershipRequiredBody: 'Dette hold kræver et aktivt medlemskab. Fortsæt på vores hjemmeside for at vælge medlemskab og booke.',
  paymentRequiredBody: 'Dette hold skal betales. Fortsæt på vores hjemmeside for at betale og booke.',
  classPlansHeading: 'Eller køb et klippekort',
  classPlansHeadingOnly: 'Køb et klippekort til dette hold',
  classPlansClips: '{count, plural, one {# klip} other {# klip}}',
  classPlansValidDays: '{count, plural, one {gyldigt # dag} other {gyldigt # dage}}',
  classPlansBuy: 'Køb',
  classPlansViewSubscription: 'Se abonnement',
  classPlansSubscription: 'Abonnement',
  classPlansAfterNote: 'Efter betalingen kommer du tilbage hertil. Åbn holdet igen og tryk Book, så bruges dit nye klippekort.',
  classPlansPaySingle: 'Betal enkeltbillet',
  waiverRequiredBody: 'Dette hold kræver at du accepterer studiets ansvarsfraskrivelse. Fortsæt på vores hjemmeside for at læse og acceptere den.',
  intakeRequiredBody: 'Dette hold stiller et par spørgsmål første gang. Fortsæt på vores hjemmeside for at svare — så husker vi dem til næste gang.',
  participantNamesBody: 'Dette hold kræver deltagernavne. Fortsæt på vores hjemmeside for at booke.',
  participantProfileBody: 'Hos dette studie bookes hver plads til en deltagerprofil. Vælg, hvem der skal deltage, på vores hjemmeside.',
  waitlistClipCardBody: 'Holdet er fuldt, og dit klippekort dækker det. Fortsæt på vores hjemmeside for at skrive dig på ventelisten, så dit klip bliver reserveret.',
  bookWithGuests: 'Book med gæster',
  fullVenueButton: 'Book hele holdet',
  continueToBooking: 'Fortsæt til booking',
  dropInPrice: 'Drop-in pris',
  clipCostShort: '{count, plural, one {# klip} other {# klip}}',
  priceFromPrefix: 'Fra',
  vatFreePrice: 'Momsfri pris',
  ownCancellationHeading: 'Særlig afmeldingsfrist:',
  ownCancellationDeadline: 'afmelding senest {count} timer før start.',
  ownCancellationDeadlineOne: 'afmelding senest {count} time før start.',
  ownCancellationNoRefund: 'ingen refundering ved afmelding.',
  membershipOnly: 'Medlemskab',
  includedInSubscription: 'Med i dit abonnement',
  freeClass: 'Gratis',
  booking: 'Booker...',
  joinWaitlist: 'Tilmeld venteliste',
  confirmBooking: 'Bekræft booking',
  guests: 'Gæster',
  perGuest: 'pr. gæst',
  memberSpotCovered: 'Din plads er dækket af dit medlemskab',
  coveredByClipCard: 'Dækket af dit klippekort',
  useClipCard: 'Brug klippekort',
  coveredBySubscription: 'Dækket af dit abonnement',
  confirmAndPay: 'Bekræft og betal',
  paymentMethod: 'Betalingsmetode',
  paymentMethodCard: 'Betalingskort',
  addGuests: 'Tilføj gæster',
  guestOption: 'Fortsæt som gæst',
  guestConsent: 'Jeg accepterer handelsbetingelserne og har læst privatlivspolitikken.',
  guestConsentRequired: 'Du skal acceptere vilkårene for at fortsætte.',
  guestSubmit: 'Fortsæt til betaling',
  guestSubmitting: 'Fortsætter…',
  guestError: 'Kunne ikke fortsætte som gæst. Prøv igen eller log ind.',
  guestAccountExists: 'Der findes allerede en konto med denne e-mail. Log ind i stedet.',
  guestOrLoginPrompt: 'Du kan købe din plads med det samme, eller logge ind hvis du allerede er medlem.',
  guestIntro: 'Vi skal kun bruge dine kontaktoplysninger, så vi kan sende dig en bekræftelse. Du kan oprette en adgangskode bagefter, hvis du får lyst.',
  guestTermsLink: 'Handelsbetingelser',
  guestPrivacyLink: 'Privatlivspolitik',
  guestCoverageHint: 'Har du klippekort eller abonnement? Log ind i stedet, så bruger vi det.',
  guestLinkSent: 'Du har booket hos os før med denne e-mail. Vi har sendt dig et link, så du kan vælge en adgangskode og fortsætte.',
  noBlogPosts: 'Ingen blogindlæg endnu',
  noClassTypes: 'Ingen holdtyper tilgængelige',
  noContactInfo: 'Ingen kontaktoplysninger tilgængelige',
  noInstructors: 'Ingen instruktører tilgængelige',
  noPricingPlans: 'Ingen prisplaner tilgængelige',
  planPeriodPicker: 'Vælg periode',
  planPeriodFourWeeks: '4 uger',
  planPeriodOneMonth: '1 måned',
  planPeriodThreeMonths: '3 måneder',
  planPeriodSixMonths: '6 måneder',
  planPeriodTwelveMonths: '12 måneder',
  planPerMonth: 'svarer til {amount} pr. måned',
  noRooms: 'Ingen lokaler tilgængelige',
  noSeasons: 'Ingen aktive sæsoner',
  noTreatments: 'Ingen behandlinger tilgængelige',
  noWaitlists: 'Ingen ventelister åbne for tilmelding lige nu',
  noClassesScheduled: 'Ingen hold planlagt i øjeblikket',
  noClassesThisWeek: 'Ingen hold denne uge',
  noClassesDay: 'Ingen hold',
  weekOverview: 'Ugeoversigt',
  weekAbbrev: 'Uge',
  previousWeek: 'Forrige uge',
  nextWeek: 'Næste uge',
  thisWeek: 'Denne uge',
  viewList: 'Hold',
  viewCalendar: 'Kalender',
  filterAll: 'Alle',
  loadTreatmentsError: 'Kunne ikke indlæse behandlinger',
  openingHours: 'Åbningstider',
  confirmed: 'Bekræftet',
  paused: 'På pause',
  popular: 'Populær',
  comingSoon: 'Snart tilgængelig',
  yearly: 'Årligt',
  monthly: 'Månedligt',
  classesThisMonth: 'hold denne måned',
  previousPurchases: 'Tidligere køb',
  showOnMap: 'Vis på kort',
  buyGiftCard: 'Køb gavekort',
  giftCardImageAlt: 'Gavekort',
  gcBuyTitle: 'Køb gavekort',
  gcBuyAmount: 'Beløb',
  gcBuyYourName: 'Dit navn',
  gcBuyYourEmail: 'Din e-mail',
  gcBuyRecipientEmail: 'Modtagerens e-mail (valgfri)',
  gcBuyMessage: 'Personlig hilsen (valgfri)',
  gcBuySubmitting: 'Køber...',
  registrationOpen: 'Tilmelding åben',
  contactFormIntro: 'Skriv til os, så vender vi tilbage hurtigst muligt.',
  contactFormSuccess: 'Vi har modtaget din besked og svarer så hurtigt vi kan.',
  noClassesPlanned: 'Ingen hold planlagt',
  nextClassesStart: 'Holdene starter {date}',
  booked: 'Booket',
  full: 'Fuldt',
  price: 'Pris',
  includes: 'Inkluderer',
  waitlist: 'Venteliste',
  awaitingPayment: 'Afventer betaling',
  payBy: 'Betal senest {time}',
  payNow: 'Betal nu',
  fullyBooked: 'Fuldt booket',
  loggedInAs: 'Logget ind som',
  spot: 'plads',
  spots: 'pladser',
  spotAvailable: 'plads ledig',
  spotsAvailable: 'pladser ledige',
  fromPrice: 'fra',
  classSingular: 'hold',
  classPlural: 'hold',
  signUp: 'Tilmeld dig',
  registrationClosed: 'Tilmelding lukket',
  klippekort: 'Klippekort',
  dropIn: 'Drop-in',
  discountCodeToggle: 'Har du en rabatkode?',
  discountCodePlaceholder: 'Rabatkode',
  discountInvalid: 'Rabatkoden er ikke gyldig.',
  discountExpired: 'Rabatkoden er udløbet.',
  discountUsedUp: 'Rabatkoden er opbrugt.',
  discountAlreadyUsed: 'Du har allerede brugt denne rabatkode.',
  discountNotForClasses: 'Rabatkoden gælder ikke hold.',
  discountTooManyAttempts: 'For mange forsøg. Prøv igen om lidt.',
  discountApply: 'Anvend',
  discountRemove: 'Fjern',
  discountLine: 'Rabat',
  priceAfterDiscount: 'Pris efter rabat',
  discountFirstPaymentOnly: 'Rabatten gælder den første betaling.',
  firstChargeLabel: 'Første træk',
  signupFeeLineLabel: 'Oprettelsesgebyr (nye medlemmer):',
  firstPaymentTotalLabel: 'I alt ved første betaling',
  firstChargeNote: 'Første træk dækker resten af denne måned. Derefter {price} den 1. i hver måned.',
  deferredFirstChargeNote: 'Du får adgang med det samme og betaler intet i dag. Første betaling på {price} trækkes {date}.',
  firstChargeNoteFolded: 'Første træk dækker resten af denne måned og næste hele måned. Derefter {price} den 1. i hver måned.',
  discountEveryPayment: 'Rabatten gælder hver betaling.',
  discountFreeMonths: '{months} måneder gratis før første betaling.',
  subscriptionTitle: 'Abonnement',
  startedOn: 'Startet',
  clipsRemaining: 'klip tilbage',
  ofTotal: 'af',
  expiresOn: 'Udløber den',
  active: 'Aktiv',
  activeUntil: 'Aktiv til',
  autoRenews: 'Fornyer automatisk',
  noAutoRenew: 'Fornyes ikke automatisk',
  pauseOrCancel: 'Pause / opsig',
  confirmUnsubscribe: 'Er du sikker på at du vil afmelde "{name}"?',
  newsletterTitle: 'Nyhedsbrev',
  newsletterSubtitle: 'Tilmeld dig og hold dig opdateret',
  newsletterSubmit: 'Tilmeld',
  newsletterSubmitting: 'Tilmelder...',
  newsletterThanksTitle: 'Tak!',
  newsletterThanksBody: 'Vi har tilmeldt dig vores nyhedsbrev.',
  emailPlaceholder: 'Din email-adresse',
  welcomeName: 'Velkommen, {name}!',
  logout: 'Log ud',
  forgotPassword: 'Glemt password',
  forgotPasswordQ: 'Glemt password?',
  forgotPasswordHint: 'Kontakt venligst {tenant} for at nulstille dit password.',
  forgotPasswordInstruction: 'Indtast din email, så sender vi et link til at nulstille din adgangskode.',
  forgotPasswordSubmit: 'Send nulstillings-link',
  forgotPasswordSent: 'Hvis der findes en konto med den email, har vi sendt et link til at nulstille adgangskoden. Tjek din indbakke.',
  backToLogin: 'Tilbage til log ind',
  passwordSetupTitle: 'Tjek din mail',
  passwordSetupBody: 'Vi har sendt en mail til {email}. Klik på linket for at bekræfte, så bliver adgangskoden aktiveret. Kom derefter tilbage og log ind her.',
  passwordTooShort: 'Adgangskoden skal være mindst 8 tegn.',
  createAccount: 'Opret konto',
  createAccountAt: 'Opret konto hos {tenant}.',
  loginAt: 'Log ind hos {tenant}.',
  fieldFirstName: 'Fornavn',
  fieldLastName: 'Efternavn',
  fieldPhone: 'Telefon',
  optionalSuffix: '(valgfri)',
  fieldPassword: 'Password',
  fieldPasswordMin: 'Password (min. 8 tegn)',
  creatingAccount: 'Opretter konto...',
  loggingIn: 'Logger ind...',
  alreadyHaveAccount: 'Har du allerede en konto?',
  noAccountQ: 'Har du ingen konto?',
  createOne: 'Opret en',
  contactTitle: 'Kontakt os',
  fillMarkedFields: 'Udfyld venligst de markerede felter korrekt.',
  sending: 'Sender...',
  sendMessage: 'Send besked',
  address: 'Adresse',
  noTestimonials: 'Ingen anmeldelser endnu',
  starsOutOf: '{n} ud af 5 stjerner',
  mapOf: 'Kort over {tenant}',
  atTime: 'kl.',
  unsubscribe: 'Afmeld',
  noUpcomingClasses: 'Ingen kommende hold',
  temperatureWater: 'Vandtemperatur',
  temperatureAir: 'Lufttemperatur',
  temperatureUpdated: 'Opdateret',
  temperatureUnavailable: 'Temperaturen er ikke tilgængelig lige nu',
  noUpcomingBookings: 'Du har ingen kommende bookinger',
  loginToSeeBookings: 'Log ind for at se dine bookinger',
  bookClass: 'Book hold',
  seeClasses: 'Se hold',
  seePrices: 'Se priser',
  topUp: 'Genopfyld',
  manageMembership: 'Administrer medlemskab',
  loginToSeeAgreement: 'Log ind for at se din aftale',
  noActiveAgreement: 'Du har ingen aktiv aftale',
  subscription: 'abonnement',
  soldOut: 'Udsolgt',
  closed: 'Lukket',
  comingSoonShort: 'Snart',
  contactMe: 'Kontakt mig',
  categoryOther: 'Andet',
  plan: 'Plan',
  fieldName: 'Navn',
  fieldEmail: 'Email',
  emailDidYouMean: 'Mente du',
  fieldMessage: 'Besked',
  contactFormThanks: 'Tak! Vi vender tilbage hurtigst muligt',
  tba: 'Ikke oplyst',
  loginOrSignUpToBook: 'Log ind eller opret en konto for at booke',
  onWaitlist: 'på ventelisten',
  joinWaitlistCta: 'Tilmeld dig venteliste',
  waitlistJoinedTitle: 'Du er på ventelisten',
  yourPosition: 'Din plads',
  alreadyOnWaitlist: 'Du er allerede på denne venteliste',
  loginOrSignUpToJoin: 'Log ind eller opret en konto for at komme på ventelisten til',
  upcomingSessions: 'Kommende hold',
  klinikPickerAllServices: 'Alle ydelser',
  klinikNoSlots: 'Ingen ledige tider i øjeblikket',
  klinikShowMore: 'Indlæs flere dage',
  klinikBookAt: 'Book hos {name}',
  pricePerPerson: '{price} pr. person',
  noUpcoming: 'Ingen kommende arrangementer',
  viewRetreat: 'Se retreat',
  viewEvent: 'Se event',
  showMore: 'Vis flere',
  fewSpotsLeft: 'Få pladser tilbage',
  retreatAbout: 'Om retreatet',
  retreatInstructors: 'Undervisere',
  retreatLocation: 'Sted',
  retreatFaq: 'Ofte stillede spørgsmål',
  retreatChooseTier: 'Vælg pakke',
  retreatNotPublished: 'Dette retreat er ikke offentligt endnu',
  eventAbout: 'Om eventet',
  eventChooseTier: 'Vælg billet',
  eventNotPublished: 'Dette event er ikke offentligt endnu',
  retreatOrganizer: 'Arrangør',
  retreatPackageInfo: 'Dette er en pakkerejse arrangeret af {organizer}, som er registreret i Rejsegarantifonden (reg.nr. {regNo}). Aftalen indgås med {organizer}.',
  retreatBooking: {
    modalAriaLabel: 'Booking-dialog',
    nav: {
      cancel: 'Annullér',
      back: 'Tilbage',
      next: 'Næste',
      toPayment: 'Til betaling',
      confirmFree: 'Bekræft tilmelding',
      submitting: 'Sender…',
      stepProgress: 'Trin {current} af {total}',
    },
    steps: {
      indicatorAriaLabel: 'Booking-trin',
      who: {
        title: 'Hvem booker?',
        description: 'Vælg om du booker for dig selv eller for to.',
        shortLabel: 'Hvem',
        countLegend: 'Antal personer',
        soloOption: 'Mig selv',
        soloHelp: '1 person',
        pairOption: 'For to',
        pairHelp: '2 personer på samme booking',
        privateDoubleSoloTitle: 'Du booker hele dobbeltværelset',
        privateDoubleSoloBody: 'Du har værelset for dig selv. Prisen er for hele værelset.',
        roommateLegend: 'Værelseskammerat',
        sharedDoubleSoloHelp: 'Du deler dobbeltværelset med en anden solo-booker. Vi matcher før retreat-start.',
        roommate: {
          matchMe: 'Vi matcher mig',
          matchMeHelp: 'Vi finder en passende roommate inden retreat-start',
          alone: 'Jeg vil helst alene',
          aloneHelp: 'Forsøger at give dig værelset alene — kan udløse single-supplement',
          knownPartner: 'Jeg kender min partner',
          knownPartnerHelp: 'Skriv hvem du vil dele med',
          knownPartnerNameLabel: 'Navn på roommate',
          knownPartnerNamePlaceholder: 'Fx Maja Hansen',
        },
      },
      info: {
        title: 'Deltager-info',
        description: 'Udfyld navn og e-mail for hver deltager.',
        shortLabel: 'Info',
        participantHeading: 'Deltager {index}',
        participantHeadingSingle: 'Dine oplysninger',
        firstNameLabel: 'Fornavn',
        lastNameLabel: 'Efternavn',
        emailLabel: 'E-mail',
        phoneLabel: 'Telefon',
        dietaryLabel: 'Kost-noter',
        dietaryPlaceholder: 'Allergier, vegetar, vegansk osv.',
        dietaryHelp: 'Maks 500 tegn',
        errors: {
          firstNameRequired: 'Fornavn er påkrævet',
          lastNameRequired: 'Efternavn er påkrævet',
          invalidEmail: 'Ugyldig e-mail',
          invalidPhone: 'Ugyldig telefon',
          dietaryTooLong: 'Maks 500 tegn',
        },
      },
      questions: {
        title: 'Tilmeldings-spørgsmål',
        description: 'Hjælp os med at planlægge dit ophold.',
        descriptionEvent: 'Hjælp os med at planlægge eventet.',
        shortLabel: 'Spørgsmål',
        noQuestions: 'Ingen yderligere spørgsmål.',
        selectPlaceholder: '— Vælg —',
        errors: { required: 'Påkrævet' },
      },
      payment: {
        title: 'Betaling',
        description: 'Vælg betalingsmetode.',
        shortLabel: 'Betaling',
        breakdownLine: '{count} × {tierName} a {perPerson}',
        vatLine: 'Moms ({rate}%)',
        totalLabel: 'Total',
        chooseModeLegend: 'Betalingsmodel',
        fullPayment: 'Betal nu',
        fullPaymentHelp: 'Betal hele beløbet i ét',
        depositPayment: 'Depositum',
        depositPaymentHelp: 'Betal {percent}% nu, resten senere',
        depositPaymentHelpWithDate: 'Betal {percent}% nu, resten senest {dueDate}',
        payNowShort: 'Betal nu: {amount}',
        installmentsPayment: 'Ratebetaling',
        installmentsPaymentHelp: '{count} månedlige rater',
        perMonthShort: '{amount} pr. md.',
        discountSectionTitle: 'Rabat',
        giftCardLabel: 'Gavekort',
        giftCardPlaceholder: 'Indtast gavekort-kode',
        giftCardHelp: 'Trækkes fra totalbeløbet',
        couponLabel: 'Rabatkode',
        couponPlaceholder: 'Indtast rabatkode',
        couponHelp: 'Aktiveres ved bekræftelse',
        submitting: 'Sender…',
      },
    },
    priceSummary: {
      lineLabel: '{count} × {tierName}',
      vatLabel: 'Moms ({rate}%)',
      discountLabel: 'Rabat',
      totalLabel: 'Total',
      payNowLabel: 'Betal nu',
      dueLaterLabel: 'Senere',
      depositNote: 'Depositum: {percent}% betales nu, resten senere',
      installmentsNote: 'Ratebetaling: {count} månedlige rater',
    },
    errors: {
      capacityTaken: 'Pladsen blev desværre booket mens du udfyldte. Vælg en anden type eller skriv dig på venteliste.',
      paymentNotConfigured: "Online betaling er ikke åbnet endnu for dette tilbud. Kontakt studiet for at tilmelde dig.",
      paymentProviderError: "Betalingen kunne ikke startes. Prøv igen om lidt, eller kontakt studiet.",
      paymentModeUnavailable: "Betalingen er ikke sat op for dette tilbud endnu. Kontakt studiet.",
      notAvailable: "Det kan ikke bookes lige nu. Kontakt studiet.",
      validation: 'Tjek venligst at alle felter er udfyldt korrekt.',
      generic: 'Noget gik galt — prøv igen om et øjeblik.',
      network: 'Netværksfejl — tjek din forbindelse og prøv igen.',
      coupon: {
        notAllowed: 'Rabatkoder kan ikke bruges på dette retreat.',
        unknown: 'Rabatkoden findes ikke. Tjek stavemåden.',
        notStarted: 'Rabatkoden er ikke aktiv endnu.',
        expired: 'Rabatkoden er udløbet.',
        usedUp: 'Rabatkoden er brugt op.',
        alreadyUsed: 'Du har allerede brugt denne rabatkode.',
        newCustomersOnly: 'Rabatkoden gælder kun nye kunder.',
        requiresMembership: 'Rabatkoden kræver et aktivt medlemskab.',
        requiresSpecificMembership: 'Rabatkoden gælder kun for kunder med et bestemt medlemskab eller forløb. Er du medlem, så log ind eller brug den e-mail, du er oprettet med.',
        wrongProduct: 'Rabatkoden gælder ikke retreats.',
        otherRetreat: 'Rabatkoden gælder ikke dette retreat.',
        notAllowedEvent: 'Rabatkoder kan ikke bruges på dette event.',
        wrongProductEvent: 'Rabatkoden gælder ikke events.',
        otherRetreatEvent: 'Rabatkoden gælder ikke dette event.',
        belowMinimum: 'Rabatkoden kræver et køb på mindst {minimum}.',
      },
    },
    packageTravel: {
      heading: 'Dette er en pakkerejse',
      organizerNote: 'Du indgår aftale med {organizer} — ikke med Holdstyring. {organizer} er arrangør og ansvarlig for pakkerejsen.',
      registeredLine: '{organizer} er registreret i Rejsegarantifonden, medlemsnr. {memberNumber}.',
      formToggle: 'Læs om dine rettigheder som rejsende',
      formIntro: 'Den kombination af rejseydelser, du tilbydes, er en pakkerejse i henhold til lov om pakkerejser og sammensatte rejsearrangementer. Du er derfor omfattet af alle rettigheder, der gælder for pakkerejser.',
      formResponsibility: '{organizer} er fuldt ansvarlig for den korrekte gennemførelse af hele pakkerejsen og har den lovpligtige konkursdækning hos Rejsegarantifonden (medlemsnr. {memberNumber}).',
      rightsTitle: 'Centrale rettigheder',
      right1: 'Du får alle væsentlige oplysninger om pakkerejsen, inden du indgår aftalen.',
      right2: 'Mindst én erhvervsdrivende er ansvarlig for korrekt levering af alle rejseydelser i aftalen.',
      right3: 'Stiger prisen mere end 8 %, kan du opsige aftalen.',
      right4: 'Du kan opsige uden gebyr og få fuld refusion, hvis væsentlige elementer ændres væsentligt, eller ved uundgåelige og ekstraordinære omstændigheder.',
      right5: 'Refusion sker uden unødig forsinkelse og senest 14 dage efter.',
      right6: 'Bliver arrangøren insolvent, refunderes dine betalinger via Rejsegarantifonden.',
      contact: 'Konkursdækning: Rejsegarantifonden, Røjelskær 11, 2840 Holte — info@rejsegarantifonden.dk, +45 45 87 83 33.',
      consentLabel: 'Jeg har læst oplysningerne om pakkerejsen og accepterer betingelserne.',
      logoAlt: 'Rejsegarantifonden',
    },
  },
}

const en: WidgetStrings = {
  checkoutLegal: {
    summaryTitle: 'Order summary',
    seller: 'Seller: {name}',
    companyId: 'Company no. {cvr}',
    platformNote: 'Holdstyring only provides the technical solution and is not a party to the purchase.',
    total: 'Total incl. VAT',
    totalBeforeDiscount: 'Total incl. VAT before discount code',
    recurringNote: 'Subscription that renews automatically until you cancel it.',
    perFourWeeks: 'per 4 weeks',
    perMonth: 'per month',
    perQuarter: 'per quarter',
    perHalfYear: 'per half year',
    perYear: 'per year',
    oneTimeNote: 'One-time purchase.',
    acceptTerms: 'I accept the terms of service and have read the information on the right of withdrawal.',
    acceptTermsEarlyStart: 'I accept the terms of service and the right of withdrawal, and that the service starts immediately.',
    termsLink: 'Terms of service',
    withdrawalLink: 'Right of withdrawal',
    privacyLink: 'Privacy policy',
    withdrawContractLink: 'Withdraw from contract',
    noWithdrawal: 'There is no right of withdrawal when booking a class on a specific date.',
    paidOrderNote: 'By clicking the button you place an order with an obligation to pay.',
    payGeneric: 'Buy and pay',
    payAmount: 'Buy and pay ({amount})',
    payCard: 'Buy and pay by card',
    payMobilePay: 'Buy and pay with MobilePay',
  },
  spotsOpenLabel: 'Spots available',
  spotsFewLabel: 'Few spots left',
  loadError: 'Could not load',
  close: 'Close',
  cancel: 'Cancel',
  login: 'Log in',
  readMore: 'Read more',
  readLess: 'Show less',
  book: 'Book',
  buy: 'Buy',
  buyLoginPrompt: 'Log in or create an account to buy',
  purchasing: 'Buying...',
  continueToPurchase: 'Continue to checkout',
  purchaseHandoffBody: 'Complete your purchase on our website.',
  alreadyBookedTitle: "You're booked",
  alreadyBookedBody: 'You have already booked this class.',
  waitlistTitle: 'On the waitlist',
  waitlistBody: "We'll let you know if a spot opens up.",
  confirmedTitle: 'Booking confirmed',
  confirmedBody: "You'll receive a confirmation by email.",
  loginToBook: 'Log in to book this class.',
  fullTitle: 'This class is full',
  fullBody: 'There is no waitlist for this class.',
  membershipRequiredBody: 'This class requires an active membership. Continue on our website to choose a membership and book.',
  paymentRequiredBody: 'This class needs to be paid for. Continue on our website to pay and book.',
  classPlansHeading: 'Or buy a clip card',
  classPlansHeadingOnly: 'Buy a clip card for this class',
  classPlansClips: '{count, plural, one {# class} other {# classes}}',
  classPlansValidDays: '{count, plural, one {valid for # day} other {valid for # days}}',
  classPlansBuy: 'Buy',
  classPlansViewSubscription: 'View subscription',
  classPlansSubscription: 'Subscription',
  classPlansAfterNote: 'After paying you will return here. Open the class again and press Book, and your new clip card will be used.',
  classPlansPaySingle: 'Pay single ticket',
  waiverRequiredBody: 'This class requires accepting the studio declaration. Continue on our website to read and accept it.',
  intakeRequiredBody: 'This class asks a few questions the first time. Continue on our website to answer them — we remember them next time.',
  participantNamesBody: 'This class needs the names of who is attending. Continue on our website to book.',
  participantProfileBody: 'At this studio every spot is booked for a participant profile. Choose who is attending on our website.',
  waitlistClipCardBody: 'This class is full and your class pass covers it. Continue on our website to join the waitlist and have a class reserved on your pass.',
  bookWithGuests: 'Book with guests',
  fullVenueButton: 'Book the whole class',
  continueToBooking: 'Continue to booking',
  dropInPrice: 'Drop-in price',
  clipCostShort: '{count, plural, one {# clip} other {# clips}}',
  priceFromPrefix: 'From',
  vatFreePrice: 'VAT-free price',
  ownCancellationHeading: 'Special cancellation deadline:',
  ownCancellationDeadline: 'cancel at least {count} hours before the start.',
  ownCancellationDeadlineOne: 'cancel at least {count} hour before the start.',
  ownCancellationNoRefund: 'no refund on cancellation.',
  membershipOnly: 'Membership',
  includedInSubscription: 'Included in your subscription',
  freeClass: 'Free',
  booking: 'Booking...',
  joinWaitlist: 'Join waitlist',
  confirmBooking: 'Confirm booking',
  guests: 'Guests',
  perGuest: 'per guest',
  memberSpotCovered: 'Your spot is covered by your membership',
  coveredByClipCard: 'Covered by your class pass',
  useClipCard: 'Use punch card',
  coveredBySubscription: 'Covered by your subscription',
  confirmAndPay: 'Confirm and pay',
  paymentMethod: 'Payment method',
  paymentMethodCard: 'Card',
  addGuests: 'Add guests',
  guestOption: 'Continue as guest',
  guestConsent: 'I accept the terms of trade and have read the privacy policy.',
  guestConsentRequired: 'You must accept the terms to continue.',
  guestSubmit: 'Continue to payment',
  guestSubmitting: 'Continuing…',
  guestError: 'Could not continue as guest. Please try again or log in.',
  guestAccountExists: 'An account already exists for this email. Please log in instead.',
  guestOrLoginPrompt: 'You can buy your spot right away, or log in if you are already a member.',
  guestIntro: 'We only need your contact details so we can send you a confirmation. You can create a password afterwards if you want to.',
  guestTermsLink: 'Terms of trade',
  guestPrivacyLink: 'Privacy policy',
  guestCoverageHint: 'Got a punch card or a membership? Log in instead and we will use it.',
  guestLinkSent: 'You have booked with us before using this email. We have sent you a link to choose a password and continue.',
  noBlogPosts: 'No blog posts yet',
  noClassTypes: 'No class types available',
  noContactInfo: 'No contact details available',
  noInstructors: 'No instructors available',
  noPricingPlans: 'No pricing plans available',
  planPeriodPicker: 'Choose period',
  planPeriodFourWeeks: '4 weeks',
  planPeriodOneMonth: '1 month',
  planPeriodThreeMonths: '3 months',
  planPeriodSixMonths: '6 months',
  planPeriodTwelveMonths: '12 months',
  planPerMonth: 'equals {amount} per month',
  noRooms: 'No rooms available',
  noSeasons: 'No active seasons',
  noTreatments: 'No treatments available',
  noWaitlists: 'No waitlists open for sign-up right now',
  noClassesScheduled: 'No classes scheduled at the moment',
  noClassesThisWeek: 'No classes this week',
  noClassesDay: 'No classes',
  weekOverview: 'Week overview',
  weekAbbrev: 'Week',
  previousWeek: 'Previous week',
  nextWeek: 'Next week',
  thisWeek: 'This week',
  viewList: 'Classes',
  viewCalendar: 'Calendar',
  filterAll: 'All',
  loadTreatmentsError: 'Could not load treatments',
  openingHours: 'Opening hours',
  confirmed: 'Confirmed',
  paused: 'Paused',
  popular: 'Popular',
  comingSoon: 'Coming soon',
  yearly: 'Yearly',
  monthly: 'Monthly',
  classesThisMonth: 'classes this month',
  previousPurchases: 'Previous purchases',
  showOnMap: 'Show on map',
  buyGiftCard: 'Buy gift card',
  giftCardImageAlt: 'Gift card',
  gcBuyTitle: 'Buy a gift card',
  gcBuyAmount: 'Amount',
  gcBuyYourName: 'Your name',
  gcBuyYourEmail: 'Your email',
  gcBuyRecipientEmail: 'Recipient email (optional)',
  gcBuyMessage: 'Personal message (optional)',
  gcBuySubmitting: 'Buying...',
  registrationOpen: 'Registration open',
  contactFormIntro: "Write to us and we'll get back to you as soon as possible.",
  contactFormSuccess: "We've received your message and will reply as soon as we can.",
  noClassesPlanned: 'No classes scheduled',
  nextClassesStart: 'Classes start {date}',
  booked: 'Booked',
  full: 'Full',
  price: 'Price',
  includes: 'Includes',
  waitlist: 'Waitlist',
  awaitingPayment: 'Awaiting payment',
  payBy: 'Pay by {time}',
  payNow: 'Pay now',
  fullyBooked: 'Fully booked',
  loggedInAs: 'Logged in as',
  spot: 'spot',
  spots: 'spots',
  spotAvailable: 'spot left',
  spotsAvailable: 'spots left',
  fromPrice: 'from',
  classSingular: 'class',
  classPlural: 'classes',
  signUp: 'Sign up',
  registrationClosed: 'Registration closed',
  klippekort: 'Class pass',
  dropIn: 'Drop-in',
  discountCodeToggle: 'Have a discount code?',
  discountCodePlaceholder: 'Discount code',
  discountInvalid: 'This discount code is not valid.',
  discountExpired: 'This discount code has expired.',
  discountUsedUp: 'This discount code has been fully used.',
  discountAlreadyUsed: 'You have already used this discount code.',
  discountNotForClasses: 'This discount code does not apply to classes.',
  discountTooManyAttempts: 'Too many attempts. Please try again shortly.',
  discountApply: 'Apply',
  discountRemove: 'Remove',
  discountLine: 'Discount',
  priceAfterDiscount: 'Price after discount',
  discountFirstPaymentOnly: 'The discount applies to the first payment.',
  firstChargeLabel: 'First charge',
  signupFeeLineLabel: 'Signup fee (new members):',
  firstPaymentTotalLabel: 'Total first payment',
  firstChargeNote: 'The first charge covers the rest of this month. After that {price} on the 1st of every month.',
  deferredFirstChargeNote: 'You get access right away and pay nothing today. The first payment of {price} is charged on {date}.',
  firstChargeNoteFolded: 'The first charge covers the rest of this month and the next full month. After that {price} on the 1st of every month.',
  discountEveryPayment: 'The discount applies to every payment.',
  discountFreeMonths: '{months} months free before the first payment.',
  subscriptionTitle: 'Subscription',
  startedOn: 'Started',
  clipsRemaining: 'clips remaining',
  ofTotal: 'of',
  expiresOn: 'Expires',
  active: 'Active',
  activeUntil: 'Active until',
  autoRenews: 'Renews automatically',
  noAutoRenew: 'Does not renew automatically',
  pauseOrCancel: 'Pause / cancel',
  confirmUnsubscribe: 'Are you sure you want to cancel "{name}"?',
  newsletterTitle: 'Newsletter',
  newsletterSubtitle: 'Sign up and stay updated',
  newsletterSubmit: 'Sign up',
  newsletterSubmitting: 'Signing up...',
  newsletterThanksTitle: 'Thank you!',
  newsletterThanksBody: 'You are now subscribed to our newsletter.',
  emailPlaceholder: 'Your email address',
  welcomeName: 'Welcome, {name}!',
  logout: 'Log out',
  forgotPassword: 'Forgot password',
  forgotPasswordQ: 'Forgot password?',
  forgotPasswordHint: 'Please contact {tenant} to reset your password.',
  forgotPasswordInstruction: "Enter your email and we'll send you a link to reset your password.",
  forgotPasswordSubmit: 'Send reset link',
  forgotPasswordSent: "If an account exists for that email, we've sent a link to reset your password. Check your inbox.",
  backToLogin: 'Back to login',
  passwordSetupTitle: 'Check your email',
  passwordSetupBody: "We've sent an email to {email}. Click the link to confirm and your password will be activated. Then come back and log in here.",
  passwordTooShort: 'Password must be at least 8 characters.',
  createAccount: 'Create account',
  createAccountAt: 'Create an account with {tenant}.',
  loginAt: 'Log in to {tenant}.',
  fieldFirstName: 'First name',
  fieldLastName: 'Last name',
  fieldPhone: 'Phone',
  optionalSuffix: '(optional)',
  fieldPassword: 'Password',
  fieldPasswordMin: 'Password (min. 8 characters)',
  creatingAccount: 'Creating account...',
  loggingIn: 'Logging in...',
  alreadyHaveAccount: 'Already have an account?',
  noAccountQ: "Don't have an account?",
  createOne: 'Create one',
  contactTitle: 'Contact us',
  fillMarkedFields: 'Please fill in the highlighted fields correctly.',
  sending: 'Sending...',
  sendMessage: 'Send message',
  address: 'Address',
  noTestimonials: 'No reviews yet',
  starsOutOf: '{n} out of 5 stars',
  mapOf: 'Map of {tenant}',
  atTime: 'at',
  unsubscribe: 'Cancel booking',
  noUpcomingClasses: 'No upcoming classes',
  temperatureWater: 'Water temperature',
  temperatureAir: 'Air temperature',
  temperatureUpdated: 'Updated',
  temperatureUnavailable: 'The temperature is unavailable right now',
  noUpcomingBookings: 'You have no upcoming bookings',
  loginToSeeBookings: 'Log in to see your bookings',
  bookClass: 'Book class',
  seeClasses: 'See classes',
  seePrices: 'See prices',
  topUp: 'Top up',
  manageMembership: 'Manage membership',
  loginToSeeAgreement: 'Log in to see your plan',
  noActiveAgreement: 'You have no active plan',
  subscription: 'subscription',
  soldOut: 'Sold out',
  closed: 'Closed',
  comingSoonShort: 'Soon',
  contactMe: 'Contact me',
  categoryOther: 'Other',
  plan: 'Plan',
  fieldName: 'Name',
  fieldEmail: 'Email',
  emailDidYouMean: 'Did you mean',
  fieldMessage: 'Message',
  contactFormThanks: "Thank you! We'll get back to you shortly",
  tba: 'TBA',
  loginOrSignUpToBook: 'Log in or create an account to book',
  onWaitlist: 'on the waitlist',
  joinWaitlistCta: 'Join the waitlist',
  waitlistJoinedTitle: "You're on the waitlist",
  yourPosition: 'Your position',
  alreadyOnWaitlist: "You're already on this waitlist",
  loginOrSignUpToJoin: 'Log in or create an account to join the waitlist for',
  upcomingSessions: 'Upcoming sessions',
  klinikPickerAllServices: 'All services',
  klinikNoSlots: 'No available times right now',
  klinikShowMore: 'Load more days',
  klinikBookAt: 'Book at {name}',
  pricePerPerson: '{price} per person',
  noUpcoming: 'No upcoming events',
  viewRetreat: 'View retreat',
  viewEvent: 'View event',
  showMore: 'Show more',
  fewSpotsLeft: 'Few spots left',
  retreatAbout: 'About this retreat',
  retreatInstructors: 'Facilitators',
  retreatLocation: 'Location',
  retreatFaq: 'Frequently asked questions',
  retreatChooseTier: 'Choose a package',
  retreatNotPublished: 'This retreat is not public yet',
  eventAbout: 'About this event',
  eventChooseTier: 'Choose a ticket',
  eventNotPublished: 'This event is not public yet',
  retreatOrganizer: 'Organizer',
  retreatPackageInfo: 'This is a package holiday arranged by {organizer}, registered with the Danish Travel Guarantee Fund (reg. no. {regNo}). The agreement is made with {organizer}.',
  retreatBooking: {
    modalAriaLabel: 'Booking dialog',
    nav: {
      cancel: 'Cancel',
      back: 'Back',
      next: 'Next',
      toPayment: 'To payment',
      confirmFree: 'Confirm booking',
      submitting: 'Sending…',
      stepProgress: 'Step {current} of {total}',
    },
    steps: {
      indicatorAriaLabel: 'Booking steps',
      who: {
        title: "Who's booking?",
        description: "Choose whether you're booking for yourself or for two.",
        shortLabel: 'Who',
        countLegend: 'Number of people',
        soloOption: 'Just me',
        soloHelp: '1 person',
        pairOption: 'For two',
        pairHelp: '2 people on the same booking',
        privateDoubleSoloTitle: "You're booking the whole double room",
        privateDoubleSoloBody: 'You have the room to yourself. Price is for the whole room.',
        roommateLegend: 'Roommate',
        sharedDoubleSoloHelp: "You'll share the double with another solo booker. We match before the retreat starts.",
        roommate: {
          matchMe: 'Match me',
          matchMeHelp: "We'll find a suitable roommate before the retreat",
          alone: "I'd prefer to be alone",
          aloneHelp: "We'll try to give you the room solo — may trigger single supplement",
          knownPartner: 'I know my partner',
          knownPartnerHelp: 'Tell us who you want to share with',
          knownPartnerNameLabel: 'Roommate name',
          knownPartnerNamePlaceholder: 'e.g. Maja Hansen',
        },
      },
      info: {
        title: 'Participant info',
        description: 'Fill in name and email for each participant.',
        shortLabel: 'Info',
        participantHeading: 'Participant {index}',
        participantHeadingSingle: 'Your details',
        firstNameLabel: 'First name',
        lastNameLabel: 'Last name',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        dietaryLabel: 'Dietary notes',
        dietaryPlaceholder: 'Allergies, vegetarian, vegan etc.',
        dietaryHelp: 'Max 500 characters',
        errors: {
          firstNameRequired: 'First name is required',
          lastNameRequired: 'Last name is required',
          invalidEmail: 'Invalid email',
          invalidPhone: 'Invalid phone',
          dietaryTooLong: 'Max 500 characters',
        },
      },
      questions: {
        title: 'Booking questions',
        description: 'Help us plan your stay.',
        descriptionEvent: 'Help us plan the event.',
        shortLabel: 'Questions',
        noQuestions: 'No additional questions.',
        selectPlaceholder: '— Choose —',
        errors: { required: 'Required' },
      },
      payment: {
        title: 'Payment',
        description: 'Choose payment method.',
        shortLabel: 'Payment',
        breakdownLine: '{count} × {tierName} at {perPerson}',
        vatLine: 'VAT ({rate}%)',
        totalLabel: 'Total',
        chooseModeLegend: 'Payment mode',
        fullPayment: 'Pay now',
        fullPaymentHelp: 'Pay the full amount in one',
        depositPayment: 'Deposit',
        depositPaymentHelp: 'Pay {percent}% now, the rest later',
        depositPaymentHelpWithDate: 'Pay {percent}% now, the rest by {dueDate}',
        payNowShort: 'Pay now: {amount}',
        installmentsPayment: 'Installments',
        installmentsPaymentHelp: '{count} monthly installments',
        perMonthShort: '{amount} per month',
        discountSectionTitle: 'Discount',
        giftCardLabel: 'Gift card',
        giftCardPlaceholder: 'Enter gift card code',
        giftCardHelp: 'Deducted from the total',
        couponLabel: 'Discount code',
        couponPlaceholder: 'Enter discount code',
        couponHelp: 'Applied on confirmation',
        submitting: 'Sending…',
      },
    },
    priceSummary: {
      lineLabel: '{count} × {tierName}',
      vatLabel: 'VAT ({rate}%)',
      discountLabel: 'Discount',
      totalLabel: 'Total',
      payNowLabel: 'Pay now',
      dueLaterLabel: 'Later',
      depositNote: 'Deposit: {percent}% now, the rest later',
      installmentsNote: 'Installments: {count} monthly payments',
    },
    errors: {
      capacityTaken: 'Unfortunately the spot was booked while you were filling out the form. Choose another type or join the waitlist.',
      paymentNotConfigured: "Online payment is not open yet for this offer. Please contact the studio to sign up.",
      paymentProviderError: "The payment could not be started. Please try again shortly or contact the studio.",
      paymentModeUnavailable: "Payment is not set up for this offer yet. Please contact the studio.",
      notAvailable: "This cannot be booked right now. Please contact the studio.",
      validation: 'Please check that all fields are filled in correctly.',
      generic: 'Something went wrong — try again in a moment.',
      network: 'Network error — check your connection and try again.',
      coupon: {
        notAllowed: 'Discount codes cannot be used for this retreat.',
        unknown: 'That discount code does not exist. Check the spelling.',
        notStarted: 'That discount code is not active yet.',
        expired: 'That discount code has expired.',
        usedUp: 'That discount code has been fully used.',
        alreadyUsed: 'You have already used this discount code.',
        newCustomersOnly: 'That discount code is for new customers only.',
        requiresMembership: 'That discount code requires an active membership.',
        requiresSpecificMembership: 'This discount code is only for customers with a specific membership or course. If you are a member, log in or use the email you signed up with.',
        wrongProduct: 'That discount code does not apply to retreats.',
        otherRetreat: 'That discount code does not apply to this retreat.',
        notAllowedEvent: 'Discount codes cannot be used for this event.',
        wrongProductEvent: 'That discount code does not apply to events.',
        otherRetreatEvent: 'That discount code does not apply to this event.',
        belowMinimum: 'That discount code requires a purchase of at least {minimum}.',
      },
    },
    packageTravel: {
      heading: 'This is a package tour',
      organizerNote: 'You are entering into an agreement with {organizer} — not with Class Booking. {organizer} is the organizer and is responsible for the package tour.',
      registeredLine: '{organizer} is registered with Rejsegarantifonden (the Danish Travel Guarantee Fund), member no. {memberNumber}.',
      formToggle: 'Read about your rights as a traveller',
      formIntro: 'The combination of travel services offered to you is a package within the meaning of the Danish Package Travel Act. You therefore benefit from all rights that apply to packages.',
      formResponsibility: '{organizer} is fully responsible for the proper performance of the package as a whole and holds the statutory insolvency protection with Rejsegarantifonden (member no. {memberNumber}).',
      rightsTitle: 'Key rights',
      right1: 'You receive all essential information about the package before concluding the contract.',
      right2: 'At least one trader is responsible for the proper performance of all travel services in the contract.',
      right3: 'If the price increases by more than 8%, you may terminate the contract.',
      right4: 'You may terminate without a fee and receive a full refund if essential elements change significantly, or in the event of unavoidable and extraordinary circumstances.',
      right5: 'Refunds are made without undue delay and no later than 14 days afterwards.',
      right6: 'If the organizer becomes insolvent, your payments are refunded via Rejsegarantifonden.',
      contact: 'Insolvency protection: Rejsegarantifonden, Røjelskær 11, 2840 Holte, Denmark — info@rejsegarantifonden.dk, +45 45 87 83 33.',
      consentLabel: 'I have read the package tour information and accept the terms.',
      logoAlt: 'Rejsegarantifonden',
    },
  },
}

const de: WidgetStrings = {
  checkoutLegal: {
    summaryTitle: 'Bestellübersicht',
    seller: 'Verkäufer: {name}',
    companyId: 'Unternehmensnummer {cvr}',
    platformNote: 'Holdstyring stellt nur die technische Lösung bereit und ist nicht Vertragspartei.',
    total: 'Gesamt inkl. MwSt.',
    totalBeforeDiscount: 'Gesamt inkl. MwSt. vor Rabattcode',
    recurringNote: 'Abonnement, das sich automatisch verlängert, bis Sie kündigen.',
    perFourWeeks: 'pro 4 Wochen',
    perMonth: 'pro Monat',
    perQuarter: 'pro Quartal',
    perHalfYear: 'pro Halbjahr',
    perYear: 'pro Jahr',
    oneTimeNote: 'Einmaliger Kauf.',
    acceptTerms: 'Ich akzeptiere die AGB und habe die Widerrufsbelehrung gelesen.',
    acceptTermsEarlyStart: 'Ich akzeptiere die AGB und die Widerrufsbelehrung und verlange ausdrücklich, dass die Leistung sofort beginnt.',
    termsLink: 'AGB',
    withdrawalLink: 'Widerrufsbelehrung',
    privacyLink: 'Datenschutz',
    withdrawContractLink: 'Vertrag widerrufen',
    noWithdrawal: 'Für die Buchung einer Stunde zu einem bestimmten Termin besteht kein Widerrufsrecht.',
    paidOrderNote: 'Mit Klick auf die Schaltfläche geben Sie eine zahlungspflichtige Bestellung ab.',
    payGeneric: 'Zahlungspflichtig bestellen',
    payAmount: 'Zahlungspflichtig bestellen ({amount})',
    payCard: 'Mit Karte zahlungspflichtig bestellen',
    payMobilePay: 'Mit MobilePay zahlungspflichtig bestellen',
  },
  spotsOpenLabel: 'Plätze frei',
  spotsFewLabel: 'Nur noch wenige Plätze',
  loadError: 'Konnte nicht geladen werden',
  close: 'Schließen',
  cancel: 'Abbrechen',
  login: 'Anmelden',
  readMore: 'Mehr lesen',
  readLess: 'Weniger anzeigen',
  book: 'Buchen',
  buy: 'Kaufen',
  buyLoginPrompt: 'Melden Sie sich an oder erstellen Sie ein Konto, um zu kaufen',
  purchasing: 'Wird gekauft...',
  continueToPurchase: 'Weiter zur Zahlung',
  purchaseHandoffBody: 'Schließen Sie Ihren Kauf auf unserer Website ab.',
  alreadyBookedTitle: 'Sie sind angemeldet',
  alreadyBookedBody: 'Sie haben diesen Kurs bereits gebucht.',
  waitlistTitle: 'Auf der Warteliste',
  waitlistBody: 'Wir melden uns, sobald ein Platz frei wird.',
  confirmedTitle: 'Buchung bestätigt',
  confirmedBody: 'Sie erhalten eine Bestätigung per E-Mail.',
  loginToBook: 'Melden Sie sich an, um diesen Kurs zu buchen.',
  fullTitle: 'Der Kurs ist ausgebucht',
  fullBody: 'Für diesen Kurs gibt es keine Warteliste.',
  membershipRequiredBody: 'Dieser Kurs erfordert eine aktive Mitgliedschaft. Fahren Sie auf unserer Website fort, um eine Mitgliedschaft zu wählen und zu buchen.',
  paymentRequiredBody: 'Dieser Kurs ist kostenpflichtig. Fahren Sie auf unserer Website fort, um zu bezahlen und zu buchen.',
  classPlansHeading: 'Oder eine Mehrfachkarte kaufen',
  classPlansHeadingOnly: 'Mehrfachkarte für diesen Kurs kaufen',
  classPlansClips: '{count, plural, one {# Einheit} other {# Einheiten}}',
  classPlansValidDays: '{count, plural, one {# Tag gültig} other {# Tage gültig}}',
  classPlansBuy: 'Kaufen',
  classPlansViewSubscription: 'Abonnement ansehen',
  classPlansSubscription: 'Abonnement',
  classPlansAfterNote: 'Nach der Zahlung kommen Sie hierher zurück. Öffnen Sie den Kurs erneut und klicken Sie auf Buchen, dann wird Ihre neue Mehrfachkarte verwendet.',
  classPlansPaySingle: 'Einzelticket bezahlen',
  waiverRequiredBody: 'Für diesen Kurs müssen Sie die Haftungserklärung des Studios akzeptieren. Fahren Sie auf unserer Website fort, um sie zu lesen und zu akzeptieren.',
  intakeRequiredBody: 'Dieser Kurs stellt beim ersten Mal ein paar Fragen. Fahren Sie auf unserer Website fort, um sie zu beantworten — danach merken wir sie uns.',
  participantNamesBody: 'Für diesen Kurs sind die Namen der Teilnehmenden erforderlich. Fahren Sie auf unserer Website fort, um zu buchen.',
  participantProfileBody: 'In diesem Studio wird jeder Platz für ein Teilnehmerprofil gebucht. Wählen Sie auf unserer Website aus, wer teilnimmt.',
  waitlistClipCardBody: 'Dieser Kurs ist ausgebucht, und Ihre Mehrfachkarte deckt ihn ab. Fahren Sie auf unserer Website fort, um sich auf die Warteliste zu setzen, damit Ihre Einheit reserviert wird.',
  bookWithGuests: 'Mit Gästen buchen',
  fullVenueButton: 'Den ganzen Kurs buchen',
  continueToBooking: 'Weiter zur Buchung',
  dropInPrice: 'Drop-in-Preis',
  clipCostShort: '{count, plural, one {# Einheit} other {# Einheiten}}',
  priceFromPrefix: 'Ab',
  vatFreePrice: 'MwSt.-befreiter Preis',
  ownCancellationHeading: 'Besondere Abmeldefrist:',
  ownCancellationDeadline: 'Abmeldung mindestens {count} Stunden vor Beginn.',
  ownCancellationDeadlineOne: 'Abmeldung mindestens {count} Stunde vor Beginn.',
  ownCancellationNoRefund: 'keine Erstattung bei Abmeldung.',
  membershipOnly: 'Mitgliedschaft',
  includedInSubscription: 'In Ihrem Abo enthalten',
  freeClass: 'Kostenlos',
  booking: 'Wird gebucht...',
  joinWaitlist: 'Auf Warteliste setzen',
  confirmBooking: 'Buchung bestätigen',
  guests: 'Gäste',
  perGuest: 'pro Gast',
  memberSpotCovered: 'Ihr Platz ist durch Ihr Abo abgedeckt',
  coveredByClipCard: 'Mit Ihrer Mehrfachkarte abgedeckt',
  useClipCard: 'Mehrfachkarte verwenden',
  coveredBySubscription: 'Durch Ihr Abo abgedeckt',
  confirmAndPay: 'Bestätigen und bezahlen',
  paymentMethod: 'Zahlungsmethode',
  paymentMethodCard: 'Karte',
  addGuests: 'Gäste hinzufügen',
  guestOption: 'Als Gast fortfahren',
  guestConsent: 'Ich akzeptiere die Geschäftsbedingungen und habe die Datenschutzerklärung gelesen.',
  guestConsentRequired: 'Sie müssen die Bedingungen akzeptieren, um fortzufahren.',
  guestSubmit: 'Weiter zur Zahlung',
  guestSubmitting: 'Wird fortgesetzt…',
  guestError: 'Fortfahren als Gast nicht möglich. Bitte erneut versuchen oder anmelden.',
  guestAccountExists: 'Für diese E-Mail existiert bereits ein Konto. Bitte melden Sie sich stattdessen an.',
  guestOrLoginPrompt: 'Sie können Ihren Platz sofort kaufen oder sich anmelden, wenn Sie bereits Mitglied sind.',
  guestIntro: 'Wir benötigen nur Ihre Kontaktdaten, damit wir Ihnen eine Bestätigung senden können. Ein Passwort können Sie später anlegen, wenn Sie möchten.',
  guestTermsLink: 'Geschäftsbedingungen',
  guestPrivacyLink: 'Datenschutzerklärung',
  guestCoverageHint: 'Haben Sie eine Mehrfachkarte oder ein Abo? Melden Sie sich stattdessen an, dann nutzen wir es.',
  guestLinkSent: 'Sie haben mit dieser E-Mail schon einmal bei uns gebucht. Wir haben Ihnen einen Link geschickt, mit dem Sie ein Passwort wählen und fortfahren können.',
  noBlogPosts: 'Noch keine Blogbeiträge',
  noClassTypes: 'Keine Kursarten verfügbar',
  noContactInfo: 'Keine Kontaktdaten verfügbar',
  noInstructors: 'Keine Kursleiter verfügbar',
  noPricingPlans: 'Keine Preispläne verfügbar',
  planPeriodPicker: 'Zeitraum wählen',
  planPeriodFourWeeks: '4 Wochen',
  planPeriodOneMonth: '1 Monat',
  planPeriodThreeMonths: '3 Monate',
  planPeriodSixMonths: '6 Monate',
  planPeriodTwelveMonths: '12 Monate',
  planPerMonth: 'entspricht {amount} pro Monat',
  noRooms: 'Keine Räume verfügbar',
  noSeasons: 'Keine aktiven Saisons',
  noTreatments: 'Keine Behandlungen verfügbar',
  noWaitlists: 'Zurzeit sind keine Wartelisten offen',
  noClassesScheduled: 'Zurzeit sind keine Kurse geplant',
  noClassesThisWeek: 'Keine Kurse in dieser Woche',
  noClassesDay: 'Keine Kurse',
  weekOverview: 'Wochenübersicht',
  weekAbbrev: 'Woche',
  previousWeek: 'Vorige Woche',
  nextWeek: 'Nächste Woche',
  thisWeek: 'Diese Woche',
  viewList: 'Kurse',
  viewCalendar: 'Kalender',
  filterAll: 'Alle',
  loadTreatmentsError: 'Behandlungen konnten nicht geladen werden',
  openingHours: 'Öffnungszeiten',
  confirmed: 'Bestätigt',
  paused: 'Pausiert',
  popular: 'Beliebt',
  comingSoon: 'Demnächst verfügbar',
  yearly: 'Jährlich',
  monthly: 'Monatlich',
  classesThisMonth: 'Kurse diesen Monat',
  previousPurchases: 'Frühere Käufe',
  showOnMap: 'Auf Karte zeigen',
  buyGiftCard: 'Gutschein kaufen',
  giftCardImageAlt: 'Gutschein',
  gcBuyTitle: 'Gutschein kaufen',
  gcBuyAmount: 'Betrag',
  gcBuyYourName: 'Ihr Name',
  gcBuyYourEmail: 'Ihre E-Mail',
  gcBuyRecipientEmail: 'E-Mail des Empfängers (optional)',
  gcBuyMessage: 'Persönliche Nachricht (optional)',
  gcBuySubmitting: 'Wird gekauft...',
  registrationOpen: 'Anmeldung offen',
  contactFormIntro: 'Schreib uns, wir melden uns schnellstmöglich zurück.',
  contactFormSuccess: 'Wir haben Ihre Nachricht erhalten und antworten so schnell wie möglich.',
  noClassesPlanned: 'Keine Kurse geplant',
  nextClassesStart: 'Die Kurse beginnen am {date}',
  booked: 'Gebucht',
  full: 'Ausgebucht',
  price: 'Preis',
  includes: 'Beinhaltet',
  waitlist: 'Warteliste',
  awaitingPayment: 'Zahlung ausstehend',
  payBy: 'Bezahlen bis {time}',
  payNow: 'Jetzt bezahlen',
  fullyBooked: 'Ausgebucht',
  loggedInAs: 'Angemeldet als',
  spot: 'Platz',
  spots: 'Plätze',
  spotAvailable: 'Platz frei',
  spotsAvailable: 'Plätze frei',
  fromPrice: 'ab',
  classSingular: 'Kurs',
  classPlural: 'Kurse',
  signUp: 'Anmelden',
  registrationClosed: 'Anmeldung geschlossen',
  klippekort: 'Mehrfachkarte',
  dropIn: 'Drop-in',
  discountCodeToggle: 'Haben Sie einen Rabattcode?',
  discountCodePlaceholder: 'Rabattcode',
  discountInvalid: 'Dieser Rabattcode ist ungültig.',
  discountExpired: 'Dieser Rabattcode ist abgelaufen.',
  discountUsedUp: 'Dieser Rabattcode ist aufgebraucht.',
  discountAlreadyUsed: 'Sie haben diesen Rabattcode bereits verwendet.',
  discountNotForClasses: 'Der Rabattcode gilt nicht für Kurse.',
  discountTooManyAttempts: 'Zu viele Versuche. Bitte versuchen Sie es gleich erneut.',
  discountApply: 'Anwenden',
  discountRemove: 'Entfernen',
  discountLine: 'Rabatt',
  priceAfterDiscount: 'Preis nach Rabatt',
  discountFirstPaymentOnly: 'Der Rabatt gilt für die erste Zahlung.',
  firstChargeLabel: 'Erste Abbuchung',
  signupFeeLineLabel: 'Aufnahmegebühr (neue Mitglieder):',
  firstPaymentTotalLabel: 'Gesamt bei der ersten Zahlung',
  firstChargeNote: 'Die erste Abbuchung deckt den Rest dieses Monats. Danach {price} am 1. jedes Monats.',
  deferredFirstChargeNote: 'Sie erhalten sofort Zugang und zahlen heute nichts. Die erste Zahlung von {price} wird am {date} abgebucht.',
  firstChargeNoteFolded: 'Die erste Abbuchung deckt den Rest dieses Monats und den nächsten vollen Monat. Danach {price} am 1. jedes Monats.',
  discountEveryPayment: 'Der Rabatt gilt für jede Zahlung.',
  discountFreeMonths: '{months} Monate gratis vor der ersten Zahlung.',
  subscriptionTitle: 'Abo',
  startedOn: 'Gestartet am',
  clipsRemaining: 'Einheiten übrig',
  ofTotal: 'von',
  expiresOn: 'Läuft ab am',
  active: 'Aktiv',
  activeUntil: 'Aktiv bis',
  autoRenews: 'Verlängert sich automatisch',
  noAutoRenew: 'Verlängert sich nicht automatisch',
  pauseOrCancel: 'Pausieren / kündigen',
  confirmUnsubscribe: 'Möchten Sie Ihre Anmeldung für "{name}" wirklich stornieren?',
  newsletterTitle: 'Newsletter',
  newsletterSubtitle: 'Melden Sie sich an und bleiben Sie auf dem Laufenden',
  newsletterSubmit: 'Anmelden',
  newsletterSubmitting: 'Wird angemeldet...',
  newsletterThanksTitle: 'Danke!',
  newsletterThanksBody: 'Sie haben unseren Newsletter abonniert.',
  emailPlaceholder: 'Ihre E-Mail-Adresse',
  welcomeName: 'Willkommen, {name}!',
  logout: 'Abmelden',
  forgotPassword: 'Passwort vergessen',
  forgotPasswordQ: 'Passwort vergessen?',
  forgotPasswordHint: 'Bitte kontaktieren Sie {tenant}, um Ihr Passwort zurückzusetzen.',
  forgotPasswordInstruction: 'Geben Sie Ihre E-Mail ein, und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.',
  forgotPasswordSubmit: 'Link zum Zurücksetzen senden',
  forgotPasswordSent: 'Falls ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet. Sehen Sie in Ihrem Postfach nach.',
  backToLogin: 'Zurück zum Login',
  passwordSetupTitle: 'Sehen Sie in Ihr Postfach',
  passwordSetupBody: 'Wir haben eine E-Mail an {email} gesendet. Klicken Sie auf den Link zur Bestätigung, dann wird Ihr Passwort aktiviert. Kommen Sie danach hierher zurück und melden Sie sich an.',
  passwordTooShort: 'Das Passwort muss mindestens 8 Zeichen lang sein.',
  createAccount: 'Konto erstellen',
  createAccountAt: 'Erstellen Sie ein Konto bei {tenant}.',
  loginAt: 'Melden Sie sich bei {tenant} an.',
  fieldFirstName: 'Vorname',
  fieldLastName: 'Nachname',
  fieldPhone: 'Telefon',
  optionalSuffix: '(optional)',
  fieldPassword: 'Passwort',
  fieldPasswordMin: 'Passwort (mind. 8 Zeichen)',
  creatingAccount: 'Konto wird erstellt...',
  loggingIn: 'Anmeldung läuft...',
  alreadyHaveAccount: 'Haben Sie schon ein Konto?',
  noAccountQ: 'Noch kein Konto?',
  createOne: 'Jetzt erstellen',
  contactTitle: 'Kontaktieren Sie uns',
  fillMarkedFields: 'Bitte füllen Sie die markierten Felder korrekt aus.',
  sending: 'Wird gesendet...',
  sendMessage: 'Nachricht senden',
  address: 'Adresse',
  noTestimonials: 'Noch keine Bewertungen',
  starsOutOf: '{n} von 5 Sternen',
  mapOf: 'Karte von {tenant}',
  atTime: 'um',
  unsubscribe: 'Abmelden',
  noUpcomingClasses: 'Keine bevorstehenden Kurse',
  temperatureWater: 'Wassertemperatur',
  temperatureAir: 'Lufttemperatur',
  temperatureUpdated: 'Aktualisiert',
  temperatureUnavailable: 'Die Temperatur ist derzeit nicht verfügbar',
  noUpcomingBookings: 'Sie haben keine bevorstehenden Buchungen',
  loginToSeeBookings: 'Melden Sie sich an, um Ihre Buchungen zu sehen',
  bookClass: 'Kurs buchen',
  seeClasses: 'Kurse ansehen',
  seePrices: 'Preise ansehen',
  topUp: 'Aufladen',
  manageMembership: 'Mitgliedschaft verwalten',
  loginToSeeAgreement: 'Melden Sie sich an, um Ihren Vertrag zu sehen',
  noActiveAgreement: 'Sie haben keinen aktiven Vertrag',
  subscription: 'Abo',
  soldOut: 'Ausverkauft',
  closed: 'Geschlossen',
  comingSoonShort: 'Bald',
  contactMe: 'Kontaktieren Sie mich',
  categoryOther: 'Sonstiges',
  plan: 'Tarif',
  fieldName: 'Name',
  fieldEmail: 'E-Mail',
  emailDidYouMean: 'Meinten Sie',
  fieldMessage: 'Nachricht',
  contactFormThanks: 'Danke! Wir melden uns schnellstmöglich',
  tba: 'Noch offen',
  loginOrSignUpToBook: 'Melden Sie sich an oder erstellen Sie ein Konto, um zu buchen',
  onWaitlist: 'auf der Warteliste',
  joinWaitlistCta: 'Auf die Warteliste',
  waitlistJoinedTitle: 'Sie stehen auf der Warteliste',
  yourPosition: 'Ihre Position',
  alreadyOnWaitlist: 'Sie stehen bereits auf dieser Warteliste',
  loginOrSignUpToJoin: 'Melden Sie sich an oder erstellen Sie ein Konto für die Warteliste von',
  upcomingSessions: 'Kommende Termine',
  klinikPickerAllServices: 'Alle Leistungen',
  klinikNoSlots: 'Zurzeit keine freien Termine',
  klinikShowMore: 'Weitere Tage laden',
  klinikBookAt: 'Bei {name} buchen',
  pricePerPerson: '{price} pro Person',
  noUpcoming: 'Keine bevorstehenden Veranstaltungen',
  viewRetreat: 'Retreat ansehen',
  viewEvent: 'Event ansehen',
  showMore: 'Mehr anzeigen',
  fewSpotsLeft: 'Wenige Plätze frei',
  retreatAbout: 'Über dieses Retreat',
  retreatInstructors: 'Leitung',
  retreatLocation: 'Ort',
  retreatFaq: 'Häufig gestellte Fragen',
  retreatChooseTier: 'Paket wählen',
  retreatNotPublished: 'Dieses Retreat ist noch nicht öffentlich',
  eventAbout: 'Über dieses Event',
  eventChooseTier: 'Ticket wählen',
  eventNotPublished: 'Dieses Event ist noch nicht öffentlich',
  retreatOrganizer: 'Veranstalter',
  retreatPackageInfo: 'Dies ist eine Pauschalreise, veranstaltet von {organizer}, registriert beim dänischen Reisegarantiefonds (Reg.-Nr. {regNo}). Der Vertrag kommt mit {organizer} zustande.',
  retreatBooking: {
    modalAriaLabel: 'Buchungsdialog',
    nav: {
      cancel: 'Abbrechen',
      back: 'Zurück',
      next: 'Weiter',
      toPayment: 'Zur Zahlung',
      confirmFree: 'Anmeldung bestätigen',
      submitting: 'Wird gesendet…',
      stepProgress: 'Schritt {current} von {total}',
    },
    steps: {
      indicatorAriaLabel: 'Buchungsschritte',
      who: {
        title: 'Wer bucht?',
        description: 'Wählen Sie, ob Sie für sich selbst oder für zwei Personen buchen.',
        shortLabel: 'Wer',
        countLegend: 'Anzahl Personen',
        soloOption: 'Für mich selbst',
        soloHelp: '1 Person',
        pairOption: 'Für zwei',
        pairHelp: '2 Personen auf derselben Buchung',
        privateDoubleSoloTitle: 'Sie buchen das gesamte Doppelzimmer',
        privateDoubleSoloBody: 'Sie haben das Zimmer für sich allein. Der Preis gilt für das gesamte Zimmer.',
        roommateLegend: 'Zimmerpartner',
        sharedDoubleSoloHelp: 'Sie teilen das Doppelzimmer mit einer anderen Einzelperson. Wir übernehmen die Zuteilung vor Beginn des Retreats.',
        roommate: {
          matchMe: 'Zimmerpartner zuweisen lassen',
          matchMeHelp: 'Wir finden vor Beginn des Retreats einen passenden Zimmerpartner für Sie',
          alone: 'Ich möchte lieber allein sein',
          aloneHelp: 'Wir versuchen, Ihnen das Zimmer allein zu geben — dabei kann ein Einzelzimmerzuschlag anfallen',
          knownPartner: 'Ich kenne meinen Zimmerpartner',
          knownPartnerHelp: 'Geben Sie an, mit wem Sie das Zimmer teilen möchten',
          knownPartnerNameLabel: 'Name des Zimmerpartners',
          knownPartnerNamePlaceholder: 'z. B. Maja Hansen',
        },
      },
      info: {
        title: 'Teilnehmerdaten',
        description: 'Geben Sie Name und E-Mail für jeden Teilnehmer an.',
        shortLabel: 'Daten',
        participantHeading: 'Teilnehmer {index}',
        participantHeadingSingle: 'Ihre Angaben',
        firstNameLabel: 'Vorname',
        lastNameLabel: 'Nachname',
        emailLabel: 'E-Mail',
        phoneLabel: 'Telefon',
        dietaryLabel: 'Ernährungshinweise',
        dietaryPlaceholder: 'Allergien, vegetarisch, vegan usw.',
        dietaryHelp: 'Max. 500 Zeichen',
        errors: {
          firstNameRequired: 'Vorname ist erforderlich',
          lastNameRequired: 'Nachname ist erforderlich',
          invalidEmail: 'Ungültige E-Mail',
          invalidPhone: 'Ungültige Telefonnummer',
          dietaryTooLong: 'Max. 500 Zeichen',
        },
      },
      questions: {
        title: 'Anmeldefragen',
        description: 'Helfen Sie uns, Ihren Aufenthalt zu planen.',
        descriptionEvent: 'Helfen Sie uns, das Event zu planen.',
        shortLabel: 'Fragen',
        noQuestions: 'Keine weiteren Fragen.',
        selectPlaceholder: '— Bitte wählen —',
        errors: { required: 'Erforderlich' },
      },
      payment: {
        title: 'Zahlung',
        description: 'Wählen Sie eine Zahlungsmethode.',
        shortLabel: 'Zahlung',
        breakdownLine: '{count} × {tierName} à {perPerson}',
        vatLine: 'MwSt. ({rate}%)',
        totalLabel: 'Gesamt',
        chooseModeLegend: 'Zahlungsmodell',
        fullPayment: 'Jetzt bezahlen',
        fullPaymentHelp: 'Den gesamten Betrag auf einmal bezahlen',
        depositPayment: 'Anzahlung',
        depositPaymentHelp: 'Jetzt {percent}% bezahlen, den Rest später',
        depositPaymentHelpWithDate: 'Jetzt {percent}% bezahlen, den Rest bis spätestens {dueDate}',
        payNowShort: 'Jetzt fällig: {amount}',
        installmentsPayment: 'Ratenzahlung',
        installmentsPaymentHelp: '{count} monatliche Raten',
        perMonthShort: '{amount} pro Monat',
        discountSectionTitle: 'Rabatt',
        giftCardLabel: 'Geschenkgutschein',
        giftCardPlaceholder: 'Geschenkgutschein-Code eingeben',
        giftCardHelp: 'Wird vom Gesamtbetrag abgezogen',
        couponLabel: 'Rabattcode',
        couponPlaceholder: 'Rabattcode eingeben',
        couponHelp: 'Wird bei der Bestätigung eingelöst',
        submitting: 'Wird gesendet…',
      },
    },
    priceSummary: {
      lineLabel: '{count} × {tierName}',
      vatLabel: 'MwSt. ({rate}%)',
      discountLabel: 'Rabatt',
      totalLabel: 'Gesamt',
      payNowLabel: 'Jetzt fällig',
      dueLaterLabel: 'Später',
      depositNote: 'Anzahlung: {percent}% jetzt, der Rest später',
      installmentsNote: 'Ratenzahlung: {count} monatliche Raten',
    },
    errors: {
      capacityTaken: 'Der Platz wurde leider gebucht, während Sie das Formular ausgefüllt haben. Wählen Sie einen anderen Zimmertyp oder tragen Sie sich auf die Warteliste ein.',
      paymentNotConfigured: "Die Online-Zahlung für dieses Angebot ist noch nicht freigeschaltet. Bitte wenden Sie sich zur Anmeldung an das Studio.",
      paymentProviderError: "Die Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es gleich erneut oder wenden Sie sich an das Studio.",
      paymentModeUnavailable: "Die Zahlung ist für dieses Angebot noch nicht eingerichtet. Bitte wenden Sie sich an das Studio.",
      notAvailable: "Das kann gerade nicht gebucht werden. Bitte wenden Sie sich an das Studio.",
      validation: 'Bitte prüfen Sie, ob alle Felder korrekt ausgefüllt sind.',
      generic: 'Etwas ist schiefgelaufen — bitte versuchen Sie es gleich erneut.',
      network: 'Netzwerkfehler — prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
      coupon: {
        notAllowed: 'Rabattcodes können für dieses Retreat nicht verwendet werden.',
        unknown: 'Diesen Rabattcode gibt es nicht. Bitte prüfen Sie die Schreibweise.',
        notStarted: 'Dieser Rabattcode ist noch nicht aktiv.',
        expired: 'Dieser Rabattcode ist abgelaufen.',
        usedUp: 'Dieser Rabattcode ist bereits vollständig eingelöst.',
        alreadyUsed: 'Sie haben diesen Rabattcode bereits verwendet.',
        newCustomersOnly: 'Dieser Rabattcode gilt nur für Neukunden.',
        requiresMembership: 'Dieser Rabattcode setzt eine aktive Mitgliedschaft voraus.',
        requiresSpecificMembership: 'Dieser Rabattcode gilt nur für Kunden mit einer bestimmten Mitgliedschaft oder einem bestimmten Kurs. Wenn Sie Mitglied sind, melden Sie sich an oder verwenden Sie die E-Mail-Adresse, mit der Sie registriert sind.',
        wrongProduct: 'Dieser Rabattcode gilt nicht für Retreats.',
        otherRetreat: 'Dieser Rabattcode gilt nicht für dieses Retreat.',
        notAllowedEvent: 'Rabattcodes können für dieses Event nicht verwendet werden.',
        wrongProductEvent: 'Dieser Rabattcode gilt nicht für Events.',
        otherRetreatEvent: 'Dieser Rabattcode gilt nicht für dieses Event.',
        belowMinimum: 'Dieser Rabattcode erfordert einen Einkauf von mindestens {minimum}.',
      },
    },
    packageTravel: {
      heading: 'Dies ist eine Pauschalreise',
      organizerNote: 'Sie schließen den Vertrag mit {organizer} – nicht mit Class Booking. {organizer} ist der Reiseveranstalter und für die Pauschalreise verantwortlich.',
      registeredLine: '{organizer} ist beim dänischen Reisegarantiefonds (Rejsegarantifonden) registriert, Mitgliedsnr. {memberNumber}.',
      formToggle: 'Ihre Rechte als Reisende(r)',
      formIntro: 'Die Ihnen angebotene Kombination von Reiseleistungen ist eine Pauschalreise im Sinne des dänischen Pauschalreisegesetzes. Ihnen stehen daher alle Rechte zu, die für Pauschalreisen gelten.',
      formResponsibility: '{organizer} ist für die ordnungsgemäße Durchführung der gesamten Pauschalreise verantwortlich und verfügt über den gesetzlichen Insolvenzschutz beim Rejsegarantifonden (Mitgliedsnr. {memberNumber}).',
      rightsTitle: 'Wesentliche Rechte',
      right1: 'Sie erhalten alle wesentlichen Informationen über die Pauschalreise, bevor Sie den Vertrag schließen.',
      right2: 'Mindestens ein Unternehmer ist für die ordnungsgemäße Erbringung aller Reiseleistungen verantwortlich.',
      right3: 'Steigt der Preis um mehr als 8 %, können Sie vom Vertrag zurücktreten.',
      right4: 'Sie können kostenfrei zurücktreten und erhalten eine volle Erstattung, wenn sich wesentliche Bestandteile erheblich ändern oder bei unvermeidbaren, außergewöhnlichen Umständen.',
      right5: 'Erstattungen erfolgen unverzüglich, spätestens innerhalb von 14 Tagen.',
      right6: 'Wird der Reiseveranstalter insolvent, werden Ihre Zahlungen über den Rejsegarantifonden erstattet.',
      contact: 'Insolvenzschutz: Rejsegarantifonden, Røjelskær 11, 2840 Holte, Dänemark – info@rejsegarantifonden.dk, +45 45 87 83 33.',
      consentLabel: 'Ich habe die Informationen zur Pauschalreise gelesen und akzeptiere die Bedingungen.',
      logoAlt: 'Rejsegarantifonden',
    },
  },
}

const sv: WidgetStrings = {
  checkoutLegal: {
    summaryTitle: 'Ordersammanfattning',
    seller: 'Säljare: {name}',
    companyId: 'Org.nr {cvr}',
    platformNote: 'Holdstyring tillhandahåller endast den tekniska lösningen och är inte part i köpet.',
    total: 'Totalt inkl. moms',
    totalBeforeDiscount: 'Totalt inkl. moms före rabattkod',
    recurringNote: 'Abonnemang som förnyas automatiskt tills du säger upp det.',
    perFourWeeks: 'per 4 veckor',
    perMonth: 'per månad',
    perQuarter: 'per kvartal',
    perHalfYear: 'per halvår',
    perYear: 'per år',
    oneTimeNote: 'Engångsköp.',
    acceptTerms: 'Jag godkänner köpvillkoren och har läst informationen om ångerrätt.',
    acceptTermsEarlyStart: 'Jag godkänner köpvillkoren och ångerrätten, och att tjänsten börjar direkt.',
    termsLink: 'Köpvillkor',
    withdrawalLink: 'Ångerrätt',
    privacyLink: 'Integritetspolicy',
    withdrawContractLink: 'Ångra avtalet',
    noWithdrawal: 'Det finns ingen ångerrätt vid bokning av ett pass på ett bestämt datum.',
    paidOrderNote: 'När du klickar på knappen gör du en beställning med betalningsskyldighet.',
    payGeneric: 'Köp och betala',
    payAmount: 'Köp och betala ({amount})',
    payCard: 'Köp och betala med kort',
    payMobilePay: 'Köp och betala med MobilePay',
  },
  spotsOpenLabel: 'Lediga platser',
  spotsFewLabel: 'Få platser kvar',
  loadError: 'Kunde inte laddas',
  close: 'Stäng',
  cancel: 'Avbryt',
  login: 'Logga in',
  readMore: 'Läs mer',
  readLess: 'Visa mindre',
  book: 'Boka',
  buy: 'Köp',
  buyLoginPrompt: 'Logga in eller skapa ett konto för att köpa',
  purchasing: 'Köper...',
  continueToPurchase: 'Fortsätt till betalning',
  purchaseHandoffBody: 'Slutför ditt köp på vår webbplats.',
  alreadyBookedTitle: 'Du är anmäld',
  alreadyBookedBody: 'Du har redan bokat den här klassen.',
  waitlistTitle: 'På väntelistan',
  waitlistBody: 'Vi hör av oss om en plats blir ledig.',
  confirmedTitle: 'Bokning bekräftad',
  confirmedBody: 'Du får en bekräftelse via e-post.',
  loginToBook: 'Logga in för att boka den här klassen.',
  fullTitle: 'Klassen är full',
  fullBody: 'Det finns ingen väntelista för den här klassen.',
  membershipRequiredBody: 'Den här klassen kräver ett aktivt medlemskap. Fortsätt på vår webbplats för att välja medlemskap och boka.',
  paymentRequiredBody: 'Den här klassen måste betalas. Fortsätt på vår webbplats för att betala och boka.',
  classPlansHeading: 'Eller köp ett klippkort',
  classPlansHeadingOnly: 'Köp ett klippkort till den här klassen',
  classPlansClips: '{count, plural, one {# klipp} other {# klipp}}',
  classPlansValidDays: '{count, plural, one {giltigt # dag} other {giltigt # dagar}}',
  classPlansBuy: 'Köp',
  classPlansViewSubscription: 'Visa abonnemang',
  classPlansSubscription: 'Abonnemang',
  classPlansAfterNote: 'Efter betalningen kommer du tillbaka hit. Öppna klassen igen och tryck på Boka, så används ditt nya klippkort.',
  classPlansPaySingle: 'Betala enkelbiljett',
  waiverRequiredBody: 'Den här klassen kräver att du godkänner studions ansvarsfriskrivning. Fortsätt på vår webbplats för att läsa och godkänna den.',
  intakeRequiredBody: 'Den här klassen ställer några frågor första gången. Fortsätt på vår webbplats för att svara — sedan kommer vi ihåg dem.',
  participantNamesBody: 'Den här klassen kräver deltagarnas namn. Fortsätt på vår webbplats för att boka.',
  participantProfileBody: 'Hos den här studion bokas varje plats för en deltagarprofil. Välj vem som ska delta på vår webbplats.',
  waitlistClipCardBody: 'Den här klassen är full och ditt klippkort täcker den. Fortsätt på vår webbplats för att ställa dig i väntelistan, så att ditt klipp reserveras.',
  bookWithGuests: 'Boka med gäster',
  fullVenueButton: 'Boka hela klassen',
  continueToBooking: 'Fortsätt till bokning',
  dropInPrice: 'Drop-in-pris',
  clipCostShort: '{count, plural, one {# klipp} other {# klipp}}',
  priceFromPrefix: 'Från',
  vatFreePrice: 'Momsfritt pris',
  ownCancellationHeading: 'Särskild avbokningsfrist:',
  ownCancellationDeadline: 'avbokning senast {count} timmar före start.',
  ownCancellationDeadlineOne: 'avbokning senast {count} timme före start.',
  ownCancellationNoRefund: 'ingen återbetalning vid avbokning.',
  membershipOnly: 'Medlemskap',
  includedInSubscription: 'Ingår i ditt abonnemang',
  freeClass: 'Gratis',
  booking: 'Bokar...',
  joinWaitlist: 'Ställ dig i kö',
  confirmBooking: 'Bekräfta bokning',
  guests: 'Gäster',
  perGuest: 'per gäst',
  memberSpotCovered: 'Din plats ingår i ditt medlemskap',
  coveredByClipCard: 'Ingår i ditt klippkort',
  useClipCard: 'Använd klippkort',
  coveredBySubscription: 'Ingår i din prenumeration',
  confirmAndPay: 'Bekräfta och betala',
  paymentMethod: 'Betalningsmetod',
  paymentMethodCard: 'Kort',
  addGuests: 'Lägg till gäster',
  guestOption: 'Fortsätt som gäst',
  guestConsent: 'Jag godkänner köpvillkoren och har läst integritetspolicyn.',
  guestConsentRequired: 'Du måste godkänna villkoren för att fortsätta.',
  guestSubmit: 'Fortsätt till betalning',
  guestSubmitting: 'Fortsätter…',
  guestError: 'Kunde inte fortsätta som gäst. Försök igen eller logga in.',
  guestAccountExists: 'Det finns redan ett konto med den här e-postadressen. Logga in i stället.',
  guestOrLoginPrompt: 'Du kan köpa din plats direkt, eller logga in om du redan är medlem.',
  guestIntro: 'Vi behöver bara dina kontaktuppgifter så att vi kan skicka en bekräftelse. Du kan skapa ett lösenord efteråt om du vill.',
  guestTermsLink: 'Köpvillkor',
  guestPrivacyLink: 'Integritetspolicy',
  guestCoverageHint: 'Har du klippkort eller abonnemang? Logga in i stället, så använder vi det.',
  guestLinkSent: 'Du har bokat hos oss tidigare med den här e-postadressen. Vi har skickat en länk så att du kan välja ett lösenord och fortsätta.',
  noBlogPosts: 'Inga blogginlägg än',
  noClassTypes: 'Inga klasstyper tillgängliga',
  noContactInfo: 'Inga kontaktuppgifter tillgängliga',
  noInstructors: 'Inga instruktörer tillgängliga',
  noPricingPlans: 'Inga prisplaner tillgängliga',
  planPeriodPicker: 'Välj period',
  planPeriodFourWeeks: '4 veckor',
  planPeriodOneMonth: '1 månad',
  planPeriodThreeMonths: '3 månader',
  planPeriodSixMonths: '6 månader',
  planPeriodTwelveMonths: '12 månader',
  planPerMonth: 'motsvarar {amount} per månad',
  noRooms: 'Inga lokaler tillgängliga',
  noSeasons: 'Inga aktiva säsonger',
  noTreatments: 'Inga behandlingar tillgängliga',
  noWaitlists: 'Inga väntelistor öppna för anmälan just nu',
  noClassesScheduled: 'Inga klasser inplanerade just nu',
  noClassesThisWeek: 'Inga klasser denna vecka',
  noClassesDay: 'Inga pass',
  weekOverview: 'Veckoöversikt',
  weekAbbrev: 'Vecka',
  previousWeek: 'Föregående vecka',
  nextWeek: 'Nästa vecka',
  thisWeek: 'Denna vecka',
  viewList: 'Klasser',
  viewCalendar: 'Kalender',
  filterAll: 'Alla',
  loadTreatmentsError: 'Kunde inte ladda behandlingar',
  openingHours: 'Öppettider',
  confirmed: 'Bekräftad',
  paused: 'Pausad',
  popular: 'Populär',
  comingSoon: 'Snart tillgänglig',
  yearly: 'Årligen',
  monthly: 'Månadsvis',
  classesThisMonth: 'klasser denna månad',
  previousPurchases: 'Tidigare köp',
  showOnMap: 'Visa på karta',
  buyGiftCard: 'Köp presentkort',
  giftCardImageAlt: 'Presentkort',
  gcBuyTitle: 'Köp presentkort',
  gcBuyAmount: 'Belopp',
  gcBuyYourName: 'Ditt namn',
  gcBuyYourEmail: 'Din e-post',
  gcBuyRecipientEmail: 'Mottagarens e-post (valfritt)',
  gcBuyMessage: 'Personligt meddelande (valfritt)',
  gcBuySubmitting: 'Köper...',
  registrationOpen: 'Anmälan öppen',
  contactFormIntro: 'Skriv till oss, så återkommer vi så snart vi kan.',
  contactFormSuccess: 'Vi har tagit emot ditt meddelande och svarar så snart vi kan.',
  noClassesPlanned: 'Inga klasser inplanerade',
  nextClassesStart: 'Klasserna börjar {date}',
  booked: 'Bokad',
  full: 'Fullbokad',
  price: 'Pris',
  includes: 'Inkluderar',
  waitlist: 'Väntelista',
  awaitingPayment: 'Väntar på betalning',
  payBy: 'Betala senast {time}',
  payNow: 'Betala nu',
  fullyBooked: 'Fullbokad',
  loggedInAs: 'Inloggad som',
  spot: 'plats',
  spots: 'platser',
  spotAvailable: 'plats ledig',
  spotsAvailable: 'platser lediga',
  fromPrice: 'från',
  classSingular: 'klass',
  classPlural: 'klasser',
  signUp: 'Anmäl dig',
  registrationClosed: 'Anmälan stängd',
  klippekort: 'Klippkort',
  dropIn: 'Drop-in',
  discountCodeToggle: 'Har du en rabattkod?',
  discountCodePlaceholder: 'Rabattkod',
  discountInvalid: 'Rabattkoden är inte giltig.',
  discountExpired: 'Rabattkoden har gått ut.',
  discountUsedUp: 'Rabattkoden är förbrukad.',
  discountAlreadyUsed: 'Du har redan använt den här rabattkoden.',
  discountNotForClasses: 'Rabattkoden gäller inte för klasser.',
  discountTooManyAttempts: 'För många försök. Försök igen om en stund.',
  discountApply: 'Använd',
  discountRemove: 'Ta bort',
  discountLine: 'Rabatt',
  priceAfterDiscount: 'Pris efter rabatt',
  discountFirstPaymentOnly: 'Rabatten gäller den första betalningen.',
  firstChargeLabel: 'Första dragningen',
  signupFeeLineLabel: 'Anmälningsavgift (nya medlemmar):',
  firstPaymentTotalLabel: 'Totalt vid första betalningen',
  firstChargeNote: 'Första dragningen täcker resten av denna månad. Därefter {price} den 1:a varje månad.',
  deferredFirstChargeNote: 'Du får tillgång direkt och betalar inget i dag. Den första betalningen på {price} dras {date}.',
  firstChargeNoteFolded: 'Första dragningen täcker resten av denna månad och nästa hela månad. Därefter {price} den 1:a varje månad.',
  discountEveryPayment: 'Rabatten gäller varje betalning.',
  discountFreeMonths: '{months} månader gratis före den första betalningen.',
  subscriptionTitle: 'Prenumeration',
  startedOn: 'Startade',
  clipsRemaining: 'klipp kvar',
  ofTotal: 'av',
  expiresOn: 'Går ut',
  active: 'Aktiv',
  activeUntil: 'Aktiv till',
  autoRenews: 'Förnyas automatiskt',
  noAutoRenew: 'Förnyas inte automatiskt',
  pauseOrCancel: 'Pausa / säg upp',
  confirmUnsubscribe: 'Är du säker på att du vill avboka "{name}"?',
  newsletterTitle: 'Nyhetsbrev',
  newsletterSubtitle: 'Anmäl dig och håll dig uppdaterad',
  newsletterSubmit: 'Anmäl dig',
  newsletterSubmitting: 'Anmäler...',
  newsletterThanksTitle: 'Tack!',
  newsletterThanksBody: 'Du prenumererar nu på vårt nyhetsbrev.',
  emailPlaceholder: 'Din e-postadress',
  welcomeName: 'Välkommen, {name}!',
  logout: 'Logga ut',
  forgotPassword: 'Glömt lösenord',
  forgotPasswordQ: 'Glömt lösenordet?',
  forgotPasswordHint: 'Kontakta {tenant} för att återställa ditt lösenord.',
  forgotPasswordInstruction: 'Ange din e-post så skickar vi en länk för att återställa ditt lösenord.',
  forgotPasswordSubmit: 'Skicka återställningslänk',
  forgotPasswordSent: 'Om det finns ett konto med den e-posten har vi skickat en länk för att återställa lösenordet. Kolla din inkorg.',
  backToLogin: 'Tillbaka till inloggning',
  passwordSetupTitle: 'Kolla din e-post',
  passwordSetupBody: 'Vi har skickat ett mejl till {email}. Klicka på länken för att bekräfta, så aktiveras lösenordet. Kom sedan tillbaka och logga in här.',
  passwordTooShort: 'Lösenordet måste vara minst 8 tecken.',
  createAccount: 'Skapa konto',
  createAccountAt: 'Skapa ett konto hos {tenant}.',
  loginAt: 'Logga in hos {tenant}.',
  fieldFirstName: 'Förnamn',
  fieldLastName: 'Efternamn',
  fieldPhone: 'Telefon',
  optionalSuffix: '(valfritt)',
  fieldPassword: 'Lösenord',
  fieldPasswordMin: 'Lösenord (minst 8 tecken)',
  creatingAccount: 'Skapar konto...',
  loggingIn: 'Loggar in...',
  alreadyHaveAccount: 'Har du redan ett konto?',
  noAccountQ: 'Har du inget konto?',
  createOne: 'Skapa ett',
  contactTitle: 'Kontakta oss',
  fillMarkedFields: 'Fyll i de markerade fälten korrekt.',
  sending: 'Skickar...',
  sendMessage: 'Skicka meddelande',
  address: 'Adress',
  noTestimonials: 'Inga recensioner ännu',
  starsOutOf: '{n} av 5 stjärnor',
  mapOf: 'Karta över {tenant}',
  atTime: 'kl.',
  unsubscribe: 'Avboka',
  noUpcomingClasses: 'Inga kommande klasser',
  temperatureWater: 'Vattentemperatur',
  temperatureAir: 'Lufttemperatur',
  temperatureUpdated: 'Uppdaterad',
  temperatureUnavailable: 'Temperaturen är inte tillgänglig just nu',
  noUpcomingBookings: 'Du har inga kommande bokningar',
  loginToSeeBookings: 'Logga in för att se dina bokningar',
  bookClass: 'Boka klass',
  seeClasses: 'Se klasser',
  seePrices: 'Se priser',
  topUp: 'Fyll på',
  manageMembership: 'Hantera medlemskap',
  loginToSeeAgreement: 'Logga in för att se ditt avtal',
  noActiveAgreement: 'Du har inget aktivt avtal',
  subscription: 'abonnemang',
  soldOut: 'Slutsålt',
  closed: 'Stängd',
  comingSoonShort: 'Snart',
  contactMe: 'Kontakta mig',
  categoryOther: 'Övrigt',
  plan: 'Plan',
  fieldName: 'Namn',
  fieldEmail: 'E-post',
  emailDidYouMean: 'Menade du',
  fieldMessage: 'Meddelande',
  contactFormThanks: 'Tack! Vi återkommer så snart vi kan',
  tba: 'Ej angivet',
  loginOrSignUpToBook: 'Logga in eller skapa ett konto för att boka',
  onWaitlist: 'i väntelistan',
  joinWaitlistCta: 'Ställ dig i väntelistan',
  waitlistJoinedTitle: 'Du står i väntelistan',
  yourPosition: 'Din plats',
  alreadyOnWaitlist: 'Du står redan i denna väntelista',
  loginOrSignUpToJoin: 'Logga in eller skapa ett konto för att ställa dig i väntelistan till',
  upcomingSessions: 'Kommande pass',
  klinikPickerAllServices: 'Alla tjänster',
  klinikNoSlots: 'Inga lediga tider just nu',
  klinikShowMore: 'Ladda fler dagar',
  klinikBookAt: 'Boka hos {name}',
  pricePerPerson: '{price} per person',
  noUpcoming: 'Inga kommande evenemang',
  viewRetreat: 'Se retreat',
  viewEvent: 'Visa evenemang',
  showMore: 'Visa fler',
  fewSpotsLeft: 'Få platser kvar',
  retreatAbout: 'Om retreatet',
  retreatInstructors: 'Ledare',
  retreatLocation: 'Plats',
  retreatFaq: 'Vanliga frågor',
  retreatChooseTier: 'Välj paket',
  retreatNotPublished: 'Detta retreat är inte offentligt än',
  eventAbout: 'Om evenemanget',
  eventChooseTier: 'Välj biljett',
  eventNotPublished: 'Det här evenemanget är inte offentligt än',
  retreatOrganizer: 'Arrangör',
  retreatPackageInfo: 'Detta är en paketresa arrangerad av {organizer}, registrerad hos danska Rejsegarantifonden (reg.nr {regNo}). Avtalet ingås med {organizer}.',
  retreatBooking: {
    modalAriaLabel: 'Bokningsdialog',
    nav: {
      cancel: 'Avbryt',
      back: 'Tillbaka',
      next: 'Nästa',
      toPayment: 'Till betalning',
      confirmFree: 'Bekräfta anmälan',
      submitting: 'Skickar…',
      stepProgress: 'Steg {current} av {total}',
    },
    steps: {
      indicatorAriaLabel: 'Bokningssteg',
      who: {
        title: 'Vem bokar?',
        description: 'Välj om du bokar för dig själv eller för två.',
        shortLabel: 'Vem',
        countLegend: 'Antal personer',
        soloOption: 'Mig själv',
        soloHelp: '1 person',
        pairOption: 'För två',
        pairHelp: '2 personer på samma bokning',
        privateDoubleSoloTitle: 'Du bokar hela dubbelrummet',
        privateDoubleSoloBody: 'Du har rummet för dig själv. Priset gäller hela rummet.',
        roommateLegend: 'Rumskamrat',
        sharedDoubleSoloHelp: 'Du delar dubbelrummet med en annan solobokare. Vi matchar er före retreatstart.',
        roommate: {
          matchMe: 'Matcha mig',
          matchMeHelp: 'Vi hittar en passande rumskamrat före retreatstart',
          alone: 'Jag vill helst bo själv',
          aloneHelp: 'Vi försöker ge dig rummet för dig själv — kan medföra enkelrumstillägg',
          knownPartner: 'Jag vet vem jag vill dela med',
          knownPartnerHelp: 'Ange vem du vill dela rum med',
          knownPartnerNameLabel: 'Rumskamratens namn',
          knownPartnerNamePlaceholder: 'T.ex. Maja Hansson',
        },
      },
      info: {
        title: 'Deltagarinfo',
        description: 'Fyll i namn och e-post för varje deltagare.',
        shortLabel: 'Info',
        participantHeading: 'Deltagare {index}',
        participantHeadingSingle: 'Dina uppgifter',
        firstNameLabel: 'Förnamn',
        lastNameLabel: 'Efternamn',
        emailLabel: 'E-post',
        phoneLabel: 'Telefon',
        dietaryLabel: 'Kostönskemål',
        dietaryPlaceholder: 'Allergier, vegetarian, vegan osv.',
        dietaryHelp: 'Max 500 tecken',
        errors: {
          firstNameRequired: 'Förnamn krävs',
          lastNameRequired: 'Efternamn krävs',
          invalidEmail: 'Ogiltig e-postadress',
          invalidPhone: 'Ogiltigt telefonnummer',
          dietaryTooLong: 'Max 500 tecken',
        },
      },
      questions: {
        title: 'Anmälningsfrågor',
        description: 'Hjälp oss att planera din vistelse.',
        descriptionEvent: 'Hjälp oss att planera evenemanget.',
        shortLabel: 'Frågor',
        noQuestions: 'Inga ytterligare frågor.',
        selectPlaceholder: '— Välj —',
        errors: { required: 'Obligatoriskt' },
      },
      payment: {
        title: 'Betalning',
        description: 'Välj betalningsmetod.',
        shortLabel: 'Betalning',
        breakdownLine: '{count} × {tierName} à {perPerson}',
        vatLine: 'Moms ({rate}%)',
        totalLabel: 'Totalt',
        chooseModeLegend: 'Betalningsmodell',
        fullPayment: 'Betala nu',
        fullPaymentHelp: 'Betala hela beloppet på en gång',
        depositPayment: 'Handpenning',
        depositPaymentHelp: 'Betala {percent}% nu, resten senare',
        depositPaymentHelpWithDate: 'Betala {percent}% nu, resten senast {dueDate}',
        payNowShort: 'Betala nu: {amount}',
        installmentsPayment: 'Delbetalning',
        installmentsPaymentHelp: '{count} månadsvisa delbetalningar',
        perMonthShort: '{amount} per månad',
        discountSectionTitle: 'Rabatt',
        giftCardLabel: 'Presentkort',
        giftCardPlaceholder: 'Ange presentkortskod',
        giftCardHelp: 'Dras från totalbeloppet',
        couponLabel: 'Rabattkod',
        couponPlaceholder: 'Ange rabattkod',
        couponHelp: 'Aktiveras vid bekräftelse',
        submitting: 'Skickar…',
      },
    },
    priceSummary: {
      lineLabel: '{count} × {tierName}',
      vatLabel: 'Moms ({rate}%)',
      discountLabel: 'Rabatt',
      totalLabel: 'Totalt',
      payNowLabel: 'Betala nu',
      dueLaterLabel: 'Senare',
      depositNote: 'Handpenning: {percent}% nu, resten senare',
      installmentsNote: 'Delbetalning: {count} månatliga betalningar',
    },
    errors: {
      capacityTaken: 'Platsen hann tyvärr bokas medan du fyllde i formuläret. Välj en annan typ eller ställ dig på väntelistan.',
      paymentNotConfigured: "Onlinebetalning är inte öppen ännu för det här erbjudandet. Kontakta studion för att anmäla dig.",
      paymentProviderError: "Betalningen kunde inte startas. Försök igen om en stund eller kontakta studion.",
      paymentModeUnavailable: "Betalningen är inte inställd för det här erbjudandet ännu. Kontakta studion.",
      notAvailable: "Det går inte att boka just nu. Kontakta studion.",
      validation: 'Kontrollera att alla fält är korrekt ifyllda.',
      generic: 'Något gick fel — försök igen om en stund.',
      network: 'Nätverksfel — kontrollera din anslutning och försök igen.',
      coupon: {
        notAllowed: 'Rabattkoder kan inte användas för denna retreat.',
        unknown: 'Rabattkoden finns inte. Kontrollera stavningen.',
        notStarted: 'Rabattkoden är inte aktiv ännu.',
        expired: 'Rabattkoden har gått ut.',
        usedUp: 'Rabattkoden är slut.',
        alreadyUsed: 'Du har redan använt denna rabattkod.',
        newCustomersOnly: 'Rabattkoden gäller endast nya kunder.',
        requiresMembership: 'Rabattkoden kräver ett aktivt medlemskap.',
        requiresSpecificMembership: 'Rabattkoden gäller bara för kunder med ett visst medlemskap eller en viss kurs. Är du medlem, logga in eller använd e-postadressen du är registrerad med.',
        wrongProduct: 'Rabattkoden gäller inte retreater.',
        otherRetreat: 'Rabattkoden gäller inte denna retreat.',
        notAllowedEvent: 'Rabattkoder kan inte användas för detta evenemang.',
        wrongProductEvent: 'Rabattkoden gäller inte evenemang.',
        otherRetreatEvent: 'Rabattkoden gäller inte detta evenemang.',
        belowMinimum: 'Rabattkoden kräver ett köp på minst {minimum}.',
      },
    },
    packageTravel: {
      heading: 'Detta är en paketresa',
      organizerNote: 'Du ingår avtal med {organizer} – inte med Class Booking. {organizer} är arrangör och ansvarig för paketresan.',
      registeredLine: '{organizer} är registrerad hos Rejsegarantifonden, medlemsnr. {memberNumber}.',
      formToggle: 'Läs om dina rättigheter som resenär',
      formIntro: 'Den kombination av resetjänster som erbjuds dig är en paketresa enligt den danska paketreselagen. Du omfattas därför av alla rättigheter som gäller för paketresor.',
      formResponsibility: '{organizer} ansvarar fullt ut för att hela paketresan genomförs korrekt och har det lagstadgade insolvensskyddet hos Rejsegarantifonden (medlemsnr. {memberNumber}).',
      rightsTitle: 'Centrala rättigheter',
      right1: 'Du får all väsentlig information om paketresan innan du ingår avtalet.',
      right2: 'Minst en näringsidkare ansvarar för korrekt leverans av alla resetjänster i avtalet.',
      right3: 'Om priset höjs med mer än 8 % kan du säga upp avtalet.',
      right4: 'Du kan säga upp utan avgift och få full återbetalning om väsentliga delar ändras väsentligt, eller vid oundvikliga och extraordinära omständigheter.',
      right5: 'Återbetalning sker utan onödigt dröjsmål och senast inom 14 dagar.',
      right6: 'Om arrangören blir insolvent återbetalas dina betalningar via Rejsegarantifonden.',
      contact: 'Insolvensskydd: Rejsegarantifonden, Røjelskær 11, 2840 Holte, Danmark – info@rejsegarantifonden.dk, +45 45 87 83 33.',
      consentLabel: 'Jag har läst informationen om paketresan och accepterar villkoren.',
      logoAlt: 'Rejsegarantifonden',
    },
  },
}

const nb: WidgetStrings = {
  checkoutLegal: {
    summaryTitle: 'Ordreoversikt',
    seller: 'Selger: {name}',
    companyId: 'Org.nr. {cvr}',
    platformNote: 'Holdstyring leverer bare den tekniske løsningen og er ikke part i kjøpet.',
    total: 'Totalt inkl. mva.',
    totalBeforeDiscount: 'Totalt inkl. mva. før rabattkode',
    recurringNote: 'Abonnement som fornyes automatisk til du sier det opp.',
    perFourWeeks: 'per 4 uker',
    perMonth: 'per måned',
    perQuarter: 'per kvartal',
    perHalfYear: 'per halvår',
    perYear: 'per år',
    oneTimeNote: 'Engangskjøp.',
    acceptTerms: 'Jeg godtar kjøpsvilkårene og har lest informasjonen om angrerett.',
    acceptTermsEarlyStart: 'Jeg godtar kjøpsvilkårene og angreretten, og at tjenesten starter med en gang.',
    termsLink: 'Kjøpsvilkår',
    withdrawalLink: 'Angrerett',
    privacyLink: 'Personvernerklæring',
    withdrawContractLink: 'Angre avtalen',
    noWithdrawal: 'Det er ingen angrerett ved booking av en time på en bestemt dato.',
    paidOrderNote: 'Når du trykker på knappen, legger du inn en bestilling med betalingsplikt.',
    payGeneric: 'Kjøp og betal',
    payAmount: 'Kjøp og betal ({amount})',
    payCard: 'Kjøp og betal med kort',
    payMobilePay: 'Kjøp og betal med MobilePay',
  },
  spotsOpenLabel: 'Ledige plasser',
  spotsFewLabel: 'Få plasser igjen',
  loadError: 'Kunne ikke lastes',
  close: 'Lukk',
  cancel: 'Avbryt',
  login: 'Logg inn',
  readMore: 'Les mer',
  readLess: 'Vis mindre',
  book: 'Book',
  buy: 'Kjøp',
  buyLoginPrompt: 'Logg inn eller opprett en konto for å kjøpe',
  purchasing: 'Kjøper...',
  continueToPurchase: 'Fortsett til betaling',
  purchaseHandoffBody: 'Fullfør kjøpet på nettstedet vårt.',
  alreadyBookedTitle: 'Du er påmeldt',
  alreadyBookedBody: 'Du har allerede booket denne timen.',
  waitlistTitle: 'På venteliste',
  waitlistBody: 'Vi gir beskjed hvis det blir en ledig plass.',
  confirmedTitle: 'Booking bekreftet',
  confirmedBody: 'Du får en bekreftelse på e-post.',
  loginToBook: 'Logg inn for å booke denne timen.',
  fullTitle: 'Timen er full',
  fullBody: 'Det er ingen venteliste for denne timen.',
  membershipRequiredBody: 'Denne timen krever et aktivt medlemskap. Fortsett på nettsiden vår for å velge medlemskap og booke.',
  paymentRequiredBody: 'Denne timen må betales. Fortsett på nettsiden vår for å betale og booke.',
  classPlansHeading: 'Eller kjøp et klippekort',
  classPlansHeadingOnly: 'Kjøp et klippekort til denne timen',
  classPlansClips: '{count, plural, one {# klipp} other {# klipp}}',
  classPlansValidDays: '{count, plural, one {gyldig # dag} other {gyldig # dager}}',
  classPlansBuy: 'Kjøp',
  classPlansViewSubscription: 'Se abonnement',
  classPlansSubscription: 'Abonnement',
  classPlansAfterNote: 'Etter betalingen kommer du tilbake hit. Åpne timen igjen og trykk Book, så brukes det nye klippekortet ditt.',
  classPlansPaySingle: 'Betal enkeltbillett',
  waiverRequiredBody: 'Denne timen krever at du godtar studioets ansvarsfraskrivelse. Fortsett på nettsiden vår for å lese og godta den.',
  intakeRequiredBody: 'Denne timen stiller noen spørsmål første gang. Fortsett på nettsiden vår for å svare — så husker vi dem til neste gang.',
  participantNamesBody: 'Denne timen krever navnene på deltakerne. Fortsett på nettsiden vår for å booke.',
  participantProfileBody: 'Hos dette studioet bookes hver plass for en deltakerprofil. Velg hvem som skal delta, på nettsiden vår.',
  waitlistClipCardBody: 'Denne timen er full, og klippekortet ditt dekker den. Fortsett på nettsiden vår for å sette deg på venteliste, så klippet ditt blir reservert.',
  bookWithGuests: 'Book med gjester',
  fullVenueButton: 'Book hele holdet',
  continueToBooking: 'Fortsett til booking',
  dropInPrice: 'Drop-in-pris',
  clipCostShort: '{count, plural, one {# klipp} other {# klipp}}',
  priceFromPrefix: 'Fra',
  vatFreePrice: 'Mva.-fri pris',
  ownCancellationHeading: 'Egen avmeldingsfrist:',
  ownCancellationDeadline: 'avmelding senest {count} timer før start.',
  ownCancellationDeadlineOne: 'avmelding senest {count} time før start.',
  ownCancellationNoRefund: 'ingen refusjon ved avmelding.',
  membershipOnly: 'Medlemskap',
  includedInSubscription: 'Inkludert i abonnementet ditt',
  freeClass: 'Gratis',
  booking: 'Booker...',
  joinWaitlist: 'Sett deg på venteliste',
  confirmBooking: 'Bekreft booking',
  guests: 'Gjester',
  perGuest: 'per gjest',
  memberSpotCovered: 'Din plass er dekket av medlemskapet ditt',
  coveredByClipCard: 'Dekket av klippekortet ditt',
  useClipCard: 'Bruk klippekort',
  coveredBySubscription: 'Dekket av abonnementet ditt',
  confirmAndPay: 'Bekreft og betal',
  paymentMethod: 'Betalingsmetode',
  paymentMethodCard: 'Betalingskort',
  addGuests: 'Legg til gjester',
  guestOption: 'Fortsett som gjest',
  guestConsent: 'Jeg godtar salgsbetingelsene og har lest personvernerklæringen.',
  guestConsentRequired: 'Du må godta vilkårene for å fortsette.',
  guestSubmit: 'Fortsett til betaling',
  guestSubmitting: 'Fortsetter…',
  guestError: 'Kunne ikke fortsette som gjest. Prøv igjen eller logg inn.',
  guestAccountExists: 'Det finnes allerede en konto med denne e-posten. Logg inn i stedet.',
  guestOrLoginPrompt: 'Du kan kjøpe plassen din med én gang, eller logge inn hvis du allerede er medlem.',
  guestIntro: 'Vi trenger bare kontaktopplysningene dine så vi kan sende deg en bekreftelse. Du kan opprette et passord etterpå hvis du vil.',
  guestTermsLink: 'Salgsbetingelser',
  guestPrivacyLink: 'Personvernerklæring',
  guestCoverageHint: 'Har du klippekort eller abonnement? Logg inn i stedet, så bruker vi det.',
  guestLinkSent: 'Du har booket hos oss før med denne e-posten. Vi har sendt deg en lenke slik at du kan velge et passord og fortsette.',
  noBlogPosts: 'Ingen blogginnlegg ennå',
  noClassTypes: 'Ingen timetyper tilgjengelig',
  noContactInfo: 'Ingen kontaktopplysninger tilgjengelig',
  noInstructors: 'Ingen instruktører tilgjengelig',
  noPricingPlans: 'Ingen prisplaner tilgjengelig',
  planPeriodPicker: 'Velg periode',
  planPeriodFourWeeks: '4 uker',
  planPeriodOneMonth: '1 måned',
  planPeriodThreeMonths: '3 måneder',
  planPeriodSixMonths: '6 måneder',
  planPeriodTwelveMonths: '12 måneder',
  planPerMonth: 'tilsvarer {amount} per måned',
  noRooms: 'Ingen lokaler tilgjengelig',
  noSeasons: 'Ingen aktive sesonger',
  noTreatments: 'Ingen behandlinger tilgjengelig',
  noWaitlists: 'Ingen ventelister åpne for påmelding akkurat nå',
  noClassesScheduled: 'Ingen timer planlagt for øyeblikket',
  noClassesThisWeek: 'Ingen timer denne uken',
  noClassesDay: 'Ingen timer',
  weekOverview: 'Ukesoversikt',
  weekAbbrev: 'Uke',
  previousWeek: 'Forrige uke',
  nextWeek: 'Neste uke',
  thisWeek: 'Denne uken',
  viewList: 'Timer',
  viewCalendar: 'Kalender',
  filterAll: 'Alle',
  loadTreatmentsError: 'Kunne ikke laste behandlinger',
  openingHours: 'Åpningstider',
  confirmed: 'Bekreftet',
  paused: 'På pause',
  popular: 'Populær',
  comingSoon: 'Snart tilgjengelig',
  yearly: 'Årlig',
  monthly: 'Månedlig',
  classesThisMonth: 'timer denne måneden',
  previousPurchases: 'Tidligere kjøp',
  showOnMap: 'Vis på kart',
  buyGiftCard: 'Kjøp gavekort',
  giftCardImageAlt: 'Gavekort',
  gcBuyTitle: 'Kjøp gavekort',
  gcBuyAmount: 'Beløp',
  gcBuyYourName: 'Ditt navn',
  gcBuyYourEmail: 'Din e-post',
  gcBuyRecipientEmail: 'Mottakerens e-post (valgfritt)',
  gcBuyMessage: 'Personlig hilsen (valgfritt)',
  gcBuySubmitting: 'Kjøper...',
  registrationOpen: 'Påmelding åpen',
  contactFormIntro: 'Skriv til oss, så kommer vi tilbake til deg så raskt som mulig.',
  contactFormSuccess: 'Vi har mottatt meldingen din og svarer så raskt vi kan.',
  noClassesPlanned: 'Ingen timer planlagt',
  nextClassesStart: 'Timene starter {date}',
  booked: 'Booket',
  full: 'Fullt',
  price: 'Pris',
  includes: 'Inkluderer',
  waitlist: 'Venteliste',
  awaitingPayment: 'Venter på betaling',
  payBy: 'Betal innen {time}',
  payNow: 'Betal nå',
  fullyBooked: 'Fullbooket',
  loggedInAs: 'Logget inn som',
  spot: 'plass',
  spots: 'plasser',
  spotAvailable: 'plass ledig',
  spotsAvailable: 'plasser ledige',
  fromPrice: 'fra',
  classSingular: 'time',
  classPlural: 'timer',
  signUp: 'Meld deg på',
  registrationClosed: 'Påmelding stengt',
  klippekort: 'Klippekort',
  dropIn: 'Drop-in',
  discountCodeToggle: 'Har du en rabattkode?',
  discountCodePlaceholder: 'Rabattkode',
  discountInvalid: 'Rabattkoden er ikke gyldig.',
  discountExpired: 'Rabattkoden er utløpt.',
  discountUsedUp: 'Rabattkoden er brukt opp.',
  discountAlreadyUsed: 'Du har allerede brukt denne rabattkoden.',
  discountNotForClasses: 'Rabattkoden gjelder ikke timer.',
  discountTooManyAttempts: 'For mange forsøk. Prøv igjen om litt.',
  discountApply: 'Bruk',
  discountRemove: 'Fjern',
  discountLine: 'Rabatt',
  priceAfterDiscount: 'Pris etter rabatt',
  discountFirstPaymentOnly: 'Rabatten gjelder den første betalingen.',
  firstChargeLabel: 'Første trekk',
  signupFeeLineLabel: 'Innmeldingsgebyr (nye medlemmer):',
  firstPaymentTotalLabel: 'Totalt ved første betaling',
  firstChargeNote: 'Første trekk dekker resten av denne måneden. Deretter {price} den 1. hver måned.',
  deferredFirstChargeNote: 'Du får tilgang med en gang og betaler ingenting i dag. Første betaling på {price} trekkes {date}.',
  firstChargeNoteFolded: 'Første trekk dekker resten av denne måneden og neste hele måned. Deretter {price} den 1. hver måned.',
  discountEveryPayment: 'Rabatten gjelder hver betaling.',
  discountFreeMonths: '{months} måneder gratis før første betaling.',
  subscriptionTitle: 'Abonnement',
  startedOn: 'Startet',
  clipsRemaining: 'klipp igjen',
  ofTotal: 'av',
  expiresOn: 'Utløper',
  active: 'Aktiv',
  activeUntil: 'Aktiv til',
  autoRenews: 'Fornyes automatisk',
  noAutoRenew: 'Fornyes ikke automatisk',
  pauseOrCancel: 'Pause / si opp',
  confirmUnsubscribe: 'Er du sikker på at du vil melde deg av "{name}"?',
  newsletterTitle: 'Nyhetsbrev',
  newsletterSubtitle: 'Meld deg på og hold deg oppdatert',
  newsletterSubmit: 'Meld deg på',
  newsletterSubmitting: 'Melder på...',
  newsletterThanksTitle: 'Takk!',
  newsletterThanksBody: 'Du er nå påmeldt nyhetsbrevet vårt.',
  emailPlaceholder: 'Din e-postadresse',
  welcomeName: 'Velkommen, {name}!',
  logout: 'Logg ut',
  forgotPassword: 'Glemt passord',
  forgotPasswordQ: 'Glemt passordet?',
  forgotPasswordHint: 'Kontakt {tenant} for å tilbakestille passordet ditt.',
  forgotPasswordInstruction: 'Skriv inn e-posten din, så sender vi en lenke for å tilbakestille passordet.',
  forgotPasswordSubmit: 'Send tilbakestillingslenke',
  forgotPasswordSent: 'Hvis det finnes en konto med den e-posten, har vi sendt en lenke for å tilbakestille passordet. Sjekk innboksen din.',
  backToLogin: 'Tilbake til innlogging',
  passwordSetupTitle: 'Sjekk e-posten din',
  passwordSetupBody: 'Vi har sendt en e-post til {email}. Klikk på lenken for å bekrefte, så blir passordet aktivert. Kom deretter tilbake og logg inn her.',
  passwordTooShort: 'Passordet må være minst 8 tegn.',
  createAccount: 'Opprett konto',
  createAccountAt: 'Opprett en konto hos {tenant}.',
  loginAt: 'Logg inn hos {tenant}.',
  fieldFirstName: 'Fornavn',
  fieldLastName: 'Etternavn',
  fieldPhone: 'Telefon',
  optionalSuffix: '(valgfritt)',
  fieldPassword: 'Passord',
  fieldPasswordMin: 'Passord (minst 8 tegn)',
  creatingAccount: 'Oppretter konto...',
  loggingIn: 'Logger inn...',
  alreadyHaveAccount: 'Har du allerede en konto?',
  noAccountQ: 'Har du ingen konto?',
  createOne: 'Opprett en',
  contactTitle: 'Kontakt oss',
  fillMarkedFields: 'Fyll ut de markerte feltene riktig.',
  sending: 'Sender...',
  sendMessage: 'Send melding',
  address: 'Adresse',
  noTestimonials: 'Ingen anmeldelser ennå',
  starsOutOf: '{n} av 5 stjerner',
  mapOf: 'Kart over {tenant}',
  atTime: 'kl.',
  unsubscribe: 'Meld av',
  noUpcomingClasses: 'Ingen kommende timer',
  temperatureWater: 'Vanntemperatur',
  temperatureAir: 'Lufttemperatur',
  temperatureUpdated: 'Oppdatert',
  temperatureUnavailable: 'Temperaturen er ikke tilgjengelig akkurat nå',
  noUpcomingBookings: 'Du har ingen kommende bookinger',
  loginToSeeBookings: 'Logg inn for å se bookingene dine',
  bookClass: 'Book time',
  seeClasses: 'Se timer',
  seePrices: 'Se priser',
  topUp: 'Fyll på',
  manageMembership: 'Administrer medlemskap',
  loginToSeeAgreement: 'Logg inn for å se avtalen din',
  noActiveAgreement: 'Du har ingen aktiv avtale',
  subscription: 'abonnement',
  soldOut: 'Utsolgt',
  closed: 'Stengt',
  comingSoonShort: 'Snart',
  contactMe: 'Kontakt meg',
  categoryOther: 'Annet',
  plan: 'Plan',
  fieldName: 'Navn',
  fieldEmail: 'E-post',
  emailDidYouMean: 'Mente du',
  fieldMessage: 'Melding',
  contactFormThanks: 'Takk! Vi kommer tilbake til deg snarest',
  tba: 'Ikke oppgitt',
  loginOrSignUpToBook: 'Logg inn eller opprett en konto for å booke',
  onWaitlist: 'på ventelisten',
  joinWaitlistCta: 'Sett deg på venteliste',
  waitlistJoinedTitle: 'Du står på ventelisten',
  yourPosition: 'Din plass',
  alreadyOnWaitlist: 'Du står allerede på denne ventelisten',
  loginOrSignUpToJoin: 'Logg inn eller opprett en konto for å komme på ventelisten til',
  upcomingSessions: 'Kommende timer',
  klinikPickerAllServices: 'Alle tjenester',
  klinikNoSlots: 'Ingen ledige timer akkurat nå',
  klinikShowMore: 'Last inn flere dager',
  klinikBookAt: 'Book hos {name}',
  pricePerPerson: '{price} per person',
  noUpcoming: 'Ingen kommende arrangementer',
  viewRetreat: 'Se retreat',
  viewEvent: 'Se arrangement',
  showMore: 'Vis flere',
  fewSpotsLeft: 'Få plasser igjen',
  retreatAbout: 'Om retreatet',
  retreatInstructors: 'Ledere',
  retreatLocation: 'Sted',
  retreatFaq: 'Ofte stilte spørsmål',
  retreatChooseTier: 'Velg pakke',
  retreatNotPublished: 'Dette retreatet er ikke offentlig ennå',
  eventAbout: 'Om arrangementet',
  eventChooseTier: 'Velg billett',
  eventNotPublished: 'Dette arrangementet er ikke offentlig ennå',
  retreatOrganizer: 'Arrangør',
  retreatPackageInfo: 'Dette er en pakkereise arrangert av {organizer}, registrert i danske Rejsegarantifonden (reg.nr. {regNo}). Avtalen inngås med {organizer}.',
  retreatBooking: {
    modalAriaLabel: 'Booking-dialog',
    nav: {
      cancel: 'Avbryt',
      back: 'Tilbake',
      next: 'Neste',
      toPayment: 'Til betaling',
      confirmFree: 'Bekreft påmelding',
      submitting: 'Sender…',
      stepProgress: 'Trinn {current} av {total}',
    },
    steps: {
      indicatorAriaLabel: 'Booking-trinn',
      who: {
        title: 'Hvem booker?',
        description: 'Velg om du booker for deg selv eller for to.',
        shortLabel: 'Hvem',
        countLegend: 'Antall personer',
        soloOption: 'Meg selv',
        soloHelp: '1 person',
        pairOption: 'For to',
        pairHelp: '2 personer på samme booking',
        privateDoubleSoloTitle: 'Du booker hele dobbeltrommet',
        privateDoubleSoloBody: 'Du har rommet for deg selv. Prisen er for hele rommet.',
        roommateLegend: 'Romkamerat',
        sharedDoubleSoloHelp: 'Du deler dobbeltrommet med en annen solo-booker. Vi matcher før retreat-start.',
        roommate: {
          matchMe: 'Match meg',
          matchMeHelp: 'Vi finner en passende roommate før retreat-start',
          alone: 'Jeg vil helst være alene',
          aloneHelp: 'Vi forsøker å gi deg rommet alene — kan utløse single-supplement',
          knownPartner: 'Jeg kjenner partneren min',
          knownPartnerHelp: 'Skriv hvem du vil dele med',
          knownPartnerNameLabel: 'Navn på roommate',
          knownPartnerNamePlaceholder: 'F.eks. Maja Hansen',
        },
      },
      info: {
        title: 'Deltakerinfo',
        description: 'Fyll ut navn og e-post for hver deltaker.',
        shortLabel: 'Info',
        participantHeading: 'Deltaker {index}',
        participantHeadingSingle: 'Dine opplysninger',
        firstNameLabel: 'Fornavn',
        lastNameLabel: 'Etternavn',
        emailLabel: 'E-post',
        phoneLabel: 'Telefon',
        dietaryLabel: 'Kostholdsnotater',
        dietaryPlaceholder: 'Allergier, vegetar, vegansk osv.',
        dietaryHelp: 'Maks 500 tegn',
        errors: {
          firstNameRequired: 'Fornavn er påkrevd',
          lastNameRequired: 'Etternavn er påkrevd',
          invalidEmail: 'Ugyldig e-post',
          invalidPhone: 'Ugyldig telefonnummer',
          dietaryTooLong: 'Maks 500 tegn',
        },
      },
      questions: {
        title: 'Påmeldingsspørsmål',
        description: 'Hjelp oss med å planlegge oppholdet ditt.',
        descriptionEvent: 'Hjelp oss med å planlegge arrangementet.',
        shortLabel: 'Spørsmål',
        noQuestions: 'Ingen flere spørsmål.',
        selectPlaceholder: '— Velg —',
        errors: { required: 'Påkrevd' },
      },
      payment: {
        title: 'Betaling',
        description: 'Velg betalingsmetode.',
        shortLabel: 'Betaling',
        breakdownLine: '{count} × {tierName} à {perPerson}',
        vatLine: 'Mva. ({rate}%)',
        totalLabel: 'Totalt',
        chooseModeLegend: 'Betalingsmodell',
        fullPayment: 'Betal nå',
        fullPaymentHelp: 'Betal hele beløpet på én gang',
        depositPayment: 'Depositum',
        depositPaymentHelp: 'Betal {percent}% nå, resten senere',
        depositPaymentHelpWithDate: 'Betal {percent}% nå, resten senest {dueDate}',
        payNowShort: 'Betal nå: {amount}',
        installmentsPayment: 'Avdragsbetaling',
        installmentsPaymentHelp: '{count} månedlige avdrag',
        perMonthShort: '{amount} per mnd.',
        discountSectionTitle: 'Rabatt',
        giftCardLabel: 'Gavekort',
        giftCardPlaceholder: 'Skriv inn gavekortkode',
        giftCardHelp: 'Trekkes fra totalbeløpet',
        couponLabel: 'Rabattkode',
        couponPlaceholder: 'Skriv inn rabattkode',
        couponHelp: 'Aktiveres ved bekreftelse',
        submitting: 'Sender…',
      },
    },
    priceSummary: {
      lineLabel: '{count} × {tierName}',
      vatLabel: 'Mva. ({rate}%)',
      discountLabel: 'Rabatt',
      totalLabel: 'Totalt',
      payNowLabel: 'Betal nå',
      dueLaterLabel: 'Senere',
      depositNote: 'Depositum: {percent}% nå, resten senere',
      installmentsNote: 'Avdrag: {count} månedlige rater',
    },
    errors: {
      capacityTaken: 'Plassen ble dessverre booket mens du fylte ut. Velg en annen type eller sett deg på venteliste.',
      paymentNotConfigured: "Nettbetaling er ikke åpnet ennå for dette tilbudet. Kontakt studioet for å melde deg på.",
      paymentProviderError: "Betalingen kunne ikke startes. Prøv igjen om litt, eller kontakt studioet.",
      paymentModeUnavailable: "Betalingen er ikke satt opp for dette tilbudet ennå. Kontakt studioet.",
      notAvailable: "Det kan ikke bookes akkurat nå. Kontakt studioet.",
      validation: 'Sjekk at alle feltene er fylt ut riktig.',
      generic: 'Noe gikk galt — prøv igjen om et øyeblikk.',
      network: 'Nettverksfeil — sjekk tilkoblingen din og prøv igjen.',
      coupon: {
        notAllowed: 'Rabattkoder kan ikke brukes på denne retreaten.',
        unknown: 'Rabattkoden finnes ikke. Sjekk stavemåten.',
        notStarted: 'Rabattkoden er ikke aktiv ennå.',
        expired: 'Rabattkoden er utløpt.',
        usedUp: 'Rabattkoden er brukt opp.',
        alreadyUsed: 'Du har allerede brukt denne rabattkoden.',
        newCustomersOnly: 'Rabattkoden gjelder kun nye kunder.',
        requiresMembership: 'Rabattkoden krever et aktivt medlemskap.',
        requiresSpecificMembership: 'Rabattkoden gjelder bare for kunder med et bestemt medlemskap eller kurs. Er du medlem, logg inn eller bruk e-postadressen du er registrert med.',
        wrongProduct: 'Rabattkoden gjelder ikke retreater.',
        otherRetreat: 'Rabattkoden gjelder ikke denne retreaten.',
        notAllowedEvent: 'Rabattkoder kan ikke brukes på dette arrangementet.',
        wrongProductEvent: 'Rabattkoden gjelder ikke arrangementer.',
        otherRetreatEvent: 'Rabattkoden gjelder ikke dette arrangementet.',
        belowMinimum: 'Rabattkoden krever et kjøp på minst {minimum}.',
      },
    },
    packageTravel: {
      heading: 'Dette er en pakkereise',
      organizerNote: 'Du inngår avtale med {organizer} – ikke med Class Booking. {organizer} er arrangør og ansvarlig for pakkereisen.',
      registeredLine: '{organizer} er registrert i Rejsegarantifonden, medlemsnr. {memberNumber}.',
      formToggle: 'Les om rettighetene dine som reisende',
      formIntro: 'Kombinasjonen av reisetjenester du tilbys, er en pakkereise i henhold til den danske pakkereiseloven. Du er derfor omfattet av alle rettigheter som gjelder for pakkereiser.',
      formResponsibility: '{organizer} er fullt ansvarlig for korrekt gjennomføring av hele pakkereisen og har lovpålagt konkursdekning hos Rejsegarantifonden (medlemsnr. {memberNumber}).',
      rightsTitle: 'Sentrale rettigheter',
      right1: 'Du får all vesentlig informasjon om pakkereisen før du inngår avtalen.',
      right2: 'Minst én næringsdrivende er ansvarlig for korrekt levering av alle reisetjenestene i avtalen.',
      right3: 'Øker prisen med mer enn 8 %, kan du si opp avtalen.',
      right4: 'Du kan si opp uten gebyr og få full refusjon hvis vesentlige elementer endres vesentlig, eller ved uunngåelige og ekstraordinære omstendigheter.',
      right5: 'Refusjon skjer uten ugrunnet opphold og senest innen 14 dager.',
      right6: 'Blir arrangøren insolvent, refunderes betalingene dine via Rejsegarantifonden.',
      contact: 'Konkursdekning: Rejsegarantifonden, Røjelskær 11, 2840 Holte, Danmark – info@rejsegarantifonden.dk, +45 45 87 83 33.',
      consentLabel: 'Jeg har lest informasjonen om pakkereisen og godtar vilkårene.',
      logoAlt: 'Rejsegarantifonden',
    },
  },
}

const es: WidgetStrings = {
  checkoutLegal: {
    summaryTitle: 'Resumen del pedido',
    seller: 'Vendedor: {name}',
    companyId: 'N.º de empresa {cvr}',
    platformNote: 'Holdstyring solo proporciona la solución técnica y no es parte en la compra.',
    total: 'Total IVA incluido',
    totalBeforeDiscount: 'Total IVA incluido antes del código de descuento',
    recurringNote: 'Suscripción que se renueva automáticamente hasta que la canceles.',
    perFourWeeks: 'cada 4 semanas',
    perMonth: 'al mes',
    perQuarter: 'al trimestre',
    perHalfYear: 'al semestre',
    perYear: 'al año',
    oneTimeNote: 'Compra única.',
    acceptTerms: 'Acepto las condiciones de venta y he leído la información sobre el derecho de desistimiento.',
    acceptTermsEarlyStart: 'Acepto las condiciones de venta y el derecho de desistimiento, y que el servicio comience de inmediato.',
    termsLink: 'Condiciones de venta',
    withdrawalLink: 'Derecho de desistimiento',
    privacyLink: 'Política de privacidad',
    withdrawContractLink: 'Desistir del contrato',
    noWithdrawal: 'No existe derecho de desistimiento al reservar una clase en una fecha concreta.',
    paidOrderNote: 'Al pulsar el botón realizas un pedido con obligación de pago.',
    payGeneric: 'Comprar y pagar',
    payAmount: 'Comprar y pagar ({amount})',
    payCard: 'Comprar y pagar con tarjeta',
    payMobilePay: 'Comprar y pagar con MobilePay',
  },
  spotsOpenLabel: 'Plazas disponibles',
  spotsFewLabel: 'Quedan pocas plazas',
  loadError: 'No se pudo cargar',
  close: 'Cerrar',
  cancel: 'Cancelar',
  login: 'Iniciar sesión',
  readMore: 'Leer más',
  readLess: 'Ver menos',
  book: 'Reservar',
  buy: 'Comprar',
  buyLoginPrompt: 'Inicia sesión o crea una cuenta para comprar',
  purchasing: 'Comprando...',
  continueToPurchase: 'Continuar al pago',
  purchaseHandoffBody: 'Completa tu compra en nuestro sitio web.',
  alreadyBookedTitle: 'Ya tienes plaza',
  alreadyBookedBody: 'Ya has reservado esta clase.',
  waitlistTitle: 'En lista de espera',
  waitlistBody: 'Te avisaremos si queda una plaza libre.',
  confirmedTitle: 'Reserva confirmada',
  confirmedBody: 'Recibirás una confirmación por correo electrónico.',
  loginToBook: 'Inicia sesión para reservar esta clase.',
  fullTitle: 'La clase está completa',
  fullBody: 'Esta clase no tiene lista de espera.',
  membershipRequiredBody: 'Esta clase requiere una suscripción activa. Continúa en nuestra web para elegir una suscripción y reservar.',
  paymentRequiredBody: 'Esta clase requiere pago. Continúa en nuestra web para pagar y reservar.',
  classPlansHeading: 'O compra una tarjeta de clases',
  classPlansHeadingOnly: 'Compra una tarjeta de clases para esta clase',
  classPlansClips: '{count, plural, one {# clase} other {# clases}}',
  classPlansValidDays: '{count, plural, one {válida durante # día} other {válida durante # días}}',
  classPlansBuy: 'Comprar',
  classPlansViewSubscription: 'Ver suscripción',
  classPlansSubscription: 'Suscripción',
  classPlansAfterNote: 'Después del pago volverás aquí. Abre la clase de nuevo y pulsa Reservar, y se usará tu nueva tarjeta de clases.',
  classPlansPaySingle: 'Pagar entrada individual',
  waiverRequiredBody: 'Esta clase requiere aceptar la exención de responsabilidad del estudio. Continúa en nuestra web para leerla y aceptarla.',
  intakeRequiredBody: 'Esta clase hace algunas preguntas la primera vez. Continúa en nuestra web para responderlas: las recordaremos la próxima vez.',
  participantNamesBody: 'Esta clase requiere los nombres de los asistentes. Continúa en nuestra web para reservar.',
  participantProfileBody: 'En este estudio cada plaza se reserva para un perfil de participante. Elige quién asiste en nuestra web.',
  waitlistClipCardBody: 'Esta clase está completa y tu bono de clases la cubre. Continúa en nuestra web para apuntarte a la lista de espera y reservar una clase de tu bono.',
  bookWithGuests: 'Reservar con invitados',
  fullVenueButton: 'Reservar la clase completa',
  continueToBooking: 'Continuar con la reserva',
  dropInPrice: 'Precio por clase suelta',
  clipCostShort: '{count, plural, one {# clase} other {# clases}}',
  priceFromPrefix: 'Desde',
  vatFreePrice: 'Precio sin IVA',
  ownCancellationHeading: 'Plazo de cancelación especial:',
  ownCancellationDeadline: 'cancelación al menos {count} horas antes del inicio.',
  ownCancellationDeadlineOne: 'cancelación al menos {count} hora antes del inicio.',
  ownCancellationNoRefund: 'sin reembolso al cancelar.',
  membershipOnly: 'Membresía',
  includedInSubscription: 'Incluido en tu suscripción',
  freeClass: 'Gratis',
  booking: 'Reservando...',
  joinWaitlist: 'Apuntarse a la lista de espera',
  confirmBooking: 'Confirmar reserva',
  guests: 'Invitados',
  perGuest: 'por invitado',
  memberSpotCovered: 'Tu plaza está incluida en tu membresía',
  coveredByClipCard: 'Incluida en tu bono de clases',
  useClipCard: 'Usar bono',
  coveredBySubscription: 'Incluida en tu suscripción',
  confirmAndPay: 'Confirmar y pagar',
  paymentMethod: 'Método de pago',
  paymentMethodCard: 'Tarjeta',
  addGuests: 'Añadir invitados',
  guestOption: 'Continuar como invitado',
  guestConsent: 'Acepto las condiciones de compra y he leído la política de privacidad.',
  guestConsentRequired: 'Debes aceptar las condiciones para continuar.',
  guestSubmit: 'Continuar al pago',
  guestSubmitting: 'Continuando…',
  guestError: 'No se pudo continuar como invitado. Inténtalo de nuevo o inicia sesión.',
  guestAccountExists: 'Ya existe una cuenta con este correo electrónico. Inicia sesión en su lugar.',
  guestOrLoginPrompt: 'Puedes comprar tu plaza ahora mismo, o iniciar sesión si ya eres miembro.',
  guestIntro: 'Solo necesitamos tus datos de contacto para poder enviarte una confirmación. Puedes crear una contraseña después si quieres.',
  guestTermsLink: 'Condiciones de compra',
  guestPrivacyLink: 'Política de privacidad',
  guestCoverageHint: '¿Tienes un bono o una suscripción? Inicia sesión y la usaremos.',
  guestLinkSent: 'Ya has reservado con nosotros con este correo. Te hemos enviado un enlace para elegir una contraseña y continuar.',
  noBlogPosts: 'Aún no hay entradas de blog',
  noClassTypes: 'No hay tipos de clase disponibles',
  noContactInfo: 'No hay datos de contacto disponibles',
  noInstructors: 'No hay profesores disponibles',
  noPricingPlans: 'No hay planes de precios disponibles',
  planPeriodPicker: 'Elige el periodo',
  planPeriodFourWeeks: '4 semanas',
  planPeriodOneMonth: '1 mes',
  planPeriodThreeMonths: '3 meses',
  planPeriodSixMonths: '6 meses',
  planPeriodTwelveMonths: '12 meses',
  planPerMonth: 'equivale a {amount} al mes',
  noRooms: 'No hay salas disponibles',
  noSeasons: 'No hay temporadas activas',
  noTreatments: 'No hay tratamientos disponibles',
  noWaitlists: 'No hay listas de espera abiertas en este momento',
  noClassesScheduled: 'No hay clases programadas en este momento',
  noClassesThisWeek: 'No hay clases esta semana',
  noClassesDay: 'Sin clases',
  weekOverview: 'Resumen semanal',
  weekAbbrev: 'Semana',
  previousWeek: 'Semana anterior',
  nextWeek: 'Semana siguiente',
  thisWeek: 'Esta semana',
  viewList: 'Clases',
  viewCalendar: 'Calendario',
  filterAll: 'Todas',
  loadTreatmentsError: 'No se pudieron cargar los tratamientos',
  openingHours: 'Horario de apertura',
  confirmed: 'Confirmada',
  paused: 'En pausa',
  popular: 'Popular',
  comingSoon: 'Próximamente',
  yearly: 'Anual',
  monthly: 'Mensual',
  classesThisMonth: 'clases este mes',
  previousPurchases: 'Compras anteriores',
  showOnMap: 'Ver en el mapa',
  buyGiftCard: 'Comprar tarjeta regalo',
  giftCardImageAlt: 'Tarjeta regalo',
  gcBuyTitle: 'Comprar tarjeta regalo',
  gcBuyAmount: 'Importe',
  gcBuyYourName: 'Tu nombre',
  gcBuyYourEmail: 'Tu correo electrónico',
  gcBuyRecipientEmail: 'Correo del destinatario (opcional)',
  gcBuyMessage: 'Mensaje personal (opcional)',
  gcBuySubmitting: 'Comprando...',
  registrationOpen: 'Inscripción abierta',
  contactFormIntro: 'Escríbenos y te responderemos lo antes posible.',
  contactFormSuccess: 'Hemos recibido tu mensaje y te responderemos lo antes posible.',
  noClassesPlanned: 'No hay clases programadas',
  nextClassesStart: 'Las clases empiezan el {date}',
  booked: 'Reservada',
  full: 'Completa',
  price: 'Precio',
  includes: 'Incluye',
  waitlist: 'Lista de espera',
  awaitingPayment: 'Pendiente de pago',
  payBy: 'Pague antes del {time}',
  payNow: 'Pagar ahora',
  fullyBooked: 'Completa',
  loggedInAs: 'Sesión iniciada como',
  spot: 'plaza',
  spots: 'plazas',
  spotAvailable: 'plaza libre',
  spotsAvailable: 'plazas libres',
  fromPrice: 'desde',
  classSingular: 'clase',
  classPlural: 'clases',
  signUp: 'Inscríbete',
  registrationClosed: 'Inscripción cerrada',
  klippekort: 'Bono de clases',
  dropIn: 'Clase suelta',
  discountCodeToggle: '¿Tienes un código de descuento?',
  discountCodePlaceholder: 'Código de descuento',
  discountInvalid: 'Este código de descuento no es válido.',
  discountExpired: 'Este código de descuento ha caducado.',
  discountUsedUp: 'Este código de descuento se ha agotado.',
  discountAlreadyUsed: 'Ya has usado este código de descuento.',
  discountNotForClasses: 'El código de descuento no se aplica a las clases.',
  discountTooManyAttempts: 'Demasiados intentos. Vuelve a intentarlo en un momento.',
  discountApply: 'Aplicar',
  discountRemove: 'Quitar',
  discountLine: 'Descuento',
  priceAfterDiscount: 'Precio con descuento',
  discountFirstPaymentOnly: 'El descuento se aplica al primer pago.',
  firstChargeLabel: 'Primer cargo',
  signupFeeLineLabel: 'Cuota de inscripción (nuevos miembros):',
  firstPaymentTotalLabel: 'Total del primer pago',
  firstChargeNote: 'El primer cargo cubre el resto de este mes. Después, {price} el día 1 de cada mes.',
  deferredFirstChargeNote: 'Tienes acceso de inmediato y no pagas nada hoy. El primer pago de {price} se cobra el {date}.',
  firstChargeNoteFolded: 'El primer cargo cubre el resto de este mes y el próximo mes completo. Después, {price} el día 1 de cada mes.',
  discountEveryPayment: 'El descuento se aplica a cada pago.',
  discountFreeMonths: '{months} meses gratis antes del primer pago.',
  subscriptionTitle: 'Suscripción',
  startedOn: 'Desde el',
  clipsRemaining: 'sesiones restantes',
  ofTotal: 'de',
  expiresOn: 'Caduca el',
  active: 'Activa',
  activeUntil: 'Activa hasta el',
  autoRenews: 'Se renueva automáticamente',
  noAutoRenew: 'No se renueva automáticamente',
  pauseOrCancel: 'Pausar / cancelar',
  confirmUnsubscribe: '¿Seguro que quieres cancelar "{name}"?',
  newsletterTitle: 'Boletín',
  newsletterSubtitle: 'Suscríbete y mantente al día',
  newsletterSubmit: 'Suscribirme',
  newsletterSubmitting: 'Enviando...',
  newsletterThanksTitle: '¡Gracias!',
  newsletterThanksBody: 'Ya estás suscrito a nuestro boletín.',
  emailPlaceholder: 'Tu correo electrónico',
  welcomeName: 'Te damos la bienvenida, {name}',
  logout: 'Cerrar sesión',
  forgotPassword: 'Recuperar contraseña',
  forgotPasswordQ: '¿Has olvidado tu contraseña?',
  forgotPasswordHint: 'Ponte en contacto con {tenant} para restablecer tu contraseña.',
  forgotPasswordInstruction: 'Introduce tu correo y te enviaremos un enlace para restablecer tu contraseña.',
  forgotPasswordSubmit: 'Enviar enlace de restablecimiento',
  forgotPasswordSent: 'Si existe una cuenta con ese correo, te hemos enviado un enlace para restablecer la contraseña. Revisa tu bandeja de entrada.',
  backToLogin: 'Volver a iniciar sesión',
  passwordSetupTitle: 'Revisa tu correo',
  passwordSetupBody: 'Hemos enviado un correo a {email}. Haz clic en el enlace para confirmar y tu contraseña quedará activada. Después vuelve aquí e inicia sesión.',
  passwordTooShort: 'La contraseña debe tener al menos 8 caracteres.',
  createAccount: 'Crear cuenta',
  createAccountAt: 'Crea una cuenta en {tenant}.',
  loginAt: 'Inicia sesión en {tenant}.',
  fieldFirstName: 'Nombre',
  fieldLastName: 'Apellidos',
  fieldPhone: 'Teléfono',
  optionalSuffix: '(opcional)',
  fieldPassword: 'Contraseña',
  fieldPasswordMin: 'Contraseña (mín. 8 caracteres)',
  creatingAccount: 'Creando cuenta...',
  loggingIn: 'Iniciando sesión...',
  alreadyHaveAccount: '¿Ya tienes una cuenta?',
  noAccountQ: '¿No tienes cuenta?',
  createOne: 'Crear una',
  contactTitle: 'Contáctanos',
  fillMarkedFields: 'Rellena correctamente los campos marcados.',
  sending: 'Enviando...',
  sendMessage: 'Enviar mensaje',
  address: 'Dirección',
  noTestimonials: 'Aún no hay reseñas',
  starsOutOf: '{n} de 5 estrellas',
  mapOf: 'Mapa de {tenant}',
  atTime: 'a las',
  unsubscribe: 'Anular reserva',
  noUpcomingClasses: 'No hay clases próximas',
  temperatureWater: 'Temperatura del agua',
  temperatureAir: 'Temperatura del aire',
  temperatureUpdated: 'Actualizado',
  temperatureUnavailable: 'La temperatura no está disponible en este momento',
  noUpcomingBookings: 'No tienes reservas próximas',
  loginToSeeBookings: 'Inicia sesión para ver tus reservas',
  bookClass: 'Reservar clase',
  seeClasses: 'Ver clases',
  seePrices: 'Ver precios',
  topUp: 'Recargar',
  manageMembership: 'Gestionar suscripción',
  loginToSeeAgreement: 'Inicia sesión para ver tu plan',
  noActiveAgreement: 'No tienes ningún plan activo',
  subscription: 'suscripción',
  soldOut: 'Agotado',
  closed: 'Cerrado',
  comingSoonShort: 'Pronto',
  contactMe: 'Contáctame',
  categoryOther: 'Otros',
  plan: 'Plan',
  fieldName: 'Nombre',
  fieldEmail: 'Correo electrónico',
  emailDidYouMean: '¿Quisiste decir',
  fieldMessage: 'Mensaje',
  contactFormThanks: '¡Gracias! Te responderemos lo antes posible',
  tba: 'Por confirmar',
  loginOrSignUpToBook: 'Inicia sesión o crea una cuenta para reservar',
  onWaitlist: 'en la lista de espera',
  joinWaitlistCta: 'Apuntarme a la lista',
  waitlistJoinedTitle: 'Estás en la lista de espera',
  yourPosition: 'Tu posición',
  alreadyOnWaitlist: 'Ya estás en esta lista de espera',
  loginOrSignUpToJoin: 'Inicia sesión o crea una cuenta para apuntarte a la lista de',
  upcomingSessions: 'Próximas sesiones',
  klinikPickerAllServices: 'Todos los servicios',
  klinikNoSlots: 'No hay horas disponibles ahora mismo',
  klinikShowMore: 'Cargar más días',
  klinikBookAt: 'Reservar en {name}',
  pricePerPerson: '{price} por persona',
  noUpcoming: 'No hay eventos próximos',
  viewRetreat: 'Ver retiro',
  viewEvent: 'Ver evento',
  showMore: 'Ver más',
  fewSpotsLeft: 'Quedan pocas plazas',
  retreatAbout: 'Sobre el retiro',
  retreatInstructors: 'Facilitadores',
  retreatLocation: 'Ubicación',
  retreatFaq: 'Preguntas frecuentes',
  retreatChooseTier: 'Elige un paquete',
  retreatNotPublished: 'Este retiro aún no es público',
  eventAbout: 'Sobre el evento',
  eventChooseTier: 'Elige una entrada',
  eventNotPublished: 'Este evento aún no es público',
  retreatOrganizer: 'Organizador',
  retreatPackageInfo: 'Se trata de un viaje combinado organizado por {organizer}, registrado en el Fondo de Garantía de Viajes danés (n.º de reg. {regNo}). El contrato se celebra con {organizer}.',
  retreatBooking: {
    modalAriaLabel: 'Diálogo de reserva',
    nav: {
      cancel: 'Cancelar',
      back: 'Atrás',
      next: 'Siguiente',
      toPayment: 'Ir al pago',
      confirmFree: 'Confirmar reserva',
      submitting: 'Enviando…',
      stepProgress: 'Paso {current} de {total}',
    },
    steps: {
      indicatorAriaLabel: 'Pasos de la reserva',
      who: {
        title: '¿Quién reserva?',
        description: 'Indica si reservas para ti solo o para dos personas.',
        shortLabel: 'Quién',
        countLegend: 'Número de personas',
        soloOption: 'Solo yo',
        soloHelp: '1 persona',
        pairOption: 'Para dos',
        pairHelp: '2 personas en la misma reserva',
        privateDoubleSoloTitle: 'Estás reservando la habitación doble completa',
        privateDoubleSoloBody: 'Tendrás la habitación para ti solo. El precio corresponde a la habitación completa.',
        roommateLegend: 'Compañero de habitación',
        sharedDoubleSoloHelp: 'Compartirás la doble con otro viajero en solitario. Hacemos la asignación antes de que empiece el retiro.',
        roommate: {
          matchMe: 'Asígname compañero',
          matchMeHelp: 'Encontraremos un compañero de habitación adecuado antes del retiro',
          alone: 'Prefiero habitación individual',
          aloneHelp: 'Intentaremos darte la habitación para uso individual — puede aplicarse suplemento',
          knownPartner: 'Ya tengo compañero',
          knownPartnerHelp: 'Indícanos con quién quieres compartir',
          knownPartnerNameLabel: 'Nombre del compañero',
          knownPartnerNamePlaceholder: 'p. ej. Maja Hansen',
        },
      },
      info: {
        title: 'Datos del participante',
        description: 'Rellena el nombre y el correo electrónico de cada participante.',
        shortLabel: 'Info',
        participantHeading: 'Participante {index}',
        participantHeadingSingle: 'Tus datos',
        firstNameLabel: 'Nombre',
        lastNameLabel: 'Apellidos',
        emailLabel: 'Email',
        phoneLabel: 'Teléfono',
        dietaryLabel: 'Preferencias alimentarias',
        dietaryPlaceholder: 'Alergias, vegetariano, vegano, etc.',
        dietaryHelp: 'Máx. 500 caracteres',
        errors: {
          firstNameRequired: 'El nombre es obligatorio',
          lastNameRequired: 'Los apellidos son obligatorios',
          invalidEmail: 'Correo electrónico no válido',
          invalidPhone: 'Teléfono no válido',
          dietaryTooLong: 'Máx. 500 caracteres',
        },
      },
      questions: {
        title: 'Preguntas de la reserva',
        description: 'Ayúdanos a planificar tu estancia.',
        descriptionEvent: 'Ayúdanos a planificar el evento.',
        shortLabel: 'Preguntas',
        noQuestions: 'Sin preguntas adicionales.',
        selectPlaceholder: '— Elige —',
        errors: { required: 'Obligatorio' },
      },
      payment: {
        title: 'Pago',
        description: 'Elige el método de pago.',
        shortLabel: 'Pago',
        breakdownLine: '{count} × {tierName} a {perPerson}',
        vatLine: 'IVA ({rate}%)',
        totalLabel: 'Total',
        chooseModeLegend: 'Modalidad de pago',
        fullPayment: 'Pago completo',
        fullPaymentHelp: 'Paga el importe completo en un solo cobro',
        depositPayment: 'Depósito',
        depositPaymentHelp: 'Paga el {percent}% ahora, el resto después',
        depositPaymentHelpWithDate: 'Paga el {percent}% ahora, el resto antes del {dueDate}',
        payNowShort: 'Pagar ahora: {amount}',
        installmentsPayment: 'Plazos',
        installmentsPaymentHelp: '{count} cuotas mensuales',
        perMonthShort: '{amount} al mes',
        discountSectionTitle: 'Descuento',
        giftCardLabel: 'Tarjeta regalo',
        giftCardPlaceholder: 'Introduce el código de la tarjeta regalo',
        giftCardHelp: 'Se descuenta del total',
        couponLabel: 'Código de descuento',
        couponPlaceholder: 'Introduce el código de descuento',
        couponHelp: 'Se aplica al confirmar',
        submitting: 'Enviando…',
      },
    },
    priceSummary: {
      lineLabel: '{count} × {tierName}',
      vatLabel: 'IVA ({rate}%)',
      discountLabel: 'Descuento',
      totalLabel: 'Total',
      payNowLabel: 'Pagar ahora',
      dueLaterLabel: 'Después',
      depositNote: 'Depósito: {percent}% ahora, el resto después',
      installmentsNote: 'Plazos: {count} pagos mensuales',
    },
    errors: {
      capacityTaken: 'Por desgracia, la plaza fue reservada mientras completabas el formulario. Elige otro tipo o únete a la lista de espera.',
      paymentNotConfigured: "El pago online aún no está disponible para esta oferta. Contacta con el estudio para inscribirte.",
      paymentProviderError: "No se pudo iniciar el pago. Inténtalo de nuevo en un momento o contacta con el estudio.",
      paymentModeUnavailable: "El pago aún no está configurado para esta oferta. Contacta con el estudio.",
      notAvailable: "No se puede reservar ahora mismo. Contacta con el estudio.",
      validation: 'Comprueba que todos los campos estén rellenados correctamente.',
      generic: 'Algo ha fallado — inténtalo de nuevo en un momento.',
      network: 'Error de red — comprueba tu conexión e inténtalo de nuevo.',
      coupon: {
        notAllowed: 'Los códigos de descuento no se pueden usar en este retiro.',
        unknown: 'Ese código de descuento no existe. Comprueba cómo lo has escrito.',
        notStarted: 'Ese código de descuento aún no está activo.',
        expired: 'Ese código de descuento ha caducado.',
        usedUp: 'Ese código de descuento ya se ha agotado.',
        alreadyUsed: 'Ya has usado este código de descuento.',
        newCustomersOnly: 'Ese código de descuento es solo para clientes nuevos.',
        requiresMembership: 'Ese código de descuento requiere una suscripción activa.',
        requiresSpecificMembership: 'Este código de descuento solo es válido para clientes con una membresía o un curso concreto. Si eres miembro, inicia sesión o usa el correo con el que te registraste.',
        wrongProduct: 'Ese código de descuento no se aplica a los retiros.',
        otherRetreat: 'Ese código de descuento no se aplica a este retiro.',
        notAllowedEvent: 'Los códigos de descuento no se pueden usar en este evento.',
        wrongProductEvent: 'Ese código de descuento no se aplica a los eventos.',
        otherRetreatEvent: 'Ese código de descuento no se aplica a este evento.',
        belowMinimum: 'Ese código de descuento requiere una compra de al menos {minimum}.',
      },
    },
    packageTravel: {
      heading: 'Este es un viaje combinado',
      organizerNote: 'Estás celebrando un acuerdo con {organizer} — no con Class Booking. {organizer} es el organizador y es responsable del viaje combinado.',
      registeredLine: '{organizer} está registrado en Rejsegarantifonden (el Fondo de Garantía de Viajes danés), número de miembro {memberNumber}.',
      formToggle: 'Conoce tus derechos como viajero',
      formIntro: 'La combinación de servicios de viaje que se te ofrece constituye un viaje combinado en el sentido de la Ley danesa de viajes combinados. Por lo tanto, te beneficias de todos los derechos aplicables a los viajes combinados.',
      formResponsibility: '{organizer} es plenamente responsable de la correcta ejecución del viaje combinado en su conjunto y cuenta con la protección legal frente a insolvencia de Rejsegarantifonden (n.º de miembro {memberNumber}).',
      rightsTitle: 'Derechos principales',
      right1: 'Recibes toda la información esencial sobre el viaje combinado antes de formalizar el contrato.',
      right2: 'Al menos un empresario es responsable de la correcta ejecución de todos los servicios de viaje del contrato.',
      right3: 'Si el precio aumenta más de un 8 %, puedes rescindir el contrato.',
      right4: 'Puedes rescindir sin penalización y recibir un reembolso completo si los elementos esenciales cambian significativamente, o ante circunstancias inevitables y extraordinarias.',
      right5: 'Los reembolsos se realizan sin demora injustificada y a más tardar 14 días después.',
      right6: 'Si el organizador quiebra, tus pagos se reembolsan a través de Rejsegarantifonden.',
      contact: 'Protección frente a insolvencia: Rejsegarantifonden, Røjelskær 11, 2840 Holte, Dinamarca — info@rejsegarantifonden.dk, +45 45 87 83 33.',
      consentLabel: 'He leído la información sobre el viaje combinado y acepto las condiciones.',
      logoAlt: 'Rejsegarantifonden',
    },
  },
}

const fr: WidgetStrings = {
  checkoutLegal: {
    summaryTitle: 'Récapitulatif de la commande',
    seller: 'Vendeur : {name}',
    companyId: 'N° d’entreprise : {cvr}',
    platformNote: 'Holdstyring fournit uniquement la solution technique et n’est pas partie à l’achat.',
    total: 'Total TTC',
    totalBeforeDiscount: 'Total TTC avant code de réduction',
    recurringNote: 'Abonnement renouvelé automatiquement jusqu’à sa résiliation.',
    perFourWeeks: 'par 4 semaines',
    perMonth: 'par mois',
    perQuarter: 'par trimestre',
    perHalfYear: 'par semestre',
    perYear: 'par an',
    oneTimeNote: 'Achat unique.',
    acceptTerms: 'J’accepte les conditions générales de vente et j’ai lu les informations sur le droit de rétractation.',
    acceptTermsEarlyStart: 'J’accepte les conditions générales de vente et le droit de rétractation, ainsi que le début immédiat de la prestation.',
    termsLink: 'Conditions générales de vente',
    withdrawalLink: 'Droit de rétractation',
    privacyLink: 'Politique de confidentialité',
    withdrawContractLink: 'Renoncer au contrat',
    noWithdrawal: 'Il n’y a pas de droit de rétractation pour la réservation d’un cours à une date précise.',
    paidOrderNote: 'En cliquant sur le bouton, vous passez une commande avec obligation de paiement.',
    payGeneric: 'Commander avec obligation de paiement',
    payAmount: 'Commander et payer ({amount})',
    payCard: 'Commander et payer par carte',
    payMobilePay: 'Commander et payer avec MobilePay',
  },
  spotsOpenLabel: 'Places disponibles',
  spotsFewLabel: 'Plus que quelques places',
  loadError: 'Impossible de charger',
  close: 'Fermer',
  cancel: 'Annuler',
  login: 'Se connecter',
  readMore: 'En savoir plus',
  readLess: 'Voir moins',
  book: 'Réserver',
  buy: 'Acheter',
  buyLoginPrompt: 'Connectez-vous ou créez un compte pour acheter',
  purchasing: 'Achat…',
  continueToPurchase: 'Continuer vers le paiement',
  purchaseHandoffBody: 'Finalisez votre achat sur notre site.',
  alreadyBookedTitle: 'Vous êtes inscrit',
  alreadyBookedBody: 'Vous avez déjà réservé ce cours.',
  waitlistTitle: 'Sur liste d’attente',
  waitlistBody: 'Nous vous préviendrons si une place se libère.',
  confirmedTitle: 'Réservation confirmée',
  confirmedBody: 'Vous recevrez une confirmation par e-mail.',
  loginToBook: 'Connectez-vous pour réserver ce cours.',
  fullTitle: 'Ce cours est complet',
  fullBody: 'Il n’y a pas de liste d’attente pour ce cours.',
  membershipRequiredBody: 'Ce cours nécessite un abonnement actif. Continuez sur notre site pour choisir un abonnement et réserver.',
  paymentRequiredBody: 'Ce cours est payant. Continuez sur notre site pour payer et réserver.',
  classPlansHeading: 'Ou achetez une carte de séances',
  classPlansHeadingOnly: 'Achetez une carte de séances pour ce cours',
  classPlansClips: '{count, plural, one {# séance} other {# séances}}',
  classPlansValidDays: '{count, plural, one {valable # jour} other {valable # jours}}',
  classPlansBuy: 'Acheter',
  classPlansViewSubscription: 'Voir l\'abonnement',
  classPlansSubscription: 'Abonnement',
  classPlansAfterNote: 'Après le paiement, vous reviendrez ici. Rouvrez le cours et cliquez sur Réserver : votre nouvelle carte de séances sera utilisée.',
  classPlansPaySingle: 'Payer le billet à l\'unité',
  waiverRequiredBody: 'Ce cours nécessite d’accepter la décharge de responsabilité du studio. Continuez sur notre site pour la lire et l’accepter.',
  intakeRequiredBody: 'Ce cours pose quelques questions la première fois. Continuez sur notre site pour y répondre — nous les mémorisons pour la prochaine fois.',
  participantNamesBody: 'Ce cours nécessite le nom des participants. Continuez sur notre site pour réserver.',
  participantProfileBody: 'Dans ce studio, chaque place est réservée pour un profil de participant. Choisissez qui participe sur notre site.',
  waitlistClipCardBody: 'Ce cours est complet et votre carte de séances le couvre. Continuez sur notre site pour rejoindre la liste d’attente afin qu’une séance de votre carte soit réservée.',
  bookWithGuests: 'Réserver avec des invités',
  fullVenueButton: 'Réserver tout le cours',
  continueToBooking: 'Continuer vers la réservation',
  dropInPrice: 'Tarif à la séance',
  clipCostShort: '{count, plural, one {# séance} other {# séances}}',
  priceFromPrefix: 'À partir de',
  vatFreePrice: 'Prix hors TVA',
  ownCancellationHeading: 'Délai d’annulation particulier :',
  ownCancellationDeadline: 'annulation au moins {count} heures avant le début.',
  ownCancellationDeadlineOne: 'annulation au moins {count} heure avant le début.',
  ownCancellationNoRefund: 'aucun remboursement en cas d’annulation.',
  membershipOnly: 'Abonnement',
  includedInSubscription: 'Inclus dans ton abonnement',
  freeClass: 'Gratuit',
  booking: 'Réservation…',
  joinWaitlist: 'Rejoindre la liste d’attente',
  confirmBooking: 'Confirmer la réservation',
  guests: 'Invités',
  perGuest: 'par invité',
  memberSpotCovered: 'Ta place est couverte par ton abonnement',
  coveredByClipCard: 'Couvert par ta carte de séances',
  useClipCard: 'Utiliser la carte de coupons',
  coveredBySubscription: 'Couvert par ton abonnement',
  confirmAndPay: 'Confirmer et payer',
  paymentMethod: 'Moyen de paiement',
  paymentMethodCard: 'Carte bancaire',
  addGuests: 'Ajouter des invités',
  guestOption: 'Continuer en tant qu’invité',
  guestConsent: 'J\'accepte les conditions générales de vente et j\'ai lu la politique de confidentialité.',
  guestConsentRequired: 'Vous devez accepter les conditions pour continuer.',
  guestSubmit: 'Continuer vers le paiement',
  guestSubmitting: 'En cours…',
  guestError: 'Impossible de continuer en tant qu’invité. Réessayez ou connectez-vous.',
  guestAccountExists: 'Un compte existe déjà avec cet e-mail. Connectez-vous à la place.',
  guestOrLoginPrompt: 'Vous pouvez acheter votre place immédiatement, ou vous connecter si vous êtes déjà membre.',
  guestIntro: 'Nous avons seulement besoin de vos coordonnées pour vous envoyer une confirmation. Vous pourrez créer un mot de passe par la suite si vous le souhaitez.',
  guestTermsLink: 'Conditions générales de vente',
  guestPrivacyLink: 'Politique de confidentialité',
  guestCoverageHint: 'Vous avez une carte de coupons ou un abonnement ? Connectez-vous plutôt et nous l\'utiliserons.',
  guestLinkSent: 'Vous avez déjà réservé chez nous avec cet e-mail. Nous vous avons envoyé un lien pour choisir un mot de passe et continuer.',
  noBlogPosts: 'Aucun article de blog pour le moment',
  noClassTypes: 'Aucun type de cours disponible',
  noContactInfo: 'Aucune coordonnée disponible',
  noInstructors: 'Aucun instructeur disponible',
  noPricingPlans: 'Aucun tarif disponible',
  planPeriodPicker: 'Choisissez la période',
  planPeriodFourWeeks: '4 semaines',
  planPeriodOneMonth: '1 mois',
  planPeriodThreeMonths: '3 mois',
  planPeriodSixMonths: '6 mois',
  planPeriodTwelveMonths: '12 mois',
  planPerMonth: 'soit {amount} par mois',
  noRooms: 'Aucune salle disponible',
  noSeasons: 'Aucune saison active',
  noTreatments: 'Aucun soin disponible',
  noWaitlists: 'Aucune liste d’attente ouverte aux inscriptions pour le moment',
  noClassesScheduled: 'Aucun cours programmé pour le moment',
  noClassesThisWeek: 'Aucun cours cette semaine',
  noClassesDay: 'Aucun cours',
  weekOverview: 'Aperçu de la semaine',
  weekAbbrev: 'Semaine',
  previousWeek: 'Semaine précédente',
  nextWeek: 'Semaine suivante',
  thisWeek: 'Cette semaine',
  viewList: 'Cours',
  viewCalendar: 'Calendrier',
  filterAll: 'Tous',
  loadTreatmentsError: 'Impossible de charger les soins',
  openingHours: 'Horaires d’ouverture',
  confirmed: 'Confirmé',
  paused: 'En pause',
  popular: 'Populaire',
  comingSoon: 'Bientôt disponible',
  yearly: 'Annuel',
  monthly: 'Mensuel',
  classesThisMonth: 'cours ce mois-ci',
  previousPurchases: 'Achats précédents',
  showOnMap: 'Afficher sur la carte',
  buyGiftCard: 'Acheter une carte cadeau',
  giftCardImageAlt: 'Carte cadeau',
  gcBuyTitle: 'Acheter une carte cadeau',
  gcBuyAmount: 'Montant',
  gcBuyYourName: 'Votre nom',
  gcBuyYourEmail: 'Votre e-mail',
  gcBuyRecipientEmail: 'E-mail du destinataire (facultatif)',
  gcBuyMessage: 'Message personnel (facultatif)',
  gcBuySubmitting: 'Achat…',
  registrationOpen: 'Inscriptions ouvertes',
  contactFormIntro: 'Écrivez-nous et nous vous répondrons dès que possible.',
  contactFormSuccess: 'Nous avons bien reçu votre message et vous répondrons dès que possible.',
  noClassesPlanned: 'Aucun cours programmé',
  nextClassesStart: 'Les cours commencent le {date}',
  booked: 'Réservé',
  full: 'Complet',
  price: 'Tarif',
  includes: 'Comprend',
  waitlist: 'Liste d’attente',
  awaitingPayment: 'Paiement en attente',
  payBy: 'Payez avant le {time}',
  payNow: 'Payer maintenant',
  fullyBooked: 'Complet',
  loggedInAs: 'Connecté en tant que',
  spot: 'place',
  spots: 'places',
  spotAvailable: 'place restante',
  spotsAvailable: 'places restantes',
  fromPrice: 'à partir de',
  classSingular: 'cours',
  classPlural: 'cours',
  signUp: 'S’inscrire',
  registrationClosed: 'Inscriptions fermées',
  klippekort: 'Carte de séances',
  dropIn: 'À la séance',
  discountCodeToggle: 'Vous avez un code promo ?',
  discountCodePlaceholder: 'Code promo',
  discountInvalid: 'Ce code promo n’est pas valide.',
  discountExpired: 'Ce code promo a expiré.',
  discountUsedUp: 'Ce code promo a été entièrement utilisé.',
  discountAlreadyUsed: 'Vous avez déjà utilisé ce code promo.',
  discountNotForClasses: 'Ce code promo ne s’applique pas aux cours.',
  discountTooManyAttempts: 'Trop de tentatives. Réessayez dans un instant.',
  discountApply: 'Appliquer',
  discountRemove: 'Retirer',
  discountLine: 'Remise',
  priceAfterDiscount: 'Prix après remise',
  discountFirstPaymentOnly: 'La remise s’applique au premier paiement.',
  firstChargeLabel: 'Premier prélèvement',
  signupFeeLineLabel: 'Frais d’inscription (nouveaux membres) :',
  firstPaymentTotalLabel: 'Total du premier paiement',
  firstChargeNote: 'Le premier prélèvement couvre le reste de ce mois. Ensuite {price} le 1er de chaque mois.',
  deferredFirstChargeNote: "Vous avez accès tout de suite et ne payez rien aujourd'hui. Le premier paiement de {price} est prélevé le {date}.",
  firstChargeNoteFolded: 'Le premier prélèvement couvre le reste de ce mois et le mois complet suivant. Ensuite {price} le 1er de chaque mois.',
  discountEveryPayment: 'La remise s’applique à chaque paiement.',
  discountFreeMonths: '{months} mois gratuits avant le premier paiement.',
  subscriptionTitle: 'Abonnement',
  startedOn: 'Commencé le',
  clipsRemaining: 'séances restantes',
  ofTotal: 'sur',
  expiresOn: 'Expire le',
  active: 'Actif',
  activeUntil: 'Actif jusqu’au',
  autoRenews: 'Renouvellement automatique',
  noAutoRenew: 'Pas de renouvellement automatique',
  pauseOrCancel: 'Mettre en pause / résilier',
  confirmUnsubscribe: 'Voulez-vous vraiment annuler « {name} » ?',
  newsletterTitle: 'Newsletter',
  newsletterSubtitle: 'Inscrivez-vous et restez informé',
  newsletterSubmit: 'S’inscrire',
  newsletterSubmitting: 'Inscription…',
  newsletterThanksTitle: 'Merci !',
  newsletterThanksBody: 'Vous êtes maintenant inscrit à notre newsletter.',
  emailPlaceholder: 'Votre adresse e-mail',
  welcomeName: 'Bienvenue, {name} !',
  logout: 'Se déconnecter',
  forgotPassword: 'Mot de passe oublié',
  forgotPasswordQ: 'Mot de passe oublié ?',
  forgotPasswordHint: 'Veuillez contacter {tenant} pour réinitialiser votre mot de passe.',
  forgotPasswordInstruction: 'Saisissez votre e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.',
  forgotPasswordSubmit: 'Envoyer le lien de réinitialisation',
  forgotPasswordSent: 'Si un compte existe pour cet e-mail, nous avons envoyé un lien pour réinitialiser votre mot de passe. Vérifiez votre boîte de réception.',
  backToLogin: 'Retour à la connexion',
  passwordSetupTitle: 'Vérifiez votre e-mail',
  passwordSetupBody: 'Nous avons envoyé un e-mail à {email}. Cliquez sur le lien pour confirmer et votre mot de passe sera activé. Revenez ensuite vous connecter ici.',
  passwordTooShort: 'Le mot de passe doit comporter au moins 8 caractères.',
  createAccount: 'Créer un compte',
  createAccountAt: 'Créez un compte chez {tenant}.',
  loginAt: 'Connectez-vous à {tenant}.',
  fieldFirstName: 'Prénom',
  fieldLastName: 'Nom',
  fieldPhone: 'Téléphone',
  optionalSuffix: '(facultatif)',
  fieldPassword: 'Mot de passe',
  fieldPasswordMin: 'Mot de passe (min. 8 caractères)',
  creatingAccount: 'Création du compte…',
  loggingIn: 'Connexion…',
  alreadyHaveAccount: 'Vous avez déjà un compte ?',
  noAccountQ: 'Vous n’avez pas de compte ?',
  createOne: 'Créez-en un',
  contactTitle: 'Contactez-nous',
  fillMarkedFields: 'Veuillez remplir correctement les champs indiqués.',
  sending: 'Envoi…',
  sendMessage: 'Envoyer le message',
  address: 'Adresse',
  noTestimonials: 'Aucun avis pour le moment',
  starsOutOf: '{n} étoiles sur 5',
  mapOf: 'Carte de {tenant}',
  atTime: 'à',
  unsubscribe: 'Annuler la réservation',
  noUpcomingClasses: 'Aucun cours à venir',
  temperatureWater: 'Température de l’eau',
  temperatureAir: 'Température de l’air',
  temperatureUpdated: 'Mis à jour',
  temperatureUnavailable: 'La température n’est pas disponible pour le moment',
  noUpcomingBookings: 'Vous n’avez aucune réservation à venir',
  loginToSeeBookings: 'Connectez-vous pour voir vos réservations',
  bookClass: 'Réserver un cours',
  seeClasses: 'Voir les cours',
  seePrices: 'Voir les tarifs',
  topUp: 'Recharger',
  manageMembership: 'Gérer l’abonnement',
  loginToSeeAgreement: 'Connectez-vous pour voir votre formule',
  noActiveAgreement: 'Vous n’avez aucune formule active',
  subscription: 'abonnement',
  soldOut: 'Épuisé',
  closed: 'Fermé',
  comingSoonShort: 'Bientôt',
  contactMe: 'Me contacter',
  categoryOther: 'Autre',
  plan: 'Formule',
  fieldName: 'Nom',
  fieldEmail: 'E-mail',
  emailDidYouMean: 'Vouliez-vous dire',
  fieldMessage: 'Message',
  contactFormThanks: 'Merci ! Nous vous répondrons dès que possible',
  tba: 'À venir',
  loginOrSignUpToBook: 'Connectez-vous ou créez un compte pour réserver',
  onWaitlist: 'sur la liste d’attente',
  joinWaitlistCta: 'Rejoindre la liste d’attente',
  waitlistJoinedTitle: 'Vous êtes sur la liste d’attente',
  yourPosition: 'Votre position',
  alreadyOnWaitlist: 'Vous êtes déjà sur cette liste d’attente',
  loginOrSignUpToJoin: 'Connectez-vous ou créez un compte pour rejoindre la liste d’attente de',
  upcomingSessions: 'Prochaines séances',
  klinikPickerAllServices: 'Tous les services',
  klinikNoSlots: 'Aucun créneau disponible pour le moment',
  klinikShowMore: 'Charger plus de jours',
  klinikBookAt: 'Réserver chez {name}',
  pricePerPerson: '{price} par personne',
  noUpcoming: 'Aucun événement à venir',
  viewRetreat: 'Voir la retraite',
  viewEvent: 'Voir l\'événement',
  showMore: 'Voir plus',
  fewSpotsLeft: 'Plus que quelques places',
  retreatAbout: 'À propos de cette retraite',
  retreatInstructors: 'Intervenants',
  retreatLocation: 'Lieu',
  retreatFaq: 'Questions fréquentes',
  retreatChooseTier: 'Choisir une formule',
  retreatNotPublished: 'Cette retraite n’est pas encore publique',
  eventAbout: 'À propos de cet événement',
  eventChooseTier: 'Choisir un billet',
  eventNotPublished: 'Cet événement n’est pas encore public',
  retreatOrganizer: 'Organisateur',
  retreatPackageInfo: 'Il s’agit d’un voyage à forfait organisé par {organizer}, enregistré auprès du Fonds de garantie de voyage danois (n° d’enreg. {regNo}). Le contrat est conclu avec {organizer}.',
  retreatBooking: {
    modalAriaLabel: 'Fenêtre de réservation',
    nav: {
      cancel: 'Annuler',
      back: 'Retour',
      next: 'Suivant',
      toPayment: 'Vers le paiement',
      confirmFree: 'Confirmer la réservation',
      submitting: 'Envoi…',
      stepProgress: 'Étape {current} sur {total}',
    },
    steps: {
      indicatorAriaLabel: 'Étapes de réservation',
      who: {
        title: 'Qui réserve ?',
        description: 'Choisissez si vous réservez pour vous-même ou pour deux.',
        shortLabel: 'Qui',
        countLegend: 'Nombre de personnes',
        soloOption: 'Moi uniquement',
        soloHelp: '1 personne',
        pairOption: 'Pour deux',
        pairHelp: '2 personnes sur la même réservation',
        privateDoubleSoloTitle: 'Vous réservez toute la chambre double',
        privateDoubleSoloBody: 'Vous avez la chambre pour vous seul. Le tarif est pour toute la chambre.',
        roommateLegend: 'Compagnon de chambre',
        sharedDoubleSoloHelp: 'Vous partagerez la chambre double avec une autre personne seule. Nous faisons le jumelage avant le début de la retraite.',
        roommate: {
          matchMe: 'Trouvez-moi quelqu’un',
          matchMeHelp: 'Nous trouverons un compagnon de chambre adapté avant la retraite',
          alone: 'Je préfère être seul',
          aloneHelp: 'Nous essaierons de vous donner la chambre seul — peut entraîner un supplément individuel',
          knownPartner: 'Je connais mon partenaire',
          knownPartnerHelp: 'Indiquez avec qui vous souhaitez partager',
          knownPartnerNameLabel: 'Nom du compagnon de chambre',
          knownPartnerNamePlaceholder: 'par ex. Maja Hansen',
        },
      },
      info: {
        title: 'Informations des participants',
        description: 'Renseignez le nom et l’e-mail de chaque participant.',
        shortLabel: 'Infos',
        participantHeading: 'Participant {index}',
        participantHeadingSingle: 'Vos informations',
        firstNameLabel: 'Prénom',
        lastNameLabel: 'Nom',
        emailLabel: 'E-mail',
        phoneLabel: 'Téléphone',
        dietaryLabel: 'Régime alimentaire',
        dietaryPlaceholder: 'Allergies, végétarien, végétalien, etc.',
        dietaryHelp: '500 caractères maximum',
        errors: {
          firstNameRequired: 'Le prénom est obligatoire',
          lastNameRequired: 'Le nom est obligatoire',
          invalidEmail: 'E-mail invalide',
          invalidPhone: 'Téléphone invalide',
          dietaryTooLong: '500 caractères maximum',
        },
      },
      questions: {
        title: 'Questions de réservation',
        description: 'Aidez-nous à préparer votre séjour.',
        descriptionEvent: 'Aidez-nous à planifier l’événement.',
        shortLabel: 'Questions',
        noQuestions: 'Aucune question supplémentaire.',
        selectPlaceholder: '— Choisir —',
        errors: { required: 'Obligatoire' },
      },
      payment: {
        title: 'Paiement',
        description: 'Choisissez le mode de paiement.',
        shortLabel: 'Paiement',
        breakdownLine: '{count} × {tierName} à {perPerson}',
        vatLine: 'TVA ({rate} %)',
        totalLabel: 'Total',
        chooseModeLegend: 'Mode de paiement',
        fullPayment: 'Payer maintenant',
        fullPaymentHelp: 'Payer la totalité en une fois',
        depositPayment: 'Acompte',
        depositPaymentHelp: 'Payez {percent} % maintenant, le reste plus tard',
        depositPaymentHelpWithDate: 'Payez {percent} % maintenant, le reste avant le {dueDate}',
        payNowShort: 'Payer maintenant : {amount}',
        installmentsPayment: 'Paiement en plusieurs fois',
        installmentsPaymentHelp: '{count} mensualités',
        perMonthShort: '{amount} par mois',
        discountSectionTitle: 'Réduction',
        giftCardLabel: 'Carte cadeau',
        giftCardPlaceholder: 'Saisir le code de la carte cadeau',
        giftCardHelp: 'Déduit du total',
        couponLabel: 'Code promo',
        couponPlaceholder: 'Saisir le code promo',
        couponHelp: 'Appliqué à la confirmation',
        submitting: 'Envoi…',
      },
    },
    priceSummary: {
      lineLabel: '{count} × {tierName}',
      vatLabel: 'TVA ({rate} %)',
      discountLabel: 'Réduction',
      totalLabel: 'Total',
      payNowLabel: 'Payer maintenant',
      dueLaterLabel: 'Plus tard',
      depositNote: 'Acompte : {percent} % maintenant, le reste plus tard',
      installmentsNote: 'Paiement en plusieurs fois : {count} mensualités',
    },
    errors: {
      capacityTaken: 'La place a malheureusement été réservée pendant que vous remplissiez le formulaire. Choisissez un autre type ou inscrivez-vous sur la liste d’attente.',
      paymentNotConfigured: "Le paiement en ligne n'est pas encore ouvert pour cette offre. Veuillez contacter le studio pour vous inscrire.",
      paymentProviderError: "Le paiement n'a pas pu être lancé. Veuillez réessayer dans un instant ou contacter le studio.",
      paymentModeUnavailable: "Le paiement n'est pas encore configuré pour cette offre. Veuillez contacter le studio.",
      notAvailable: "Cette offre ne peut pas être réservée pour le moment. Veuillez contacter le studio.",
      validation: 'Veuillez vérifier que tous les champs sont correctement remplis.',
      generic: 'Une erreur s’est produite — réessayez dans un instant.',
      network: 'Erreur réseau — vérifiez votre connexion et réessayez.',
      coupon: {
        notAllowed: 'Les codes promo ne peuvent pas être utilisés pour cette retraite.',
        unknown: 'Ce code promo n’existe pas. Vérifiez l’orthographe.',
        notStarted: 'Ce code promo n’est pas encore actif.',
        expired: 'Ce code promo a expiré.',
        usedUp: 'Ce code promo a été entièrement utilisé.',
        alreadyUsed: 'Vous avez déjà utilisé ce code promo.',
        newCustomersOnly: 'Ce code promo est réservé aux nouveaux clients.',
        requiresMembership: 'Ce code promo nécessite un abonnement actif.',
        requiresSpecificMembership: 'Ce code promo est réservé aux clients ayant un abonnement ou un cours précis. Si vous êtes membre, connectez-vous ou utilisez l\'e-mail avec lequel vous êtes inscrit.',
        wrongProduct: 'Ce code promo ne s’applique pas aux retraites.',
        otherRetreat: 'Ce code promo ne s’applique pas à cette retraite.',
        notAllowedEvent: 'Les codes de réduction ne peuvent pas être utilisés pour cet événement.',
        wrongProductEvent: 'Ce code de réduction ne s’applique pas aux événements.',
        otherRetreatEvent: 'Ce code de réduction ne s’applique pas à cet événement.',
        belowMinimum: 'Ce code promo nécessite un achat d’au moins {minimum}.',
      },
    },
    packageTravel: {
      heading: 'Il s’agit d’un voyage à forfait',
      organizerNote: 'Vous concluez un contrat avec {organizer} — et non avec Class Booking. {organizer} est l’organisateur et est responsable du voyage à forfait.',
      registeredLine: '{organizer} est enregistré auprès de Rejsegarantifonden (le Fonds de garantie de voyage danois), n° de membre {memberNumber}.',
      formToggle: 'En savoir plus sur vos droits en tant que voyageur',
      formIntro: 'La combinaison de services de voyage qui vous est proposée constitue un forfait au sens de la loi danoise sur les voyages à forfait. Vous bénéficiez donc de tous les droits applicables aux forfaits.',
      formResponsibility: '{organizer} est entièrement responsable de la bonne exécution du forfait dans son ensemble et dispose de la protection légale contre l’insolvabilité auprès de Rejsegarantifonden (n° de membre {memberNumber}).',
      rightsTitle: 'Droits essentiels',
      right1: 'Vous recevez toutes les informations essentielles sur le forfait avant de conclure le contrat.',
      right2: 'Au moins un professionnel est responsable de la bonne exécution de tous les services de voyage inclus dans le contrat.',
      right3: 'Si le prix augmente de plus de 8 %, vous pouvez résilier le contrat.',
      right4: 'Vous pouvez résilier sans frais et obtenir un remboursement intégral si des éléments essentiels changent de manière significative, ou en cas de circonstances inévitables et extraordinaires.',
      right5: 'Les remboursements sont effectués sans retard injustifié et au plus tard 14 jours après.',
      right6: 'Si l’organisateur devient insolvable, vos paiements sont remboursés via Rejsegarantifonden.',
      contact: 'Protection contre l’insolvabilité : Rejsegarantifonden, Røjelskær 11, 2840 Holte, Danemark — info@rejsegarantifonden.dk, +45 45 87 83 33.',
      consentLabel: 'J’ai lu les informations sur le voyage à forfait et j’accepte les conditions.',
      logoAlt: 'Rejsegarantifonden',
    },
  },
}

const STRINGS: Record<WidgetLocale, WidgetStrings> = { da, en, de, sv, nb, es, fr }

/** Narrow an arbitrary locale string ('en-GB', 'EN', undefined) to a supported one. */
export function normalizeLocale(locale?: string | null): WidgetLocale {
  if (!locale) return 'da'
  const base = locale.toLowerCase().split(/[-_]/)[0]
  return (SUPPORTED as string[]).includes(base) ? (base as WidgetLocale) : 'da'
}

/** Strings for a tenant locale. Unknown locales fall back to Danish. */
export function t(locale?: string | null): WidgetStrings {
  return STRINGS[normalizeLocale(locale)]
}

/** Intl locale tag for dates and numbers, e.g. 'de-DE'. */
/**
 * Formats a one/other ICU plural template (`{count, plural, one {# clip} other {# clips}}`) for
 * `count` in the widget locale; `#` is the count. A template without a plural block falls back
 * to replacing `{count}`, so older strings keep working. The widget has no ICU runtime, and
 * "1 clips" / "1 Einheiten" looked wrong in every language.
 */
export function plural(template: string, count: number, locale?: string | null): string {
  const m = template.match(/\{count,\s*plural,\s*one\s*\{([^}]*)\}\s*other\s*\{([^}]*)\}\s*\}/)
  if (!m) return template.replace('{count}', String(count))
  const category = new Intl.PluralRules(intlLocale(locale)).select(count)
  const form = category === 'one' ? m[1] : m[2]
  return template.replace(m[0], form).replace(/#/g, String(count))
}

export function intlLocale(locale?: string | null): string {
  return INTL_LOCALE[normalizeLocale(locale)]
}

/**
 * Money formatting. Amounts in the API's *Dkk fields are in the TENANT's
 * currency (initData.currency) — the "Dkk" suffix is historical. Mirrors
 * apps/holdstyring/src/lib/pricing-utils.ts:formatPriceWithCurrency so the
 * widgets show the same price string as the tenant's own site: DKK on a
 * Danish site keeps the classic "150 kr", everything else goes through Intl
 * so symbol and placement match the locale ("DKK 150", "€150", "150 €", ...).
 */
function moneyFormatter(locale: string | null | undefined, currency: string): Intl.NumberFormat | null {
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  } catch {
    return null // unknown/garbage currency code — caller falls back
  }
}

export function formatMoney(
  value: number,
  locale?: string | null,
  currency?: string | null,
  opts?: { keepOre?: boolean },
): string {
  const cur = (currency || 'DKK').toUpperCase()
  // keepOre: beløb der skal stemme på øret med det, der trækkes (købsvinduets rabat og
  // «Pris efter rabat», Tog 12: 50 % af 799 viste «400 kr», mens Stripe trak 399,50).
  // Hele beløb vises stadig uden decimaler.
  const cents = Math.round(value * 100)
  if (opts?.keepOre && cents % 100 !== 0) {
    const amount = cents / 100
    const nf = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    if (cur === 'DKK' || cur === 'SEK' || cur === 'NOK') {
      return `${amount.toLocaleString(intlLocale(locale), nf)} kr`
    }
    try {
      return new Intl.NumberFormat(intlLocale(locale), { style: 'currency', currency: cur, ...nf }).format(amount)
    } catch {
      return `${amount.toLocaleString(intlLocale(locale), nf)} ${cur}`
    }
  }
  const rounded = Math.round(value)
  // Skandinaviske kroner renderes ALTID som "150 kr" — også på ikke-danske
  // sider — så en dansk studios engelske widget viser "150 kr", ikke "DKK 150".
  if (cur === 'DKK' || cur === 'SEK' || cur === 'NOK') {
    return `${rounded.toLocaleString(intlLocale(locale))} kr`
  }
  const fmt = moneyFormatter(locale, cur)
  return fmt ? fmt.format(rounded) : `${rounded.toLocaleString(intlLocale(locale))} ${cur}`
}

export interface MoneyParts {
  /** Formatted number without the currency marker, e.g. "1.500". */
  amount: string
  /** Currency marker, e.g. "kr", "€", "DKK". */
  symbol: string
  /** True when the marker belongs before the number ("£150", "DKK 150"). */
  symbolFirst: boolean
}

/**
 * formatMoney split into number + currency marker, for layouts that style the
 * two differently (big price number, small suffix). Render symbol before or
 * after the number according to symbolFirst.
 */
export function formatMoneyParts(value: number, locale?: string | null, currency?: string | null): MoneyParts {
  const cur = (currency || 'DKK').toUpperCase()
  const rounded = Math.round(value)
  // Skandinaviske kroner: altid "150 kr"-formen, uanset side-locale.
  if (cur === 'DKK' || cur === 'SEK' || cur === 'NOK') {
    return { amount: rounded.toLocaleString(intlLocale(locale)), symbol: 'kr', symbolFirst: false }
  }
  const fmt = moneyFormatter(locale, cur)
  if (!fmt) {
    return { amount: rounded.toLocaleString(intlLocale(locale)), symbol: cur, symbolFirst: false }
  }
  const parts = fmt.formatToParts(rounded)
  const idx = parts.findIndex(p => p.type === 'currency')
  return {
    amount: parts.filter(p => p.type !== 'currency').map(p => p.value).join('').trim(),
    symbol: idx >= 0 ? parts[idx].value : cur,
    symbolFirst: idx === 0,
  }
}

/**
 * Weekday names indexed by JS getDay() — index 0 is Sunday.
 *
 * Derived from Intl rather than a hardcoded array per language, so adding a
 * locale to WidgetLocale needs no new day lists. 2023-01-01 was a Sunday, so
 * offsetting from it lines the output up with getDay(). Formateret i UTC (som datoerne er
 * lavet i): i browserens zone gav en browser vest for UTC lørdag som indeks 0, så alle
 * ugedage stod én dag forkert (QA 27-10).
 */
export function dayNames(locale?: string | null, style: 'long' | 'short' = 'long'): string[] {
  const fmt = new Intl.DateTimeFormat(intlLocale(locale), { weekday: style, timeZone: 'UTC' })
  return Array.from({ length: 7 }, (_, i) => {
    const label = fmt.format(new Date(Date.UTC(2023, 0, 1 + i)))
    return label.charAt(0).toUpperCase() + label.slice(1)
  })
}
